import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import IdeaCanvas from './IdeaCanvas';
import CodebaseUpload from './CodebaseUpload';
import ResumeInput from './ResumeInput';
import HistoryModal from './HistoryModal';
import MemoryInsights, { buildMemoryEntry, appendMemory, readMemory } from './MemoryLayer';

import ChatPanel from './ChatPanel';
import AgentChatPanel from './AgentChatPanel';
import StatusLine from './StatusLine';
import ResumeChangesModal from './ResumeChangesModal';

import Graph3D from './Graph3D';


import { NODE_TYPES_CONFIG, buildDynamicConfig, getNodeConfig } from './nodeConfig';
import { MODES, detectMode } from './modeConfig';
import { useCanvasMode, buildFlowNode, readSSEStream, appendVersion, readVersions, resolveNodePattern } from './useCanvasMode';
import { NoveltyContext } from './NoveltyContext';
import DiscoveryDeck from './DiscoveryDeck';
import { getSubtreeNodeIds } from './treeUtils';
import { readTemplates, saveTemplate } from './TemplateStore';
import { useGateway } from './gateway/useGateway';
import ShareModal from './ShareModal';
import ShareViewer from './ShareViewer';
import { useAuth } from './AuthContext';
import { setTokenGetter, authFetch } from './api';
import LandingPage from './LandingPage';
import BlogPage from './BlogPage';
import Sidebar from './Sidebar';
import SettingsPage from './settings/SettingsPage';
import KnowledgeGraph from './KnowledgeGraph';
import { useAutoRefine } from './useAutoRefine';
import { executeActions } from './actions/registry';
import WorkflowSetup from './WorkflowSetup';
import NextExperience from './NextExperience';
import { NextModeHeader, NextModeEmptyState, getNextMode } from './NextModeWorkspace';
import { usePortfolio } from './usePortfolio';
import { useLearnLoop } from './useLearnLoop';
import { useExperimentLoop } from './useExperimentLoop';
import { usePatternExecutor } from './usePatternExecutor';
import { useMnemonicVideo } from './useMnemonicVideo';
import PipelineOverlay from './PipelineOverlay';
import VideoModal from './VideoModal';
import FlowchartView from './FlowchartView';
import IdeaWalkthroughView from './IdeaWalkthroughView';
import AgentFlowView from './AgentFlowView';
import LearnJourneyView from './LearnJourneyView';
import WorkflowJourneyView from './WorkflowJourneyView';
import GmailPicker from './GmailConnect';
import useGmail from './useGmail';
import InviteAccept from './InviteAccept';
import { useTimelineNav } from './useTimelineNav';
import { useNodeTools } from './useNodeTools';
import TimelineFilmstrip from './TimelineFilmstrip';
import { useUndoStack } from './useUndoStack';
import { useGhostNodes } from './useGhostNodes';
import { useHoverPreview } from './useHoverPreview';
import PreviewOverlay from './PreviewOverlay';
import { usePrototypeBuilder } from './usePrototypeBuilder';
import FullPrototypePlayer from './FullPrototypePlayer';
import InspectorPanel from './InspectorPanel';
import useStreams from './useStreams';
import useSynapses from './useSynapses';
import useActivation from './useActivation';
import { YjsProvider, useYjs } from './yjs/YjsContext';
import { generateRoomId, buildRoomUrl } from './yjs/roomUtils';
import SyncStatusBar from './yjs/SyncStatusBar';
import { ForestProvider, useForest } from './ForestContext';
import ClaudeCodePicker from './ClaudeCodePicker';
import ClaudeMemoryImport from './ClaudeMemoryImport';
import ExportDropdown from './ExportDropdown';
import SessionFilesBar from './SessionFilesBar';
import SessionContextPanel from './SessionContextPanel';
import CoworkPanel from './CoworkPanel';
import ForestTabBar from './ForestTabBar';
import ForestMetaCanvas from './ForestMetaCanvas';
import ForestSynthesis from './ForestSynthesis';
import VelocityMeter from './VelocityMeter';
import CommandPalette from './CommandPalette';
import PipelineBar, { getPipelineStages, computeStageStatus } from './PipelineBar';
import { serverStorage } from './serverStorage';
import './App.css';

// Upsert a node into the array — replaces existing node with same ID, or appends
function upsertNode(nodes, newNode) {
  const idx = nodes.findIndex(n => n.id === newNode.id);
  if (idx >= 0) {
    const updated = [...nodes];
    updated[idx] = newNode;
    return updated;
  }
  return [...nodes, newNode];
}

const API_URL = process.env.REACT_APP_API_URL || '';
const WS_URL = API_URL
  ? API_URL.replace(/^http/, 'ws') + '/ws'
  : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;

// Mode-specific toolbar button labels & tooltips
const DEBATE_LABELS = {
  idea:     { icon: '⚔', label: 'CRITIQUE',  tooltip: 'Auto-critique of your idea' },
  resume:   { icon: '◎', label: 'REVIEW',    tooltip: 'Hiring manager review of resume strategy' },
  codebase: { icon: '⟨/⟩', label: 'AUDIT',  tooltip: 'Security audit of codebase architecture' },
  decision: { icon: '⚖', label: 'ADVOCATE',  tooltip: "Devil's advocate analysis of your decision" },
  writing:  { icon: '✦', label: 'EDITORIAL', tooltip: 'Senior editor review of your writing' },
  plan:     { icon: '◉', label: 'RISK',      tooltip: 'Risk analyst review of your plan' },
  learn:    { icon: '⧫', label: 'CHALLENGE', tooltip: 'Socratic challenge of your understanding' },
};

const CHAT_LABELS = {
  idea:     { title: 'STRATEGIST', tooltip: 'Product strategist companion' },
  resume:   { title: 'COACH',     tooltip: 'Career coach companion' },
  codebase: { title: 'ADVISOR',   tooltip: 'Tech advisor companion' },
  decision: { title: 'ANALYST',   tooltip: 'Decision analyst companion' },
  writing:  { title: 'EDITOR',    tooltip: 'Writing editor companion' },
  plan:     { title: 'PLANNER',   tooltip: 'Project advisor companion' },
  learn:    { title: 'TUTOR',     tooltip: 'AI learning tutor' },
};

// Helper: stream generation via WebSocket, with same callback pattern as readSSEStream
// Returns null if WS send fails (caller should fall back to REST)
// Stores activeReqId for stop/cancel support
let activeWsReqId = null;
function streamViaGateway(gateway, type, params, onNode) {
  return new Promise((resolve) => {
    const reqId = gateway.send(type, params, {
      onNode: (data) => onNode(data),
      onMeta: (data) => onNode({ ...data, _meta: true }),
      onProgress: (stage) => onNode({ _progress: true, stage }),
      onText: (data) => onNode(data),
      onResult: (data) => { activeWsReqId = null; resolve({ done: true, result: data }); },
      onCanvasArtifact: (data) => onNode({ _canvas: true, ...data }),
      onDone: () => { activeWsReqId = null; resolve({ done: true }); },
      onError: (message) => { activeWsReqId = null; resolve({ error: message }); },
    });
    if (!reqId) { resolve(null); return; }
    activeWsReqId = reqId;
  });
}


// ── Round / temporal helpers (mirrors Graph3D) ────────────────────────────────
function getRoundIndex(node) {
  const id = node.id || '';
  const type = (node.data?.type || node.type || '').toLowerCase();
  if (type === 'seed') return 0;
  const crit = id.match(/crit_r(\d+)/);  if (crit)  return parseInt(crit[1]) * 2;
  const rebut = id.match(/rebut_r(\d+)/); if (rebut) return parseInt(rebut[1]) * 2 + 1;
  if (/^(fin_|syn_|finalize_|synthesis_)/.test(id)) return 12;
  return 1;
}

const ROUND_LABELS = {
  0: 'SEED', 1: 'GENERATE', 2: 'R1 CRITIQUE', 3: 'R1 REBUT',
  4: 'R2 CRITIQUE', 5: 'R2 REBUT', 6: 'R3 CRITIQUE', 7: 'R3 REBUT',
  8: 'R4 CRITIQUE', 9: 'R4 REBUT', 10: 'R5 CRITIQUE', 11: 'R5 REBUT', 12: 'SYNTHESIS',
};
const ROUND_SHORT = {
  0: 'SEED', 1: 'GEN', 2: 'C1', 3: 'B1', 4: 'C2', 5: 'B2',
  6: 'C3', 7: 'B3', 8: 'C4', 9: 'B4', 10: 'C5', 11: 'B5', 12: 'SYN',
};

// ── Timeline Bar (2D view) ────────────────────────────────────────────────────
function TimelineBar2D({ roundRange, onRoundRangeChange, isPlaying, onPlayToggle,
  playbackSpeed, onSpeedChange, isolatedRound, onIsolatedRoundChange, maxRound }) {

  const handleSliderMin = (e) => {
    const v = Number(e.target.value);
    onRoundRangeChange(prev => [Math.min(v, prev[1]), prev[1]]);
    onIsolatedRoundChange(null);
    if (isPlaying) onPlayToggle();
  };
  const handleSliderMax = (e) => {
    const v = Number(e.target.value);
    onRoundRangeChange(prev => [prev[0], Math.max(v, prev[0])]);
    onIsolatedRoundChange(null);
    if (isPlaying) onPlayToggle();
  };

  return (
    <div style={{
      position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 10,
      background: 'rgba(10,10,20,0.92)', borderTop: '1px solid #2a2a3a',
      padding: '8px 16px 10px', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <style>{`
        .tl2d-range { margin: 0; padding: 0; }
        .tl2d-range::-webkit-slider-thumb {
          pointer-events: all; -webkit-appearance: none;
          width: 14px; height: 14px; border-radius: 50%;
          background: #6c63ff; border: 2px solid #0a0a0f;
          cursor: pointer; position: relative; z-index: 5; margin-top: -5px;
        }
        .tl2d-range::-moz-range-thumb {
          pointer-events: all; width: 14px; height: 14px; border-radius: 50%;
          background: #6c63ff; border: 2px solid #0a0a0f; cursor: pointer;
        }
        .tl2d-range::-webkit-slider-runnable-track { height: 4px; background: transparent; }
        .tl2d-range::-moz-range-track { height: 4px; background: transparent; }
      `}</style>

      {/* Play / Pause */}
      <button onClick={onPlayToggle} style={{
        background: 'transparent', border: `1px solid ${isPlaying ? '#ff5f6d' : '#6c63ff'}`,
        color: isPlaying ? '#ff5f6d' : '#6c63ff', fontFamily: 'monospace',
        fontSize: 12, fontWeight: 700, padding: '3px 0', borderRadius: 5,
        cursor: 'pointer', width: 32, flexShrink: 0, lineHeight: 1,
      }}>
        {isPlaying ? '■' : '▶'}
      </button>

      {/* Speed */}
      <select value={playbackSpeed} onChange={e => onSpeedChange(Number(e.target.value))} style={{
        background: '#111118', border: '1px solid #2a2a3a', color: '#888',
        fontFamily: 'monospace', fontSize: 9, padding: '3px 2px', borderRadius: 4,
        cursor: 'pointer', width: 40, flexShrink: 0,
      }}>
        <option value={0.5}>0.5×</option>
        <option value={1}>1×</option>
        <option value={2}>2×</option>
      </select>

      {/* Dual range slider */}
      <div style={{ flex: 1, position: 'relative', height: 36, display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, height: 4, top: 10, background: '#1a1a2a', borderRadius: 2, pointerEvents: 'none' }} />
        <div style={{
          position: 'absolute', top: 10, height: 4, borderRadius: 2, pointerEvents: 'none',
          left: `${(roundRange[0] / (maxRound || 1)) * 100}%`,
          width: `${((roundRange[1] - roundRange[0]) / (maxRound || 1)) * 100}%`,
          background: isolatedRound !== null ? '#fbbf24' : '#6c63ff',
        }} />

        {Array.from({ length: (maxRound || 0) + 1 }, (_, i) => {
          const inRange = isolatedRound !== null ? i === isolatedRound : i >= roundRange[0] && i <= roundRange[1];
          return (
            <div key={i} style={{
              position: 'absolute', left: `${(i / (maxRound || 1)) * 100}%`, top: 0,
              transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column',
              alignItems: 'center', pointerEvents: 'none',
            }}>
              <div style={{ width: 1, height: 6, background: inRange ? '#6c63ff' : '#333', marginBottom: 1 }} />
              <div style={{ height: 8 }} />
              <span style={{
                fontSize: 7, marginTop: 2, color: inRange ? '#888' : '#383838',
                whiteSpace: 'nowrap', letterSpacing: '0.02em', fontFamily: 'monospace',
              }}>
                {ROUND_SHORT[i] || i}
              </span>
            </div>
          );
        })}

        <input type="range" className="tl2d-range" min={0} max={maxRound || 1} step={1}
          value={roundRange[0]} onChange={handleSliderMin}
          style={{ position: 'absolute', width: '100%', top: 4, appearance: 'none', WebkitAppearance: 'none', background: 'transparent', pointerEvents: 'none', zIndex: 3, height: 16 }}
        />
        <input type="range" className="tl2d-range" min={0} max={maxRound || 1} step={1}
          value={roundRange[1]} onChange={handleSliderMax}
          style={{ position: 'absolute', width: '100%', top: 4, appearance: 'none', WebkitAppearance: 'none', background: 'transparent', pointerEvents: 'none', zIndex: 4, height: 16 }}
        />
      </div>

      {/* Round label */}
      <span style={{
        color: '#888', fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
        flexShrink: 0, minWidth: 90, textAlign: 'right', fontFamily: 'monospace',
      }}>
        {isolatedRound !== null
          ? `⦿ ${ROUND_LABELS[isolatedRound] || `R${isolatedRound}`}`
          : `${ROUND_LABELS[roundRange[0]] || roundRange[0]} — ${ROUND_LABELS[roundRange[1]] || roundRange[1]}`
        }
      </span>
    </div>
  );
}

// ── Access Denied screen (403 — email not in allowlist) ──
function AccessDenied({ email, onLogout }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#0a0a0f', color: '#e8e8f0', fontFamily: 'var(--font-mono, monospace)' }}>
      <div style={{ textAlign: 'center', maxWidth: 420, padding: '0 24px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2 style={{ fontSize: 16, marginBottom: 8, letterSpacing: '0.08em', color: '#f87171' }}>ACCESS DENIED</h2>
        <p style={{ fontSize: 13, opacity: 0.6, marginBottom: 24, lineHeight: 1.6 }}>
          <strong style={{ color: '#e8e8f0' }}>{email}</strong> is not authorized to use ThoughtClaw.
          <br />Contact the administrator for access.
        </p>
        <button
          onClick={onLogout}
          style={{
            background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 8,
            color: '#8888aa', padding: '10px 24px', cursor: 'pointer', fontSize: 12,
            letterSpacing: '0.06em', fontFamily: 'inherit',
          }}
        >
          SIGN OUT
        </button>
      </div>
    </div>
  );
}

// ── Loading screen ──
function LoadingScreen() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#0a0a0f', color: '#6c63ff', fontFamily: 'var(--font-mono, monospace)' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>◈</div>
        <div style={{ fontSize: 12, letterSpacing: '0.1em', opacity: 0.7 }}>LOADING...</div>
      </div>
    </div>
  );
}

// ── Onboarding helpers ──
function getOnboardingState() {
  try {
    return JSON.parse(localStorage.getItem('tc_onboarding') || '{}');
  } catch { return {}; }
}
function setOnboardingState(patch) {
  try {
    const prev = getOnboardingState();
    localStorage.setItem('tc_onboarding', JSON.stringify({ ...prev, ...patch }));
  } catch { /* ignore */ }
}

// ── Empty state shown when no session is selected ──
const MODE_DESCRIPTIONS = {
  idea: 'Product & startup thinking',
  codebase: 'Reverse-engineer any repo',
  decision: 'Map trade-offs & alternatives',
  writing: 'Plan essays, articles & proposals',
  plan: 'Build roadmaps & timelines',
  learn: 'Deep-dive into any concept',
};

// ── Onboarding Sparks (first-time users) ──
const ONBOARDING_SPARKS = [
  { id: 'idea', label: 'I have a startup idea', desc: 'AI builds a structured product thinking tree', mode: 'idea', icon: '◈', color: '#6c63ff' },
  { id: 'decision', label: 'I need to make a decision', desc: 'Map trade-offs, risks, and alternatives', mode: 'decision', icon: '⚖', color: '#ffa94d' },
  { id: 'learn', label: "I'm learning something new", desc: 'Build a concept tree with prerequisites', mode: 'learn', icon: '⧫', color: '#cc5de8' },
  { id: 'writing', label: 'I want to write something', desc: 'Plan and structure any written work', mode: 'writing', icon: '✦', color: '#20c997' },
  { id: 'plan', label: "I'm planning a project", desc: 'Build roadmaps, milestones, and dependencies', mode: 'plan', icon: '◉', color: '#339af0' },
  { id: 'demo', label: 'Just show me', desc: 'Watch AI think live — takes 15 seconds', mode: 'idea', icon: '▶', color: '#ff6b6b' },
];

function OnboardingSparks({ onSparkClick, onNewWorkflow }) {
  const [fadeSparks, setFadeSparks] = useState(false);

  return (
    <div className={`onboarding-sparks ${fadeSparks ? 'onboarding-sparks--fading' : ''}`}>
      <div className="onboarding-sparks-icon">◈</div>
      <div className="onboarding-sparks-title">What's on your mind?</div>
      <div className="onboarding-sparks-sub">Pick one and watch AI build a thinking tree in real-time.</div>

      {/* Workflow CTA */}
      <button className="empty-state-workflow-btn" onClick={() => { setFadeSparks(true); setTimeout(onNewWorkflow, 300); }}>
        <span className="empty-state-workflow-icon">🔀</span>
        <div className="empty-state-workflow-text">
          <span className="empty-state-workflow-label">New Workflow</span>
          <span className="empty-state-workflow-desc">Set a goal, see the AI thinking plan, then run it</span>
        </div>
        <span className="empty-state-workflow-arrow">→</span>
      </button>

      <div className="empty-state-divider">
        <span>or quick start a mode</span>
      </div>

      <div className="onboarding-sparks-grid">
        {ONBOARDING_SPARKS.map(spark => (
          <button
            key={spark.id}
            className={`onboarding-spark ${spark.id === 'demo' ? 'onboarding-spark--demo' : ''}`}
            onClick={() => {
              setFadeSparks(true);
              setTimeout(() => onSparkClick(spark), 300);
            }}
          >
            <span className="onboarding-spark-icon" style={{ color: spark.color }}>{spark.icon}</span>
            <span className="onboarding-spark-text">
              <span className="onboarding-spark-label">{spark.label}</span>
              <span className="onboarding-spark-desc">{spark.desc}</span>
            </span>
            {spark.id === 'demo' && <span className="onboarding-spark-arrow">→</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ onNewSession, onNewWorkflow, isFirstTime, onSparkClick }) {
  if (isFirstTime) {
    return <OnboardingSparks onSparkClick={onSparkClick} onNewWorkflow={onNewWorkflow} />;
  }
  return (
    <div className="empty-state">
      <div className="empty-state-title">What are you building?</div>
      <div className="empty-state-sub">
        Start a new session or pick one from the sidebar.
      </div>

      {/* Workflow-first CTA */}
      <button className="empty-state-workflow-btn" onClick={onNewWorkflow}>
        <span className="empty-state-workflow-icon">🔀</span>
        <div className="empty-state-workflow-text">
          <span className="empty-state-workflow-label">New Workflow</span>
          <span className="empty-state-workflow-desc">Set a goal, see the AI thinking plan, then run it</span>
        </div>
        <span className="empty-state-workflow-arrow">→</span>
      </button>

      <div className="empty-state-divider">
        <span>or quick start a mode</span>
      </div>

      <div className="empty-state-modes">
        {MODES.filter(m => !m.hidden).map(m => (
          <button
            key={m.id}
            className="empty-state-chip"
            onClick={() => onNewSession(m.id)}
          >
            <span className="empty-state-chip-icon" style={{ color: m.color }}>{m.icon}</span>
            <span className="empty-state-chip-text">
              <span className="empty-state-chip-label">{m.label}</span>
              {MODE_DESCRIPTIONS[m.id] && <span className="empty-state-chip-desc">{MODE_DESCRIPTIONS[m.id]}</span>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Onboarding Tooltips (shown during first generation) ──
function OnboardingTooltips({ step, onDismiss }) {
  const TIPS = [
    { id: 'streaming', text: 'Nodes are streaming from AI — each one is clickable', position: 'top-center' },
    { id: 'drill', text: 'Double-click any node to drill deeper', position: 'top-center' },
    { id: 'debate', text: 'Try the debate button — two AI personas will argue your idea', position: 'bottom-right' },
  ];
  const tip = TIPS[step];
  if (!tip) return null;
  return (
    <div className={`onboarding-tooltip onboarding-tooltip--${tip.position}`}>
      <span className="onboarding-tooltip-text">{tip.text}</span>
      <button className="onboarding-tooltip-x" onClick={() => onDismiss(tip.id)}>×</button>
    </div>
  );
}

// ── Progressive Nudge (sessions 2-3) ──
function ProgressiveNudge({ text, onDismiss }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => { setVisible(false); onDismiss(); }, 8000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  if (!visible) return null;
  return (
    <div className="progressive-nudge" onClick={() => { setVisible(false); onDismiss(); }}>
      <span className="progressive-nudge-text">{text}</span>
    </div>
  );
}

// ── Route wrapper: /share/:id → ShareViewer, landing page if not logged in, else → main app ──
function AppRouter() {
  const { user, loading, logout, isConfigured, getToken, forceRefreshToken } = useAuth();
  const [activeSession, setActiveSession] = useState(null);
  const [activeForest, setActiveForest] = useState(null);
  const [workflowSetup, setWorkflowSetup] = useState(null); // { sessionId } when in workflow creation flow
  const [showSettings, setShowSettings] = useState(() => window.location.pathname.startsWith('/settings'));
  const [settingsTab, setSettingsTab] = useState(null);
  const [showKnowledge, setShowKnowledge] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_collapsed');
      if (saved !== null) return saved === 'true';
      return window.innerWidth <= 768; // default collapsed on mobile
    } catch { return false; }
  });
  const sidebarRef = useRef(null);

  // ── Onboarding: detect first-time user ──
  const [isFirstTime, setIsFirstTime] = useState(() => !getOnboardingState().completed);

  const handleSparkClick = useCallback((spark) => {
    if (spark.id === 'demo') {
      // "Just show me" — open a decision session with pre-filled idea
      setActiveSession({
        isNew: true,
        mode: spark.mode,
        onboardingDemo: true,
        onboardingIdea: 'An app that turns voice memos into structured project plans',
      });
    } else {
      // Regular spark — open a new session in that mode
      setActiveSession({ isNew: true, mode: spark.mode, onboardingSpark: true });
    }
    setOnboardingState({ completed: true });
    setIsFirstTime(false);
  }, []);

  // Wire up authFetch token getter at the router level (before any child effects)
  // This must be here (not in App) because KnowledgeGraph and SettingsPage
  // render as alternatives to App and need authFetch to work.
  useEffect(() => {
    setTokenGetter(getToken, forceRefreshToken);
  }, [getToken, forceRefreshToken]);

  // Persist sidebar collapsed state
  useEffect(() => {
    try { localStorage.setItem('sidebar_collapsed', sidebarCollapsed); }
    catch { /* ignore */ }
  }, [sidebarCollapsed]);

  // Check access on first authenticated API call
  useEffect(() => {
    if (!user || !isConfigured) return;
    authFetch(`${API_URL}/api/usage`)
      .then(res => {
        if (res.status === 403) setAccessDenied(true);
      })
      .catch(() => { /* ignore network errors */ });
  }, [user, isConfigured]);

  // Share links require auth (standalone, no sidebar)
  const shareMatch = window.location.pathname.match(/^\/share\/([a-zA-Z0-9_-]+)$/);
  if (shareMatch) {
    if (loading) return <LoadingScreen />;
    if (isConfigured && !user) return <LandingPage shareId={shareMatch[1]} />;
    if (accessDenied) return <AccessDenied email={user?.email} onLogout={logout} />;
    return <ShareViewer shareId={shareMatch[1]} />;
  }

  // Room links — collaborative sessions via Yjs (standalone, no sidebar)
  const roomMatch = window.location.pathname.match(/^\/room\/([a-zA-Z0-9_-]+)$/);
  if (roomMatch) {
    if (loading) return <LoadingScreen />;
    if (isConfigured && !user) return <LandingPage roomId={roomMatch[1]} />;
    if (accessDenied) return <AccessDenied email={user?.email} onLogout={logout} />;
    return (
      <YjsProvider roomId={roomMatch[1]}>
        <App
          initialSession={{ isNew: true, roomId: roomMatch[1] }}
          onBackToDashboard={() => {
            window.location.href = '/';
          }}
          onSessionSaved={() => {
            // Room sessions are auto-saved — refresh sidebar on next dashboard visit
          }}
        />
      </YjsProvider>
    );
  }

  // Invite links — accept workspace invitations
  const inviteMatch = window.location.pathname.match(/^\/invite\/([a-f0-9]+)$/);
  if (inviteMatch) {
    if (loading) return <LoadingScreen />;
    if (isConfigured && !user) return <LandingPage />;
    return <InviteAccept token={inviteMatch[1]} />;
  }

  // Blog page (public, no auth required)
  if (window.location.pathname === '/blog') {
    return <BlogPage />;
  }

  // Show loading while Firebase checks auth state
  if (loading) return <LoadingScreen />;

  // If Firebase is configured and user not signed in, show landing page
  if (isConfigured && !user) {
    return <LandingPage />;
  }

  // If user is signed in but not authorized (403 from server)
  if (accessDenied) {
    return <AccessDenied email={user?.email} onLogout={logout} />;
  }

  const normalizedPath = window.location.pathname.replace(/\/+$/, '');
  const effectivePath = normalizedPath || '/';

  // Lifecycle-first redesign remains available at `/next`, but the authenticated
  // home is the original shell again.
  if (effectivePath === '/next') {
    return (
      <NextExperience
        user={user}
        logout={logout}
        renderSession={(session, onBack) => (
          <App
            key={session.id || `next-${session.mode || 'idea'}-${session.prefillIdea || 'new'}`}
            initialSession={{ ...session, nextExperience: true }}
            onBackToDashboard={onBack}
            onSessionSaved={() => {}}
            onOpenSettings={(tab) => {
              const suffix = tab ? `?tab=${encodeURIComponent(tab)}` : '';
              window.location.href = `/settings${suffix}`;
            }}
          />
        )}
        renderWorkflow={(request, handlers) => (
          <WorkflowSetup
            key={request.key}
            sessionId={`wf_${request.key.toString(36)}`}
            initialMode={request.modeId}
            initialGoal={request.goal}
            onWorkflowReady={({ workflow, goal, contextText, contextSources, intakeMeta }) => handlers.onReady({
              isNew: true,
              mode: 'idea',
              workflow,
              workflowGoal: goal,
              workflowContext: contextText,
              workflowSources: contextSources,
              intakeMeta: intakeMeta || null,
              nextExperience: true,
            })}
            onSkip={handlers.onSkip}
          />
        )}
        renderKnowledge={(onClose) => <KnowledgeGraph onClose={onClose} />}
        renderSettings={(tab, onClose, onOpenSession) => (
          <SettingsPage initialTab={tab} onClose={onClose} onOpenSession={onOpenSession} />
        )}
      />
    );
  }

  // `/classic` and legacy authenticated routes render the original shell.
  // Refresh sidebar after session auto-save
  const handleSessionSaved = () => {
    sidebarRef.current?.refresh();
  };

  // Close sidebar on mobile when a session is opened
  const handleOpenSession = (session) => {
    setActiveSession(session);
    if (window.innerWidth <= 768) setSidebarCollapsed(true);
  };

  // Authenticated (or auth not configured = local dev)
  return (
    <div className="app-layout">
      {/* Mobile: backdrop when sidebar is open */}
      {!sidebarCollapsed && (
        <div
          className="sidebar-backdrop"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}
      <Sidebar
        ref={sidebarRef}
        activeSessionId={activeSession?.id || null}
        onOpenSession={handleOpenSession}
        onNewSession={() => { setActiveSession(null); setWorkflowSetup(null); setActiveForest(null); setShowSettings(false); setShowKnowledge(false); if (window.innerWidth <= 768) setSidebarCollapsed(true); }}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(c => !c)}
        onOpenSettings={() => { setShowSettings(true); setShowKnowledge(false); setActiveSession(null); setActiveForest(null); if (window.innerWidth <= 768) setSidebarCollapsed(true); }}
        onOpenKnowledge={() => { setShowKnowledge(true); setShowSettings(false); setActiveSession(null); setActiveForest(null); if (window.innerWidth <= 768) setSidebarCollapsed(true); }}
        onOpenForest={(forest) => { setActiveForest(forest); setActiveSession(null); setShowSettings(false); setShowKnowledge(false); if (window.innerWidth <= 768) setSidebarCollapsed(true); }}
      />
      <div className="app-main">
        {/* Mobile: floating toggle to reopen sidebar */}
        {sidebarCollapsed && (
          <button
            className="sidebar-mobile-toggle"
            onClick={() => setSidebarCollapsed(false)}
          >
            ☰
          </button>
        )}
        {showKnowledge ? (
          <KnowledgeGraph onClose={() => setShowKnowledge(false)} />
        ) : showSettings ? (
          <SettingsPage initialTab={settingsTab} onClose={() => { setShowSettings(false); setSettingsTab(null); window.history.pushState({}, '', '/'); }} onOpenSession={handleOpenSession} />
        ) : activeForest ? (
          <ForestProvider forest={activeForest}>
            <ForestCanvasArea
              forest={activeForest}
              onSessionSaved={handleSessionSaved}
              onExit={() => setActiveForest(null)}
            />
          </ForestProvider>
        ) : workflowSetup ? (
          <WorkflowSetup
            sessionId={workflowSetup.sessionId}
            initialMode={workflowSetup.modeId}
            onWorkflowReady={({ workflow, goal, contextText, contextSources, intakeMeta }) => {
              // Transition to the full App with the workflow ready to execute
              setWorkflowSetup(null);
              setActiveSession({
                isNew: true,
                mode: 'idea',
                workflow,
                workflowGoal: goal,
                workflowContext: contextText,
                workflowSources: contextSources,
                // Guided-intake record → lands in session meta.intake +
                // seeds sessionBrief (see SPEC-GUIDED-INTAKE.md §2.7)
                intakeMeta: intakeMeta || null,
              });
            }}
            onSkip={() => {
              setWorkflowSetup(null);
              setActiveSession({ isNew: true, mode: 'idea' });
            }}
          />
        ) : !activeSession ? (
          <EmptyState
            isFirstTime={isFirstTime}
            onSparkClick={handleSparkClick}
            onNewSession={(modeId) => setActiveSession({ isNew: true, mode: modeId })}
            onNewWorkflow={(modeId) => {
              // Create a temporary session ID for file uploads during workflow setup
              const tempId = `wf_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
              setWorkflowSetup({ sessionId: tempId, modeId });
            }}
          />
        ) : (
          <App
            key={activeSession.id || 'new-' + (activeSession.mode || 'default')}
            initialSession={activeSession}
            onBackToDashboard={() => { setActiveSession(null); setWorkflowSetup(null); setActiveForest(null); setShowSettings(false); setShowKnowledge(false); }}
            onSessionSaved={handleSessionSaved}
            onOpenSettings={(tab) => { setSettingsTab(tab || null); setShowSettings(true); setActiveSession(null); }}
            onOpenForest={(forest) => { setActiveForest(forest); setActiveSession(null); }}
          />
        )}
      </div>
    </div>
  );
}

// ── Forest Canvas Area: tab bar + meta view or inline canvas nodes ──
function ForestCanvasArea({ forest, onSessionSaved, onExit }) {
  const ctx = useForest();

  const activeCanvasKey = ctx?.activeCanvasKey ?? '__meta__';
  const forestCanvases = ctx?.forestCanvases ?? [];

  // Build a synthetic session for the active canvas tab.
  // The key on <App> forces React to remount when switching canvases,
  // which resets the internal useCanvasMode state cleanly.
  const canvasSession = React.useMemo(() => {
    if (activeCanvasKey === '__meta__') return null;
    const canvas = forestCanvases.find(c => c.canvasKey === activeCanvasKey);
    if (!canvas) return null;
    return {
      id: `forest-canvas-${activeCanvasKey}`,
      label: canvas.title || activeCanvasKey,
      rawNodes: canvas.nodes || [],
      mode: 'idea',
      source: 'forest',
      _forestCanvasKey: activeCanvasKey,
    };
  }, [activeCanvasKey, forestCanvases]);

  if (!ctx) return null;

  return (
    <div className="forest-canvas-area" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <ForestTabBar onExit={onExit} />
      {activeCanvasKey === '__meta__' ? (
        <ForestMetaCanvas />
      ) : activeCanvasKey === '__synthesis__' ? (
        <ForestSynthesis />
      ) : canvasSession ? (
        <App
          key={activeCanvasKey}
          initialSession={canvasSession}
          onBackToDashboard={onExit}
          onSessionSaved={onSessionSaved}
        />
      ) : (
        <div className="forest-meta-empty">Select a canvas tab to view it.</div>
      )}
    </div>
  );
}

export { AppRouter };
export default function App({ initialSession, onBackToDashboard, onSessionSaved, onOpenForest, onOpenSettings }) {
  // ── Hydrate server-backed local cache on mount ────────────
  // For keys that persist server-side via serverStorage (version history,
  // session memory, saved templates), pull the canonical value from
  // Firestore once at boot. Without this a fresh browser / cleared
  // localStorage starts empty even though the data exists server-side.
  // See client/src/serverStorage.js + server/routes/kv.js.
  useEffect(() => {
    serverStorage.hydrate('IDEA_GRAPH_VERSIONS');
    serverStorage.hydrate('IDEA_MEMORY_SESSIONS');
    serverStorage.hydrate('THOUGHT_TEMPLATES');
    // Per-mode session lists (see useCanvasMode storageKey args below)
    serverStorage.hydrate('IDEA_CANVAS_SESSIONS');
    serverStorage.hydrate('CODEBASE_CANVAS_SESSIONS');
  }, []);

  // ── Toolbar scroll ──────────────────────────────────────────
  const toolbarScrollRef = useRef(null);
  const [toolbarCanScrollLeft, setToolbarCanScrollLeft] = useState(false);
  const [toolbarCanScrollRight, setToolbarCanScrollRight] = useState(false);

  // ── Mode ──────────────────────────────────────────────────
  const [manualMode, setManualMode]   = useState(() => {
    // For new sessions with a specific mode, set it immediately
    if (initialSession?.isNew && initialSession.mode && initialSession.mode !== 'idea') {
      return initialSession.mode;
    }
    return null; // null = follow auto-detect
  });
  const [detectedMode, setDetectedMode] = useState(null);
  const detectTimerRef = useRef(null);

  // ── Novelty generation controls ────────────────────────────
  const [chaosLevel, setChaosLevel] = useState(() => {
    try { return parseInt(localStorage.getItem('chaos_level') ?? '100', 10); } catch { return 100; }
  });
  const [constraints, setConstraints] = useState(() => {
    try { return JSON.parse(localStorage.getItem('novelty_constraints') ?? '[]'); } catch { return []; }
  });
  const [showConstraints, setShowConstraints] = useState(false);
  const [constraintInput, setConstraintInput] = useState('');

  // Persist novelty controls
  useEffect(() => { try { localStorage.setItem('chaos_level', String(chaosLevel)); } catch {} }, [chaosLevel]);
  useEffect(() => { try { localStorage.setItem('novelty_constraints', JSON.stringify(constraints)); } catch {} }, [constraints]);

  // ── Idea mode specific state ──────────────────────────────
  const [idea, setIdea] = useState('');
  const [redirectState, setRedirectState] = useState('idle');
  const [steeringText, setSteeringText] = useState('');
  const ideaRef = useRef(null);          // textarea ref for auto-resize
  const fileInputRef = useRef(null);     // hidden file input ref
  const sessionFileInputRef = useRef(null); // hidden file input for session context files
  const [attachedFile, setAttachedFile] = useState(null); // { name, size }

  // ── Codebase mode specific state ──────────────────────────
  const [cbFolderName, setCbFolderName] = useState('');
  const [cbProjectPath, setCbProjectPath] = useState('');
  const executionAbortRef = useRef(null);

  // ── Resume mode specific state ────────────────────────────
  const [resumeJobLabel, setResumeJobLabel] = useState('');
  const [resumePdf, setResumePdf]           = useState(null); // base64 PDF kept for changes API
  // Synthesis is declared before displayMode below, so keep the current mode and
  // evidence state in a ref. This prevents Career synthesis from treating a job
  // description as if it were the candidate's resume.
  const synthesisContextRef = useRef({ mode: 'idea', hasResume: false });
  synthesisContextRef.current = {
    mode: manualMode ?? detectedMode ?? 'idea',
    hasResume: !!resumePdf,
  };

  // ── Learn mode PDF state ─────────────────────────────────
  const [learnPdf, setLearnPdf] = useState(null); // base64 PDF for paper-aware learning
  const [showResumeChanges, setShowResumeChanges] = useState(false);
  const [resumeChanges, setResumeChanges]   = useState(null); // { summary, changes[] }
  const [isGeneratingChanges, setIsGeneratingChanges] = useState(false);
  const [resumeChangesError, setResumeChangesError]   = useState(null);

  // ── Auth ────────────────────────────────────────────────
  const { user: authUser, getToken, logout: authLogout } = useAuth();
  const [upgradePrompt, setUpgradePrompt] = useState(null); // { limit, plan }
  // Note: setTokenGetter is called in AppRouter (parent) so authFetch works
  // for all views (Settings, Knowledge Graph, etc.), not just App.

  // ── Gateway (WebSocket) ─────────────────────────────────
  const gateway = useGateway(WS_URL, getToken);
  const gatewayRef = useRef(gateway);
  gatewayRef.current = gateway;
  const sessionIdRef = useRef(null);
  sessionIdRef.current = gateway.sessionId || null;

  // ── Yjs collaborative context ───────────────────────────
  const yjs = useYjs(); // null when not in a /room/ URL
  const yjsSyncRef = useRef(null);
  yjsSyncRef.current = yjs;

  // ── Node focus for chat-first interactions ─────────────────
  const handleNodeFocus = useCallback((node, opts = {}) => {
    if (!node) {
      setFocusedNode(null);
      return;
    }
    setFocusedNode({ node, surgicalExpanded: opts.surgicalExpanded || false });
    setShowChat(true);
  }, []);

  // ── Novelty ref (for hooks that need chaos/constraints) ───
  const noveltyRef = useRef({ chaosLevel: 100, constraints: [] });
  noveltyRef.current = { chaosLevel, constraints };

  // ── Canvas hooks ──────────────────────────────────────────
  const idea$ = useCanvasMode({ storageKey: 'IDEA_CANVAS_SESSIONS', sessionLabel: 'idea', yjsSyncRef, onNodeFocus: handleNodeFocus, noveltyRef });
  const cb$ = useCanvasMode({ storageKey: 'CODEBASE_CANVAS_SESSIONS', sessionLabel: 'folderName', onNodeFocus: handleNodeFocus, noveltyRef });

  // ── Auto-Refine hooks (one per canvas) ─────────────────────
  const refineIdea$ = useAutoRefine({
    rawNodesRef: idea$.rawNodesRef,
    applyLayout: idea$.applyLayout,
    drillStackRef: idea$.drillStackRef,
    dynamicTypesRef: idea$.dynamicTypesRef,
    yjsSyncRef,
    setNodeCount: idea$.setNodeCount,
    sessionIdRef,
    noveltyRef,
  });
  const refineCb$ = useAutoRefine({
    rawNodesRef: cb$.rawNodesRef,
    applyLayout: cb$.applyLayout,
    drillStackRef: cb$.drillStackRef,
    dynamicTypesRef: cb$.dynamicTypesRef,
    yjsSyncRef: { current: null },
    setNodeCount: cb$.setNodeCount,
    sessionIdRef,
    noveltyRef,
  });

  // ── Portfolio hooks (one per canvas) ────────────────────
  const portfolioIdea$ = usePortfolio({
    rawNodesRef: idea$.rawNodesRef,
    applyLayout: idea$.applyLayout,
    drillStackRef: idea$.drillStackRef,
    setNodeCount: idea$.setNodeCount,
    yjsSyncRef,
    noveltyRef,
  });
  const portfolioCb$ = usePortfolio({
    rawNodesRef: cb$.rawNodesRef,
    applyLayout: cb$.applyLayout,
    drillStackRef: cb$.drillStackRef,
    setNodeCount: cb$.setNodeCount,
    yjsSyncRef: { current: null },
    noveltyRef,
  });

  // ── Learn Loop hook (idea canvas only) ──────────────────────
  const learn$ = useLearnLoop({
    rawNodesRef: idea$.rawNodesRef,
    applyLayout: idea$.applyLayout,
    drillStackRef: idea$.drillStackRef,
    dynamicTypesRef: idea$.dynamicTypesRef,
    yjsSyncRef,
    setNodeCount: idea$.setNodeCount,
  });

  const [treeSwapBanner, setTreeSwapBanner] = useState(null);
  const [evolving, setEvolving] = useState(false);
  const [evolutionTask, setEvolutionTask] = useState(null);
  const experiment$ = useExperimentLoop({
    rawNodesRef: idea$.rawNodesRef,
    applyLayout: idea$.applyLayout,
    drillStackRef: idea$.drillStackRef,
    dynamicTypesRef: idea$.dynamicTypesRef,
    yjsSyncRef,
    setNodeCount: idea$.setNodeCount,
    sessionIdRef,
    onTreeSwapped: (info) => {
      setTreeSwapBanner(info);
      setTimeout(() => setTreeSwapBanner(null), 4000);
    },
  });

  // ── Data Streams hook (synaptic nodes) ──────────────────
  const streams$ = useStreams({ sessionId: sessionIdRef.current, authFetch });

  // ── Synaptic Phase 2 hooks ────────────────────────────────
  const synapses$ = useSynapses({ sessionId: sessionIdRef.current, authFetch });
  const activation$ = useActivation({ sessionId: sessionIdRef.current, authFetch });

  const mnemonic$ = useMnemonicVideo();
  const proto$ = usePrototypeBuilder();
  const [videoModalNodeId, setVideoModalNodeId] = useState(null);

  // ── Load initial session from dashboard ───────────────────
  const initialSessionLoaded = useRef(false);
  const [sessionLoading, setSessionLoading] = useState(
    !!(initialSession && !initialSession.isNew && initialSession.source === 'cloud' && initialSession.id)
  );
  useEffect(() => {
    if (initialSessionLoaded.current || !initialSession || initialSession.isNew) return;
    initialSessionLoaded.current = true;

    const loadSession = (session) => {
      const sessionMode = session.mode || 'idea';
      // Normalize cloud session nodes field to rawNodes
      const normalized = { ...session };
      if (!normalized.rawNodes && normalized.nodes?.length) {
        normalized.rawNodes = normalized.nodes.map(n => n.id && n.data ? n : buildFlowNode(n));
      }
      // Ensure all rawNodes have the data wrapper (forest canvas nodes may be raw)
      if (normalized.rawNodes?.length && normalized.rawNodes[0] && !normalized.rawNodes[0].data) {
        normalized.rawNodes = normalized.rawNodes.map(n => n.data ? n : buildFlowNode(n));
      }

      if (sessionMode === 'codebase') {
        if (normalized.rawNodes?.length) {
          cb$.handleLoadSession(normalized, (label) => setCbFolderName(label));
        }
        setManualMode('codebase');
      } else {
        if (normalized.rawNodes?.length) {
          idea$.handleLoadSession(normalized, (label) => setIdea(label));
        } else if (normalized.idea) {
          setIdea(normalized.idea);
        }
        if (sessionMode !== 'idea') {
          setManualMode(sessionMode);
        }
      }

      // Restore saved prototype if present
      if (normalized.prototype) {
        proto$.setPrototype(normalized.prototype);
      }
      // Restore chat messages if present
      if (normalized.chatMessages?.length) {
        setSavedChatMessages(normalized.chatMessages);
      }
      // Restore persisted contract verification checklist (contract loop).
      // Cloud sessions carry it on meta.intake; local sessions fall back to
      // the localStorage mirror.
      const cv = normalized.meta?.intake?.verification || loadContractVerificationCache(normalized.id || session.id);
      if (cv?.results?.length) {
        setContractVerification({ status: 'done', criteria: cv.results, iteration: cv.iteration || 0 });
      }
      // Restore artifacts and debates for context panel
      if (normalized.artifacts?.length) setSessionArtifacts(normalized.artifacts);
      if (normalized.debates?.length) setSessionDebates(normalized.debates);
      // Restore learn state (mastery + lesson cache + paper title) from server session
      if (normalized.learnMasteryMap || normalized.learnLessonCache) {
        learn$.restoreLearnState(normalized.learnMasteryMap, normalized.learnLessonCache);
      }
      if (normalized.learnPaperTitle) {
        learn$.setPaperTitle(normalized.learnPaperTitle);
      }
      // Restore cached workflowOutput (avoids re-synthesis on session click).
      // Priority order:
      //   1. Server-side persisted synthesis (canonical, survives across devices)
      //   2. localStorage cache (legacy, browser-local fallback)
      // Also seed outputNodeCountRef so the auto-synth effect doesn't fire as
      // long as the cached output's node count matches the loaded tree.
      const sid = normalized.id || session.id;
      const serverSynth = normalized.synthesizedOutput || session.synthesizedOutput;
      const serverSynthNodeCount = normalized.synthesizedOutputNodeCount ?? session.synthesizedOutputNodeCount;
      // Restore cached Tier 3 verifications so re-clicks return instantly
      const cachedVerifications = normalized.noveltyVerifications || session.noveltyVerifications;
      if (cachedVerifications && typeof cachedVerifications === 'object') {
        setVerifications(cachedVerifications);
      }
      // Restore the full novelty/QD/evolve/competition inspector state.
      // Lookup order: server (Firestore) → localStorage cache. The cache
      // covers local-only sessions that never reach Firestore.
      const snap = normalized.noveltySnapshot
        || session.noveltySnapshot
        || loadNoveltySnapshotCache(normalized.id || session.id);
      if (snap && typeof snap === 'object') {
        if (snap.noveltyScores) setNoveltyScores(snap.noveltyScores);
        if (snap.dmadResults) setDmadResults(snap.dmadResults);
        if (snap.noveltySummary) setNoveltySummary(snap.noveltySummary);
        if (snap.researchStatus) setResearchStatus(snap.researchStatus);
        if (snap.competition) setCompetition(snap.competition);
        if (snap.qdArchive) setQdArchive(snap.qdArchive);
        if (snap.qdTags) setQdTags(snap.qdTags);
        if (snap.recombinations) setRecombinations(snap.recombinations);
        if (snap.blendScores) setBlendScores(snap.blendScores);
        if (snap.evolveGenerations) setEvolveGenerations(snap.evolveGenerations);
        if (snap.evolveLineage) setEvolveLineage(snap.evolveLineage);

        // Synthesize a "done" generationFlow so the GenerationFlowCard +
        // NoveltyInspector re-render in chat when the user switches back.
        // Without this the card vanishes (live state is per-session) even
        // though the snapshot is restored.
        const restoredFlow = [];
        if ((normalized.nodes || normalized.rawNodes || []).length) {
          restoredFlow.push({ id: 'tree-gen', label: 'Tree Generation', status: 'done', nodeCount: (normalized.nodes || normalized.rawNodes || []).length });
        }
        if (snap.dmadResults?.length) restoredFlow.push({ id: 'novelty', label: 'Novelty Engine', status: 'done' });
        if (snap.qdArchive) restoredFlow.push({ id: 'qd', label: 'QD Archive + Recombine', status: 'done' });
        if (Object.keys(snap.competition || {}).length) restoredFlow.push({ id: 'competition', label: 'Competition scan', status: 'done' });
        if (Object.keys(snap.evolveGenerations || {}).length) restoredFlow.push({ id: 'evolve', label: 'Evolution (Phase C)', status: 'done' });
        if (restoredFlow.length) setGenerationFlow(restoredFlow);
      }
      if (serverSynth) {
        setWorkflowOutput(serverSynth);
        // Always seed staleness ref. Without this, sessions with
        // synthesizedOutput but null synthesizedOutputNodeCount trigger
        // a spurious re-synth on switch, replacing the cached output.
        outputNodeCountRef.current = serverSynthNodeCount != null
          ? serverSynthNodeCount
          : (normalized.nodes || normalized.rawNodes || []).length;
      } else if (sid) {
        try {
          const cache = JSON.parse(localStorage.getItem('TC_WORKFLOW_OUTPUTS') || '{}');
          if (cache[sid]?.content) {
            setWorkflowOutput(cache[sid].content);
            // localStorage cache pre-dates per-session staleness tracking;
            // assume it matches current node count to avoid spurious re-synth.
            outputNodeCountRef.current = (normalized.nodes || normalized.rawNodes || []).length;
          }
        } catch { /* non-fatal */ }
      }
      setSessionLoading(false);
    };

    // If session already has rawNodes (local), load immediately
    if (initialSession.rawNodes?.length) {
      loadSession(initialSession);
    } else if (initialSession.source === 'cloud' && initialSession.id) {
      // Fetch full session data from server for cloud sessions
      setSessionLoading(true);
      authFetch(`${API_URL}/api/sessions/${initialSession.id}`)
        .then(r => r.ok ? r.json() : null)
        .then(fullSession => {
          if (fullSession) loadSession(fullSession);
          else loadSession(initialSession);
        })
        .catch(() => loadSession(initialSession));
    } else {
      loadSession(initialSession);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Onboarding: auto-generate for "Just show me" demo ──
  const onboardingDemoTriggered = useRef(false);
  const [onboardingTooltipStep, setOnboardingTooltipStep] = useState(-1); // -1 = not active
  const [showDemoPrompt, setShowDemoPrompt] = useState(false);
  const [progressiveNudge, setProgressiveNudge] = useState(null); // { text }

  useEffect(() => {
    if (onboardingDemoTriggered.current) return;
    // Enable the full power pipeline for first-time users.
    // (autoRefineOnGen + autoPortfolioOnGen removed alongside the
    // Refine + Portfolio toolbar checkboxes — superseded by 🧬 Novelty.)
    if (initialSession?.onboardingDemo || initialSession?.onboardingSpark) {
      setNoveltyEngine(true);
      setEvolveEngine(true);
      setAutonomousMode(true);
    }
    if (initialSession?.onboardingDemo && initialSession.onboardingIdea) {
      onboardingDemoTriggered.current = true;
      setIdea(initialSession.onboardingIdea);
      // Auto-trigger generation after a brief delay for the UI to settle
      const t = setTimeout(() => {
        handleGenerateResearchRef.current?.();
      }, 600);
      return () => clearTimeout(t);
    }
    if (initialSession?.prefillIdea) {
      setIdea(initialSession.prefillIdea);
    }
    if (initialSession?.onboardingSpark) {
      // Focus the input for spark users
      setTimeout(() => ideaRef.current?.focus(), 300);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track first-ever generation for tooltips
  const handleGenerateResearchRef = useRef(null);
  useEffect(() => {
    handleGenerateResearchRef.current = handleGenerateResearch;
  });

  // Show tooltip step 0 when nodes start streaming (first-ever generation)
  const prevNodeCount = useRef(0);
  useEffect(() => {
    const ob = getOnboardingState();
    if (ob.tooltipsDismissed) return;
    const nodeCount = idea$.nodes.length;
    // Step 0: first nodes appear
    if (prevNodeCount.current === 0 && nodeCount > 0 && idea$.isGenerating && !ob.tooltipsDismissed) {
      if (!ob.firstGenDone) setOnboardingTooltipStep(0);
    }
    // Step 1: generation completes
    if (prevNodeCount.current > 0 && !idea$.isGenerating && onboardingTooltipStep === 0) {
      setOnboardingTooltipStep(1);
      // Show "Now try your own idea" for demo path
      if (initialSession?.onboardingDemo) {
        setShowDemoPrompt(true);
        setTimeout(() => { setShowDemoPrompt(false); setIdea(''); ideaRef.current?.focus(); }, 6000);
      }
      setOnboardingState({ firstGenDone: true });
    }
    prevNodeCount.current = nodeCount;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea$.nodes.length, idea$.isGenerating]);

  // Step 2: after user clicks a node
  const handleTooltipNodeInteraction = useCallback(() => {
    if (onboardingTooltipStep === 1) setOnboardingTooltipStep(2);
  }, [onboardingTooltipStep]);

  const handleDismissTooltip = useCallback(() => {
    setOnboardingTooltipStep(-1);
    setOnboardingState({ tooltipsDismissed: true });
  }, []);

  // Progressive nudges (sessions 2-3)
  useEffect(() => {
    const ob = getOnboardingState();
    const sessionCount = parseInt(ob.sessionCount || '0', 10);
    if (!idea$.isGenerating && idea$.nodes.length > 3 && !ob.tooltipsDismissed !== true) {
      if (sessionCount === 1 && !ob.debateNudged) {
        setProgressiveNudge({ text: 'Stress-test this? Two AI personas will debate your idea.' });
        setOnboardingState({ debateNudged: true });
      } else if (sessionCount === 2 && !ob.portfolioNudged) {
        setProgressiveNudge({ text: 'See 3 alternative approaches?' });
        setOnboardingState({ portfolioNudged: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea$.isGenerating, idea$.nodes.length]);

  // Increment session count on each generation
  useEffect(() => {
    if (idea$.isGenerating) {
      const ob = getOnboardingState();
      const count = parseInt(ob.sessionCount || '0', 10);
      setOnboardingState({ sessionCount: count + 1 });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea$.isGenerating]);

  // ── Yjs sync bridge: load remote nodes & subscribe to changes ──
  useEffect(() => {
    if (!yjs || !yjs.synced) return;

    // On initial sync, load any remote nodes into canvas
    const remoteNodes = yjs.readNodesFromYjs();
    if (remoteNodes.length > 0 && idea$.rawNodesRef.current.length === 0) {
      idea$.rawNodesRef.current = remoteNodes;
      idea$.applyLayout(remoteNodes, []);
      idea$.setNodeCount(remoteNodes.length);
    } else if (idea$.rawNodesRef.current.length > 0 && remoteNodes.length === 0) {
      // Migrate existing local nodes into the Yjs doc (one-time)
      yjs.writeNodesToYjs(idea$.rawNodesRef.current);
      yjs.writeMetaToYjs({ idea, mode: 'idea' });
    }

    // Load meta (idea text)
    const meta = yjs.readMetaFromYjs();
    if (meta.idea && !idea) setIdea(meta.idea);

    // Subscribe to remote node changes
    return yjs.onNodesChanged((allNodes) => {
      idea$.rawNodesRef.current = allNodes;
      idea$.applyLayout(allNodes, idea$.drillStackRef.current);
      idea$.setNodeCount(allNodes.length);
    });
  }, [yjs?.synced]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dynamic type config for adaptive mode ────────────────
  const dynamicConfigRef = useRef(null);    // built color/icon map
  const dynamicTypesRef = useRef(null);     // raw _meta types array
  const [dynamicDomain, setDynamicDomain] = useState(null); // domain label for legend
  const [dynamicLegendTypes, setDynamicLegendTypes] = useState([]); // ordered types for legend
  const [isFetchingUrl, setIsFetchingUrl] = useState(false);
  const [isDecomposing, setIsDecomposing] = useState(false);

  // ── Thinking Patterns ──────────────────────────────────────
  const [activePattern, setActivePattern] = useState(null);        // pattern definition (from _meta)
  const [patternFramework, setPatternFramework] = useState(null);  // resolved framework metadata
  const [availablePatterns, setAvailablePatterns] = useState([]);   // all patterns for node-level picker

  // Fetch available patterns on mount
  useEffect(() => {
    authFetch(`${API_URL}/api/patterns`).then(r => r.ok ? r.json() : []).then(list => {
      if (Array.isArray(list)) setAvailablePatterns(list);
    }).catch(() => {});
  }, []);

  // ── Pattern Executor hook (must be after dynamicConfigRef declaration) ──
  const patternExec$ = usePatternExecutor({
    rawNodesRef: idea$.rawNodesRef,
    applyLayout: idea$.applyLayout,
    drillStackRef: idea$.drillStackRef,
    dynamicTypesRef: idea$.dynamicTypesRef,
    dynamicConfigRef,
    setNodeCount: idea$.setNodeCount,
    buildDynamicConfigFn: buildDynamicConfig,
  });

  // ── Synthesize output document from tree nodes (LLM-powered) ──
  // Workflow output document — shown in journey view, updated on every execution
  // Cached in localStorage to avoid re-synthesis on session reload
  const [workflowOutput, setWorkflowOutput] = useState(null);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const showChatRef = useRef(null); // forward-ref to setShowChat (defined later)
  const synthAbortRef = useRef(null);

  // Cache helpers for workflowOutput persistence
  const saveWorkflowOutputCache = useCallback((content) => {
    const sid = gateway.sessionId || initialSession?.id;
    if (!sid || !content) return;
    try {
      const cache = JSON.parse(localStorage.getItem('TC_WORKFLOW_OUTPUTS') || '{}');
      cache[sid] = { content, ts: Date.now() };
      // Keep max 20 cached outputs
      const keys = Object.keys(cache);
      if (keys.length > 20) {
        const sorted = keys.sort((a, b) => (cache[a].ts || 0) - (cache[b].ts || 0));
        for (let i = 0; i < keys.length - 20; i++) delete cache[sorted[i]];
      }
      localStorage.setItem('TC_WORKFLOW_OUTPUTS', JSON.stringify(cache));
    } catch { /* quota exceeded — non-fatal */ }
  }, [gateway.sessionId, initialSession?.id]);

  const loadWorkflowOutputCache = useCallback((sessionId) => {
    try {
      const cache = JSON.parse(localStorage.getItem('TC_WORKFLOW_OUTPUTS') || '{}');
      return cache[sessionId]?.content || null;
    } catch { return null; }
  }, []);

  // Contract verification cache — mirrors meta.intake.verification for
  // sessions without a server-minted id (same pattern as TC_WORKFLOW_OUTPUTS).
  const saveContractVerificationCache = useCallback((sessionId, record) => {
    if (!sessionId || !record) return;
    try {
      const cache = JSON.parse(localStorage.getItem('TC_CONTRACT_VERIFICATIONS') || '{}');
      cache[sessionId] = { record, ts: Date.now() };
      const keys = Object.keys(cache);
      if (keys.length > 20) {
        const sorted = keys.sort((a, b) => (cache[a].ts || 0) - (cache[b].ts || 0));
        for (let i = 0; i < keys.length - 20; i++) delete cache[sorted[i]];
      }
      localStorage.setItem('TC_CONTRACT_VERIFICATIONS', JSON.stringify(cache));
    } catch { /* quota exceeded — non-fatal */ }
  }, []);

  const loadContractVerificationCache = useCallback((sessionId) => {
    if (!sessionId) return null;
    try {
      const cache = JSON.parse(localStorage.getItem('TC_CONTRACT_VERIFICATIONS') || '{}');
      return cache[sessionId]?.record || null;
    } catch { return null; }
  }, []);

  // Snapshot cache for novelty/QD/evolve/competition state. Mirrors the
  // server-side noveltySnapshot field for sessions not synced to Firestore
  // (local sessions, anonymous users). Saved when state settles after a
  // generation; loaded on session switch to restore the right-side inspector.
  const saveNoveltySnapshotCache = useCallback((sessionId, snapshot) => {
    if (!sessionId || !snapshot) return;
    try {
      const cache = JSON.parse(localStorage.getItem('TC_NOVELTY_SNAPSHOTS') || '{}');
      cache[sessionId] = { snapshot, ts: Date.now() };
      const keys = Object.keys(cache);
      if (keys.length > 20) {
        const sorted = keys.sort((a, b) => (cache[a].ts || 0) - (cache[b].ts || 0));
        for (let i = 0; i < keys.length - 20; i++) delete cache[sorted[i]];
      }
      localStorage.setItem('TC_NOVELTY_SNAPSHOTS', JSON.stringify(cache));
    } catch { /* quota exceeded — non-fatal */ }
  }, []);

  const loadNoveltySnapshotCache = useCallback((sessionId) => {
    if (!sessionId) return null;
    try {
      const cache = JSON.parse(localStorage.getItem('TC_NOVELTY_SNAPSHOTS') || '{}');
      return cache[sessionId]?.snapshot || null;
    } catch { return null; }
  }, []);

  const updateWorkflowOutput = useCallback(async (nodes, goal) => {
    if (!nodes?.length) return;

    // Abort any in-flight synthesis
    if (synthAbortRef.current) synthAbortRef.current.abort();
    const controller = new AbortController();
    synthAbortRef.current = controller;

    // Show immediate placeholder while LLM synthesizes
    setWorkflowOutput('*Synthesizing output...*');
    setIsSynthesizing(true);

    try {
      const serialized = nodes.map(n => {
        const d = n.data || n;
        return {
          id: n.id || d.id,
          type: d.type,
          label: d.label,
          reasoning: d.reasoning,
          parentIds: d.parentIds || (d.parentId ? [d.parentId] : []),
        };
      });

      // Phase B redesign: include novelty engine output in synthesis context.
      // The deliverable doc now references blends, reasoner critiques, and QD coverage.
      // Read latest values from refs to avoid stale closure (this callback is memoized).
      const novelyPayload = (Object.keys(noveltyScoresRef.current || {}).length > 0 || (recombinationsRef.current || []).length > 0) ? {
        avgNovelty: noveltySummaryRef.current?.avgNovelty,
        topNovel: Object.entries(noveltyScoresRef.current || {})
          .filter(([, v]) => typeof v.score === 'number')
          .sort((a, b) => b[1].score - a[1].score)
          .slice(0, 5)
          .map(([nodeId, v]) => {
            const node = (nodes || []).find(n => (n.id || n.data?.id) === nodeId);
            return { nodeId, label: node?.data?.label || node?.label, score: v.score, closestExisting: v.closestExisting, whyNovel: v.whyNovel };
          }),
        recombinations: (recombinationsRef.current || []).map((r, i) => ({
          label: r.label, blendStrategy: r.blendStrategy, reasoning: r.reasoning,
          inheritsFrom: r.inheritsFrom, parents: r.parents, error: r.error,
          score: blendScoresRef.current?.[`blend-${i}`]?.score,
        })),
        dmadResults: (dmadResultsRef.current || []).map(d => ({ label: d.label, method: d.method, output: d.output, error: d.error })),
        qdStats: qdArchiveRef.current?.stats,
      } : null;

      const res = await authFetch(`${API_URL}/api/synthesize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nodes: serialized,
          goal: goal || '',
          mode: synthesisContextRef.current.mode,
          hasResume: synthesisContextRef.current.hasResume,
          chaosLevel: noveltyRef?.current?.chaosLevel ?? 100,
          noveltyContext: novelyPayload,
          // Persist the synthesis to the session record server-side so
          // future loads don't re-run the LLM. See loadSession restore path.
          sessionId: gateway.sessionId || initialSession?.id || null,
        }),
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(`Synthesis error: ${res.status}`);

      // Stream the response
      let content = '';
      await readSSEStream(res, (data) => {
        if (data.text) {
          content += data.text;
          setWorkflowOutput(content);
        }
      });

      if (content) {
        setWorkflowOutput(content);
        saveWorkflowOutputCache(content);
      }
      return content; // contract loop verifies against the synthesis text
    } catch (err) {
      if (err.name === 'AbortError') return null;
      console.error('Synthesis error:', err);
      // Fallback to basic tree walk on error
      const fallback = nodes.map(n => {
        const d = n.data || n;
        return `### ${d.label || n.id}\n${d.reasoning || ''}`;
      }).join('\n\n');
      setWorkflowOutput(fallback || '');
      saveWorkflowOutputCache(fallback);
      return fallback || null;
    } finally {
      setIsSynthesizing(false);
      synthAbortRef.current = null;
    }
  }, [saveWorkflowOutputCache]);

  // ── Auto-execute workflow from orchestrator ────────────────
  const patternExecRef = useRef(patternExec$);
  patternExecRef.current = patternExec$;

  useEffect(() => {
    if (!initialSession?.workflow) return;

    const wf = initialSession.workflow;
    const goal = initialSession.workflowGoal || '';
    const context = initialSession.workflowContext || '';

    setIdea(goal);

    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      patternExecRef.current.execute(
        wf, goal, [], 'idea', { context, domain: '' },
        // onComplete: synthesize output, then verify against the intake
        // contract (brief acceptance criteria) when one exists.
        async () => {
          const nodes = idea$.rawNodesRef.current;
          const synthesis = await updateWorkflowOutput(nodes, goal);
          const brief = initialSession?.intakeMeta?.brief;
          if (brief && !contractRunRef.current) {
            contractRunRef.current = true;
            runContractLoopRef.current?.(brief, goal, synthesis);
          }
        },
      );
    }, 800);

    return () => { cancelled = true; clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Contract loop (verify → fix → re-verify, cap 2 fix passes) ──
  // The brief's Acceptance Criteria are the contract. Fail-open: any
  // error leaves the run untouched and shows nothing fatal.
  // See features/ai-workflows/contract-loop.feature.
  const [contractVerification, setContractVerification] = useState(
    // Reloading a session restores the persisted checklist.
    () => {
      const v = initialSession?.intakeMeta?.verification || initialSession?.meta?.intake?.verification;
      return v?.results?.length ? { status: 'done', criteria: v.results, iteration: v.iteration || 0 } : null;
    }
  );
  const contractRunRef = useRef(false);
  const runContractLoopRef = useRef(null);

  const runContractLoop = useCallback(async (brief, goal, initialSynthesis) => {
    const post = (path, body) => authFetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(r => r.json());
    const serialize = () => (idea$.rawNodesRef.current || []).map(n => {
      const d = n.data || n;
      return { id: n.id || d.id, type: d.type, label: d.label, reasoning: d.reasoning };
    });

    let synthesis = initialSynthesis || '';
    let last = null;
    let iter = 0;
    try {
      for (iter = 0; iter <= 2; iter++) {
        setContractVerification({ status: 'verifying', criteria: last?.criteria || [], iteration: iter });
        const verify = await post('/api/contract/verify', { brief, nodes: serialize(), synthesis });
        if (verify.error) throw new Error(verify.error);
        if (!verify.criteria?.length) { setContractVerification(null); return; } // no criteria in brief
        last = verify;
        const failures = verify.criteria.filter(c => c.verdict === 'fail');
        if (!failures.length || iter >= 2) {
          setContractVerification({ status: 'done', criteria: verify.criteria, iteration: iter });
          break;
        }
        setContractVerification({ status: 'fixing', criteria: verify.criteria, iteration: iter });
        const fix = await post('/api/contract/fix', { brief, failures, nodes: serialize(), synthesis });
        if (fix.error || !fix.nodes?.length) {
          setContractVerification({ status: 'done', criteria: verify.criteria, iteration: iter, note: 'fix pass unavailable' });
          break;
        }
        for (const fn of fix.nodes) {
          if (fn._update) {
            const existing = (idea$.rawNodesRef.current || []).find(n => n.id === fn.id);
            if (existing) existing.data = { ...existing.data, label: fn.label, reasoning: fn.reasoning, contractFix: true };
          } else {
            const flowNode = buildFlowNode({ id: fn.id, type: fn.type, label: fn.label, reasoning: fn.reasoning, parentId: fn.parentId });
            flowNode.data.generatedBy = 'contract-fix';
            flowNode.data.contractFix = true;
            idea$.rawNodesRef.current = upsertNode(idea$.rawNodesRef.current, flowNode);
          }
        }
        idea$.applyLayout(idea$.rawNodesRef.current, []);
        idea$.setNodeCount((idea$.rawNodesRef.current || []).length);
        // Regenerate the doc so the next verify judges the fixed output.
        const next = await updateWorkflowOutput(idea$.rawNodesRef.current, goal);
        if (next) synthesis = next;
      }
      // Persist verification history (fire-and-forget). Server PUT needs a
      // minted session id; the localStorage mirror covers local sessions.
      const intake = initialSession?.intakeMeta;
      if (last) {
        const record = { at: new Date().toISOString(), iteration: iter, results: last.criteria };
        saveContractVerificationCache(gateway.sessionId || initialSession?.id, record);
        if (gateway.sessionId && intake) {
          authFetch(`${API_URL}/api/sessions/${gateway.sessionId}/intake`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ intake: { ...intake, verification: record } }),
          }).catch(() => { /* non-fatal */ });
        }
      }
    } catch (err) {
      console.warn('[contract] loop failed (fail-open):', err.message);
      setContractVerification(prev => prev?.criteria?.length
        ? { status: 'error', criteria: prev.criteria, iteration: iter, error: err.message }
        : null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateway.sessionId, updateWorkflowOutput]);
  runContractLoopRef.current = runContractLoop;

  // Persist the guided-intake record once the server has minted a session
  // id for this workflow run. Fire-and-forget; meta.intake + sessionBrief.
  const intakePersistedRef = useRef(false);
  useEffect(() => {
    const intake = initialSession?.intakeMeta;
    const sid = gateway.sessionId;
    if (!intake || !sid || intakePersistedRef.current) return;
    intakePersistedRef.current = true;
    authFetch(`${API_URL}/api/sessions/${sid}/intake`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ intake }),
    }).catch(() => { /* non-fatal */ });
  }, [gateway.sessionId, initialSession?.intakeMeta]);

  // ── Mode: derived ─────────────────────────────────────────
  // displayMode drives the tab highlight + placeholder + icon
  // activeMode collapses all non-codebase modes to 'idea' for canvas routing
  const displayMode = manualMode ?? detectedMode ?? 'idea';
  const activeMode  = displayMode === 'codebase' ? 'codebase' : 'idea';
  const active = activeMode === 'idea' ? idea$ : cb$;
  const refine$ = activeMode === 'idea' ? refineIdea$ : refineCb$;
  const portfolio$ = activeMode === 'idea' ? portfolioIdea$ : portfolioCb$;
  // ── Timeline filmstrip navigation ──────────────────────────
  const timeline = useTimelineNav({
    rawNodesRef: active.rawNodesRef,
    selectedNode: active.selectedNode,
    handleNodeClick: active.handleNodeClick,
    nodeCount: active.nodeCount,
  });
  const ideaText = useMemo(() => {
    if (activeMode === 'codebase') return cbFolderName;
    if (displayMode === 'resume') return resumeJobLabel;
    return idea;
  }, [activeMode, cbFolderName, displayMode, resumeJobLabel, idea]);
  // ── Pipeline state (hoisted before synthesis effects that reference it) ──
  const [pipelineStages, setPipelineStages] = useState(null); // pipeline overlay stages

  // ── Auto-update output doc when pattern execution finishes or nodes change ──
  const prevExecutingRef = useRef(false);
  const outputNodeCountRef = useRef(0);
  useEffect(() => {
    const wasExecuting = prevExecutingRef.current;
    const nowExecuting = patternExec$.isExecuting;
    prevExecutingRef.current = nowExecuting;

    // Transition from executing → done
    if (wasExecuting && !nowExecuting && idea$.rawNodesRef.current?.length > 0) {
      // Skip mid-pipeline — the pipeline-complete effect handles final synthesis
      if (pipelineStages) return;
      outputNodeCountRef.current = idea$.rawNodesRef.current.length;
      updateWorkflowOutput(idea$.rawNodesRef.current, ideaText);
      setViewMode('workflow-journey');
    }
  }, [patternExec$.isExecuting, updateWorkflowOutput, ideaText, pipelineStages]);


  // Auto-generate workflow output when nodes exist but output hasn't been created yet.
  //
  // Freshness model: outputNodeCountRef.current tracks the node count at the
  // time the cached synthesis was produced (set either by loadSession's cache
  // restore, or by this effect/the pipeline-complete effect when they fire).
  // Trigger only when:
  //   - we have nodes
  //   - we don't yet have an output OR the output is stale relative to current nodes
  //   - we're not in the middle of generating / executing a pattern / running a pipeline
  // This blocks the "click old session → re-synthesize" bug because loadSession
  // restores both workflowOutput AND outputNodeCountRef in the same render.
  useEffect(() => {
    const nodeCount = idea$.nodeCount;
    if (nodeCount === 0) return;
    if (patternExec$.isExecuting || idea$.isGenerating) return;
    if (pipelineStages) return; // pipeline-complete effect handles its own synth
    const isStale = workflowOutput && outputNodeCountRef.current !== nodeCount;
    const isMissing = !workflowOutput;
    if (!isStale && !isMissing) return;
    const timer = setTimeout(() => {
      outputNodeCountRef.current = nodeCount;
      updateWorkflowOutput(idea$.rawNodesRef.current, ideaText);
    }, 300);
    return () => clearTimeout(timer);
  }, [idea$.nodeCount, workflowOutput, patternExec$.isExecuting, idea$.isGenerating, updateWorkflowOutput, ideaText, pipelineStages]);

  // Update output when chat actions add/remove nodes (node count changes)
  useEffect(() => {
    const nodeCount = idea$.nodeCount;
    if (workflowOutput && nodeCount > 0 && nodeCount !== outputNodeCountRef.current && !patternExec$.isExecuting) {
      // Skip mid-pipeline — the pipeline-complete effect handles final synthesis
      if (pipelineStages) return;
      outputNodeCountRef.current = nodeCount;
      const timer = setTimeout(() => {
        updateWorkflowOutput(idea$.rawNodesRef.current, ideaText);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [idea$.nodeCount, workflowOutput, patternExec$.isExecuting, updateWorkflowOutput, ideaText, pipelineStages]);

  // ── Pipeline-complete synthesis: synthesize ONCE when all pipeline stages finish ──
  const prevPipelineAllDoneRef = useRef(false);
  useEffect(() => {
    if (!pipelineStages) { prevPipelineAllDoneRef.current = false; return; }
    const allDone = pipelineStages.every(s => s.status === 'done');
    const wasAllDone = prevPipelineAllDoneRef.current;
    prevPipelineAllDoneRef.current = allDone;
    // Only fire on transition to all-done (not on subsequent renders)
    if (allDone && !wasAllDone && idea$.rawNodesRef.current?.length > 0) {
      outputNodeCountRef.current = idea$.rawNodesRef.current.length;
      updateWorkflowOutput(idea$.rawNodesRef.current, ideaText);
      setViewMode('workflow-journey');
    }
  }, [pipelineStages, updateWorkflowOutput, ideaText]);

  // ── Node precision tools ────────────────────────────────────
  const nodeTools = useNodeTools({
    rawNodesRef: active.rawNodesRef,
    applyLayout: active.applyLayout,
    drillStackRef: active.drillStackRef,
    setNodeCount: active.setNodeCount,
    yjsSyncRef,
    selectedNode: active.selectedNode,
    handleNodeClick: active.handleNodeClick,
    deleteNodeBranch: active.deleteNodeBranch,
    handleSaveNodeEdit: active.handleSaveNodeEdit,
    idea: ideaText,
    mode: displayMode,
  });

  // ── F4: Undo Stack ──────────────────────────────────────────
  const undoStack = useUndoStack({
    rawNodesRef: active.rawNodesRef,
    applyLayout: active.applyLayout,
    drillStackRef: active.drillStackRef,
    setNodeCount: active.setNodeCount,
    yjsSyncRef,
  });

  // ── F4: Ghost Nodes ───────────────────────────────────────
  const ghostNodes = useGhostNodes({
    rawNodesRef: active.rawNodesRef,
    applyLayout: active.applyLayout,
    drillStackRef: active.drillStackRef,
    setNodeCount: active.setNodeCount,
  });

  // ── F4: Preview Overlay ───────────────────────────────────
  const [previewState, setPreviewState] = useState(null);
  // shape: { nodes, removedIds, label, onAccept, onReject } | null

  // ── F5: Hover Preview ─────────────────────────────────────
  const hoverPreview = useHoverPreview({ rawNodesRef: active.rawNodesRef });

  // ── F5: Inspector Panel ───────────────────────────────────
  const [inspectorNode, setInspectorNode] = useState(null);

  // ── Memory Layer ──────────────────────────────────────────
  const [showMemory, setShowMemory] = useState(false);
  const memorySessionCount = readMemory().length;

  // ── Version History ───────────────────────────────────────
  const [showHistory, setShowHistory] = useState(false);
  const [ideaVersions, setIdeaVersions] = useState([]);

  // ── Debate (inline chat card) ─────────────────────────────
  const debateRoundsRef = useRef([]);
  const [debateStream, setDebateStream] = useState(null);
  const debateAbortRef = useRef(null);
  const debateLoopRef = useRef(false);
  const startDebateInChatRef = useRef(null);
  const startRefineInChatRef = useRef(null);
  const startPortfolioInChatRef = useRef(null);
  const startPrototypeBuildRef = useRef(null);
  const handleConsensusReachedRef = useRef(null);

  // Auto-switch to journey view when debate/refine/portfolio finishes within a mode
  const prevDebateRef = useRef(null);
  const prevRefiningRef = useRef(false);
  const prevPortfolioRef = useRef(false);
  useEffect(() => {
    const wasDebating = prevDebateRef.current != null;
    const nowDebating = debateStream != null;
    const wasRefining = prevRefiningRef.current;
    const nowRefining = refine$.isRefining;
    const wasPortfolio = prevPortfolioRef.current;
    const nowPortfolio = portfolio$.isGenerating;

    prevDebateRef.current = debateStream;
    prevRefiningRef.current = nowRefining;
    prevPortfolioRef.current = nowPortfolio;

    const justFinished = (wasDebating && !nowDebating) || (wasRefining && !nowRefining) || (wasPortfolio && !nowPortfolio);
    if (justFinished && idea$.rawNodesRef.current?.length > 0) {
      // Skip mid-pipeline — the pipeline-complete effect handles final synthesis
      if (pipelineStages) return;
      outputNodeCountRef.current = idea$.rawNodesRef.current.length;
      updateWorkflowOutput(idea$.rawNodesRef.current, ideaText);
      setViewMode('workflow-journey');
    }
  }, [debateStream, refine$.isRefining, portfolio$.isGenerating, updateWorkflowOutput, ideaText, pipelineStages]);

  // ── View level (session vs cowork) ───────────────────────
  const [viewLevel, setViewLevel] = useState('session'); // 'session' | 'cowork'
  const coworkSelectRef = useRef(null); // callback to select artifact in CoworkPanel

  // ── Right panel (chat / context) ──────────────────────────
  const [rightPanel, setRightPanel] = useState(null); // 'chat' | 'context' | null
  const showChat = rightPanel === 'chat';
  const setShowChat = useCallback((v) => {
    if (typeof v === 'function') {
      setRightPanel(prev => v(prev === 'chat') ? 'chat' : null);
    } else {
      setRightPanel(v ? 'chat' : null);
    }
  }, []);
  showChatRef.current = setShowChat; // wire up forward-ref for updateWorkflowOutput
  const [chatFilter, setChatFilter] = useState(null);
  // shape: { types?: string[], nodeIds?: string[] } | null
  const [pendingChatCards, setPendingChatCards] = useState([]);
  const [savedChatMessages, setSavedChatMessages] = useState([]);
  const [generationFlow, setGenerationFlow] = useState(null); // array of { id, label, status, startedAt, completedAt, nodeCount, detail }
  const [agentFlowLog, setAgentFlowLog] = useState([]); // execution events for agent flow view
  // ── Phase A: Novelty engine state ──────────────────────────
  const [noveltyScores, setNoveltyScores] = useState({}); // { [nodeId]: { score, closestExisting, whyNovel } }
  const [dmadResults, setDmadResults] = useState([]); // [{ id, label, method, model, output: {critique, transformations}, ms }]
  // Live per-reasoner status map: { [reasonerId]: { label, model, status: 'running'|'done'|'error', elapsedMs, timeoutMs, startedAt, error? } }
  // Updated by _dmad_status (start/heartbeat) and _dmad (done) SSE events.
  // Surfaces in NoveltyInspector LIVE PROGRESS strip + LENSES tab as
  // pending rows showing "Means-Ends Analyst — 45s elapsed of 300s".
  const [dmadStatus, setDmadStatus] = useState({});
  const [noveltySummary, setNoveltySummary] = useState(null); // { avgNovelty } when stage_done fires
  // Research grounding status — populated by `_researchStatus` SSE event,
  // surfaced in SCORES tab so users know whether the engine ran on real
  // research or training-data opinion. See novelty-engine-phase-a.feature.
  const [researchStatus, setResearchStatus] = useState(null);
  // Competition grounding — Tier 2 auto-scan results + Tier 3 deep briefs.
  // See features/ai-workflows/novelty-engine-phase-a.feature.
  const [competition, setCompetition] = useState({});      // { [candidateId]: scan result }
  const [verifications, setVerifications] = useState({});  // { [candidateId]: deep brief }
  // ── Phase B: QD Archive + Recombination state ──────────────
  const [qdArchive, setQdArchive] = useState(null); // { stats, dimensions, cells }
  const [recombinations, setRecombinations] = useState([]); // [{label, blendStrategy, reasoning, inheritsFrom, parents, ms}]
  const [blendScores, setBlendScores] = useState({}); // { [blendId]: {score,...} }
  const [qdTags, setQdTags] = useState({}); // { [nodeId]: { mechanism, risk, audience, horizon } }
  const [discoveryDeckVisible, setDiscoveryDeckVisible] = useState(false);
  // ── Phase C: Evolutionary loop state ─────────────────
  const [evolveEngine, setEvolveEngine] = useState(false);
  const [evolveGenerations, setEvolveGenerations] = useState({}); // { [genIdx]: [candidate, ...] }
  const [evolveLineage, setEvolveLineage] = useState([]); // [{ childId, parentIds, operator, genIdx }]
  const [evolveActiveGen, setEvolveActiveGen] = useState(null);

  // Snapshot persistence to localStorage. Debounced mirror of the
  // noveltySnapshot. Server-side persistence covers cloud sessions
  // (Firestore); this covers local sessions + provides a fast restore
  // path on reload. Fires once data settles after a generation.
  useEffect(() => {
    // Session id sources, in priority: gateway (server-minted) → initial
    // mount → most recent local IDEA_CANVAS_SESSIONS entry. The third
    // fallback catches sessions created mid-runtime via "+ New" before
    // the gateway has attached its server-minted id.
    let sid = gateway.sessionId || initialSession?.id;
    if (!sid) {
      try {
        const localSessions = JSON.parse(localStorage.getItem('IDEA_CANVAS_SESSIONS') || '[]');
        sid = localSessions[0]?.id || null;
      } catch { /* non-fatal */ }
    }
    if (!sid) return;
    const hasData = Object.keys(noveltyScores).length > 0
      || dmadResults.length > 0
      || qdArchive
      || recombinations.length > 0
      || Object.keys(competition).length > 0
      || Object.keys(evolveGenerations).length > 0;
    if (!hasData) return;
    const t = setTimeout(() => {
      saveNoveltySnapshotCache(sid, {
        noveltyScores, dmadResults, noveltySummary, researchStatus,
        competition, qdArchive, qdTags, recombinations, blendScores,
        evolveGenerations, evolveLineage,
      });
    }, 800);
    return () => clearTimeout(t);
  }, [
    gateway.sessionId, initialSession?.id, saveNoveltySnapshotCache,
    noveltyScores, dmadResults, noveltySummary, researchStatus,
    competition, qdArchive, qdTags, recombinations, blendScores,
    evolveGenerations, evolveLineage,
  ]);

  // Refs mirror the novelty state so the memoized synthesize callback can
  // read the latest values without re-creating on every novelty update.
  const noveltyScoresRef = useRef({});
  const dmadResultsRef = useRef([]);
  const recombinationsRef = useRef([]);
  const blendScoresRef = useRef({});
  const qdArchiveRef = useRef(null);
  const noveltySummaryRef = useRef(null);
  // Live progress for each Phase A/B substep
  const [noveltyProgress, setNoveltyProgress] = useState({
    dmadDone: 0, dmadTotal: 6,
    scoreDone: 0, scoreTotal: 0,
    tagDone: 0, tagTotal: 0,
    blendDone: 0, blendTotal: 5,
    blendScoreDone: 0, blendScoreTotal: 0,
    activeStage: null, // 'dmad' | 'score' | 'tag' | 'blend' | 'blend_score' | null
  });
  const [noveltyFilters, setNoveltyFilters] = useState({
    minNovelty: 0,
    cellMechanism: null,
    cellRisk: null,
    cellAudience: null,
    cellHorizon: null,
  });
  const [executionStream, setExecutionStream] = useState(null); // { nodeLabel, text, done, error }
  const [refineStream, setRefineStream] = useState(null); // live refine progress for inline chat card
  const [portfolioStream, setPortfolioStream] = useState(null); // live portfolio progress for inline chat card
  const [learnStream, setLearnStream] = useState(null); // live learn loop progress for inline chat card
  const learnDrillPushRef = useRef(null); // push to LearnJourneyView breadcrumb from external callers (chat drill)
  const [experimentStream, setExperimentStream] = useState(null); // live experiment loop progress
  const [prototypeStream, setPrototypeStream] = useState(null); // live prototype build progress
  const [showPrototypeViewer, setShowPrototypeViewer] = useState(false);
  const [focusedNode, setFocusedNode] = useState(null); // { node, surgicalExpanded } for chat-first node interactions

  // ── Share ────────────────────────────────────────────────────
  const [showShareModal, setShowShareModal] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const reactFlowRef = useRef(null);

  // ── User profile & usage ───────────────────────────────────
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [usageData, setUsageData] = useState(null);

  // Fetch usage data on mount and close user menu on outside click
  useEffect(() => {
    authFetch(`${API_URL}/api/usage`).then(r => r.ok ? r.json() : null).then(d => d && setUsageData(d)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!showUserMenu) return;
    const close = () => setShowUserMenu(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showUserMenu]);

  // ── Auto-Fractal (∞ EXPLORE) ────────────────────────────
  const [showAutoFractal, setShowAutoFractal] = useState(false);
  const [autoFractalRounds, setAutoFractalRounds] = useState(5);
  const [autoFractalRunning, setAutoFractalRunning] = useState(false);
  const [autoFractalProgress, setAutoFractalProgress] = useState(null);

  // ── Post-generation automation options ───────────────────
  const [autoRefineOnGen, setAutoRefineOnGen] = useState(false);
  const [noveltyEngine, setNoveltyEngine] = useState(false); // 🧬 experimental: heterogeneous reasoners + novelty score (Phase A)
  const [autoPortfolioOnGen, setAutoPortfolioOnGen] = useState(false);
  const [autoPrototypeOnGen, setAutoPrototypeOnGen] = useState(false);
  const [portfolioFocus, setPortfolioFocus] = useState(null); // dynamic focus context from chat (types/nodeIds/userIntent)
  const [pipelineCheckpoint, setPipelineCheckpoint] = useState(null); // { recommended, stageType, alternatives, reasoning, shouldSkip }
  const [autonomousMode, setAutonomousMode] = useState(false); // skip checkpoints when true
  const advancePipelineRef = useRef(null); // ref to break circular hook dependency
  const [emailContext, setEmailContext] = useState(null); // { id, subject, messageCount, formatted }
  const [showPlusMenu, setShowPlusMenu] = useState(false); // + attachments popover
  const plusMenuBtnRef = useRef(null);
  const [claudeCodeContext, setClaudeCodeContext] = useState(() => initialSession?.contextPack?.context ? {
    context: initialSession.contextPack.context,
    sessionCount: (initialSession.contextPack.imported?.codeSessions || 0) + (initialSession.contextPack.imported?.coworkSessions || 0),
  } : null); // { context: string, sessionCount: number }
  const [showClaudeCodePicker, setShowClaudeCodePicker] = useState(false);
  const [showClaudeMemoryImport, setShowClaudeMemoryImport] = useState(false);
  const [sessionFiles, setSessionFiles] = useState([]); // attached files for this session
  const [sessionFileContext, setSessionFileContext] = useState(null); // extracted text context
  const [sessionArtifacts, setSessionArtifacts] = useState([]); // generated artifacts for this session
  const [googleArtifacts, setGoogleArtifacts] = useState([]); // Google Docs/Slides/Sheets created from chat
  const [sessionDebates, setSessionDebates] = useState([]); // debate rounds for this session

  const gmail = useGmail({
    onThreadSelected: (ctx) => setEmailContext(ctx),
    onClearEmail: () => setEmailContext(null),
    mode: displayMode,
  });

  // ── GitHub integration status ──────────────────────────────
  const [githubStatus, setGithubStatus] = useState({ configured: false, connected: false, account: null });
  useEffect(() => {
    authFetch(`${API_URL}/api/integrations/github/status`).then(r => r.ok ? r.json() : null).then(d => { if (d) setGithubStatus(d); }).catch(() => {});
  }, []);
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'github-connected') {
        setGithubStatus(prev => ({ ...prev, connected: true, account: e.data.username }));
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);
  const githubConnect = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/integrations/github/connect`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      if (!res.ok) return;
      const { authUrl } = await res.json();
      const w = 600, h = 700, left = window.screenX + (window.innerWidth - w) / 2, top = window.screenY + (window.innerHeight - h) / 2;
      window.open(authUrl, 'github-oauth', `width=${w},height=${h},left=${left},top=${top}`);
    } catch {}
  }, []);
  const githubDisconnect = useCallback(async () => {
    await authFetch(`${API_URL}/api/integrations/github/disconnect`, { method: 'POST' }).catch(() => {});
    setGithubStatus(prev => ({ ...prev, connected: false, account: null }));
  }, []);

  // ── View Mode ──────────────────────────────────────────────
  const [viewMode, setViewMode] = useState('flowchart'); // 'flowchart' | 'tree' | '3d'
  const is3D = viewMode === '3d'; // backward compat

  // Auto-switch to journey view when workflow output first becomes available
  const hadWorkflowOutputRef = useRef(false);
  useEffect(() => {
    if (workflowOutput && !hadWorkflowOutputRef.current && viewMode === 'flowchart') {
      hadWorkflowOutputRef.current = true;
      setViewMode('workflow-journey');
    } else if (workflowOutput) {
      hadWorkflowOutputRef.current = true;
    }
    if (!workflowOutput) {
      hadWorkflowOutputRef.current = false;
    }
  }, [workflowOutput, viewMode]);

  // ── 2D Temporal Navigation ──────────────────────────────
  const [roundRange, setRoundRange]           = useState([0, 12]);
  const [isPlayingRounds, setIsPlayingRounds] = useState(false);
  const [playbackSpeed, setPlaybackSpeed]     = useState(1);
  const [isolatedRound, setIsolatedRound]     = useState(null);
  const playbackTimerRef = useRef(null);

  const [isCritiquing, setIsCritiquing] = useState(false);
  const [treeSearchQuery, setTreeSearchQuery] = useState('');

  // ── Cross-links toggle ───────────────────────────────────
  const [showCrossLinks, setShowCrossLinks] = useState(false);

  // ── Command Palette (⌘K) ────────────────────────────────
  const [cmdPaletteOpen, setCmdPaletteOpen] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdPaletteOpen(v => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ── Node scoring ─────────────────────────────────────────
  const [isScoring, setIsScoring] = useState(false);

  // Shared progress banner state (set by research + codebase-analysis pipelines).
  // Originally introduced for the 3-lens multi-agent generator (now removed);
  // name kept to avoid a wide rename. See callsites in handleGenerateResearch
  // and handleAnalyzeCodebase.
  const [multiAgentProgress, setMultiAgentProgress] = useState(null);

  // ── Auto-save (skip when Yjs handles persistence or in forest canvas) ────────
  // Use ref to skip auto-save on initial session load (prevents timestamp update on click)
  const autoSaveInitRef = useRef(false);
  useEffect(() => {
    if (!autoSaveInitRef.current) { autoSaveInitRef.current = true; return; }
    if (yjs) return; // Yjs handles persistence via y-indexeddb
    if (initialSession?.source === 'forest') return; // Forest canvases save via ForestContext
    if (activeMode === 'idea') idea$.triggerAutoSave(idea, displayMode);
    if (activeMode === 'learn') idea$.triggerAutoSave(idea, 'learn');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea$.nodeCount, idea, activeMode]);

  useEffect(() => {
    if (initialSession?.source === 'forest') return;
    if (activeMode === 'codebase') cb$.triggerAutoSave(cbFolderName, 'codebase');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cb$.nodeCount, cbFolderName, activeMode]);

  // Notify parent (sidebar) after auto-save completes
  // Only trigger on actual new activity (node count increase), not initial session load
  const prevNodeCountRef = useRef(null);
  useEffect(() => {
    if (!onSessionSaved) return;
    const nodeCount = activeMode === 'codebase' ? cb$.nodeCount : idea$.nodeCount;
    if (nodeCount === 0) return;
    // First time we see nodes (initial load) — record but don't trigger refresh
    if (prevNodeCountRef.current === null) {
      prevNodeCountRef.current = nodeCount;
      return;
    }
    // Only refresh sidebar when node count actually changes (new generation/activity)
    if (nodeCount === prevNodeCountRef.current) return;
    prevNodeCountRef.current = nodeCount;
    const t = setTimeout(() => onSessionSaved(), 1200); // after auto-save debounce
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea$.nodeCount, cb$.nodeCount]);

  // ── Sync learn state (mastery + lessons) to server session ──
  const learnSyncTimerRef = useRef(null);
  useEffect(() => {
    const sid = gateway.sessionId || initialSession?.id;
    if (!sid || activeMode !== 'learn') return;
    clearTimeout(learnSyncTimerRef.current);
    learnSyncTimerRef.current = setTimeout(() => {
      authFetch(`${API_URL}/api/sessions/${sid}/learn`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          masteryMap: learn$.masteryMap,
          lessonCache: learn$.lessonCache,
          ...(learn$.paperTitle ? { paperTitle: learn$.paperTitle } : {}),
        }),
      }).catch(() => {});
    }, 2000); // debounce 2s
    return () => clearTimeout(learnSyncTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learn$.masteryMap, learn$.lessonCache, learn$.paperTitle, activeMode]);

  // ── Sync cross-links toggle to canvas mode ref ───────────
  useEffect(() => {
    active.showCrossLinksRef.current = showCrossLinks;
    if (active.rawNodesRef.current.length > 0) {
      active.applyLayout(active.rawNodesRef.current, active.drillStackRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCrossLinks]);

  // ── Scoring: trigger after generation ────────────────────
  const triggerScoring = useCallback(async (rawNodes, ideaText) => {
    if (!rawNodes?.length || !ideaText?.trim()) return;
    setIsScoring(true);
    try {
      const res = await authFetch(`${API_URL}/api/score-nodes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idea: ideaText.trim(),
          nodes: rawNodes.map(n => ({
            id: n.id, type: n.data?.type, label: n.data?.label,
            reasoning: n.data?.reasoning, parentId: n.data?.parentId,
          })),
        }),
      });
      if (!res.ok) throw new Error(`Score error: ${res.status}`);
      const { scores } = await res.json();
      active.setNodeScores(scores);
    } catch (err) {
      console.error('Scoring failed:', err);
    } finally {
      setIsScoring(false);
    }
  }, [idea$]);

  // ── Save version + memory after generation ────────────────
  const saveVersionAndMemory = useCallback((ideaText, rawNodes) => {
    if (!ideaText?.trim() || !rawNodes?.length) return;
    appendVersion(ideaText.trim(), rawNodes);
    appendMemory(buildMemoryEntry(ideaText.trim(), rawNodes));
  }, []);

  // ── 429 upgrade check helper ────────────────────────────
  const checkUpgradable = useCallback(async (res) => {
    if (res.status === 429) {
      try {
        const data = await res.json();
        if (data.upgradable) {
          setUpgradePrompt({ limit: data.limit, plan: data.plan });
          throw new Error('Daily generation limit reached. Upgrade to Pro for more.');
        }
        throw new Error(data.error || 'Rate limit exceeded');
      } catch (e) {
        if (e.message.includes('limit')) throw e;
        throw new Error('Rate limit exceeded');
      }
    }
    if (!res.ok) throw new Error(`Server error: ${res.status}`);
  }, []);

  // ── Shared: run learn loop inline in chat ──────────────────
  const startLearnInChat = useCallback((targetMastery = 7, { startConceptId } = {}) => {
    const startNode = startConceptId ? idea$.rawNodesRef.current.find(n => n.id === startConceptId) : null;
    setLearnStream({
      status: 'generating_probe',
      conceptId: startConceptId || undefined,
      conceptLabel: startNode?.data?.label || startNode?.label || undefined,
      detail: 'Starting comprehension loop...',
    });
    setShowChat(true);
    learn$.handleStartLearn(ideaText, targetMastery, (progress) => {
      if (progress.status === 'done' || progress.status === 'complete') {
        setLearnStream(null);
        // Persist completed feedback cards to chat history
        const cards = (progress.completedCards || []).map(c => ({
          type: 'learn_card',
          state: c.state,
        }));
        if (progress.status === 'complete') {
          cards.push({
            type: 'learn_card',
            state: { status: 'complete', masteryMap: progress.masteryMap || {}, totalConcepts: progress.totalConcepts },
          });
        }
        if (cards.length) setPendingChatCards(prev => [...prev, ...cards]);
      } else {
        setLearnStream(progress);
      }
    }, { startConceptId });
  }, [learn$, idea$, ideaText]);

  // ── Idea mode: generate ───────────────────────────────────
  const handleGenerate = useCallback(async () => {
    if (!idea.trim() || idea$.isGenerating || idea$.isRegenerating) return;
    undoStack.pushSnapshot('before-generate');
    idea$.resetCanvas();
    idea$.setIsGenerating(true);
    setShowChat(true);
    setRedirectState('idle');

    // Reset dynamic config for new generation
    dynamicConfigRef.current = null;
    dynamicTypesRef.current = null;
    setDynamicDomain(null);
    setDynamicLegendTypes([]);

    if (idea$.abortRef.current) idea$.abortRef.current.abort();
    const controller = new AbortController();
    idea$.abortRef.current = controller;

    try {
      // ── URL detection & fetching ────────────────────────────
      let fetchedUrlContent = null;
      const urlRegex = /https?:\/\/[^\s"'<>]+/g;
      const urls = idea.trim().match(urlRegex);
      if (urls?.length) {
        setIsFetchingUrl(true);
        try {
          const fetches = urls.map(async (url) => {
            try {
              // Detect if this is a root domain (no meaningful path) → crawl the whole site
              const parsed = new URL(url);
              const path = parsed.pathname.replace(/\/+$/, '');
              const isRootDomain = !path || path === '';

              if (isRootDomain) {
                // Crawl the full site — fetches homepage + key subpages
                const r = await authFetch(`${API_URL}/api/crawl-site`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ url }),
                  signal: controller.signal,
                });
                if (!r.ok) return null;
                const { pages } = await r.json();
                // Return each page as a separate content entry
                return (pages || []).filter(p => p.text).map(p => ({ url: p.url, text: p.text }));
              } else {
                // Specific page — fetch just that URL
                const r = await authFetch(`${API_URL}/api/fetch-url`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ url }),
                  signal: controller.signal,
                });
                if (!r.ok) return null;
                const { text } = await r.json();
                return text ? [{ url, text }] : null;
              }
            } catch { return null; }
          });
          const results = await Promise.all(fetches);
          // Flatten: each URL fetch may return multiple pages (from crawl)
          fetchedUrlContent = results.filter(Boolean).flat();
          if (!fetchedUrlContent.length) fetchedUrlContent = null;
        } finally {
          setIsFetchingUrl(false);
        }
      }

      // Look up matching templates for structural guidance
      const allTemplates = readTemplates();
      const templateGuidance = allTemplates.length > 0
        ? allTemplates.slice(0, 3).map(t => ({ domain: t.domain, idea_summary: t.idea_summary, structure: t.structure }))
        : undefined;

      const genParams = { idea: idea.trim(), mode: displayMode, fetchedUrlContent, templateGuidance, emailThread: emailContext?.formatted || null, claudeCodeContext: claudeCodeContext?.context || null, sessionFileContext: sessionFileContext || null, sessionId: gateway.sessionId || initialSession?.id || null, chaosLevel, constraints: constraints.length ? constraints : undefined, noveltyEngine, evolveEngine, ...(learnPdf && displayMode === 'learn' ? { learnPdf } : {}) };
      const seenTypes = [];
      if (yjs) yjs.setLocalGenerating(true);
      const onNodeData = (nodeData) => {
        // Capture server-minted sessionId so subsequent API calls (teach/probe/
        // telegram/etc.) that rely on gateway.sessionId work on a fresh session.
        if (nodeData._done && nodeData.sessionId) {
          gateway.attachSession(nodeData.sessionId);
          return;
        }
        if (nodeData._meta) {
          const config = buildDynamicConfig(nodeData.types || []);
          dynamicConfigRef.current = config;
          dynamicTypesRef.current = nodeData.types || [];
          idea$.dynamicTypesRef.current = nodeData.types || [];
          setDynamicDomain(nodeData.domainLabel || nodeData.domain || 'Canvas');
          if (yjs) yjs.writeMetaToYjs({ types: nodeData.types, domain: nodeData.domain, idea: idea.trim(), mode: displayMode });
          // Extended _meta: store thinking pattern + resolved framework
          if (nodeData.pattern) {
            setActivePattern(nodeData.pattern);
          }
          if (nodeData.framework) {
            setPatternFramework(nodeData.framework);
          } else if (nodeData.frameworkSkeleton) {
            // Minimal skeleton — could resolve server-side later
            setPatternFramework(nodeData.frameworkSkeleton);
          }
          return;
        }
        const flowNode = buildFlowNode(nodeData);
        if (dynamicConfigRef.current) flowNode.data.dynamicConfig = dynamicConfigRef.current;
        // Provenance metadata
        flowNode.data.generatedBy = 'tree-generation';
        flowNode.data.generatedAt = new Date().toISOString();
        flowNode.data.chaosLevel = chaosLevel;
        if (constraints.length) flowNode.data.appliedConstraints = constraints;
        if (yjs) {
          yjs.addNodeToYjs(flowNode);
          // Yjs observer will update rawNodesRef + applyLayout for all clients
        } else {
          idea$.rawNodesRef.current = upsertNode(idea$.rawNodesRef.current, flowNode);
          idea$.applyLayout(idea$.rawNodesRef.current, []);
        }
        idea$.setNodeCount((idea$.rawNodesRef.current || []).length + 1);
        if (nodeData.type && !seenTypes.includes(nodeData.type)) {
          seenTypes.push(nodeData.type);
          setDynamicLegendTypes([...seenTypes]);
        }
      };

      let result;
      if (gatewayRef.current.connected) {
        result = await streamViaGateway(gatewayRef.current, 'generate', genParams, onNodeData);
      }
      if (!result) {
        // WS not available or send failed — fall back to REST+SSE
        const res = await authFetch(`${API_URL}/api/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(genParams),
          signal: controller.signal,
        });
        await checkUpgradable(res);
        result = await readSSEStream(res, onNodeData);
      }
      if (result.error) idea$.setError(result.error);
      saveVersionAndMemory(idea, idea$.rawNodesRef.current);
    } catch (err) {
      if (err.name !== 'AbortError') idea$.setError(err.message);
    } finally {
      idea$.setIsGenerating(false);
      setIsFetchingUrl(false);
      if (yjs) yjs.setLocalGenerating(false);
      // Auto-collapse deep branches for large trees
      if (!controller.signal.aborted && idea$.rawNodesRef.current.length > 15) {
        idea$.autoCollapseDeep();
      }
      // After generation: advance pipeline or start learn mode
      if (!controller.signal.aborted && idea$.rawNodesRef.current.length > 0) {
        if (initialSession?.nextExperience) {
          // The lifecycle-first experience finishes one clear job at a time.
          // Follow-up stages are explicit mode-specific actions in the header.
        } else if (displayMode === 'learn') {
          startLearnInChat(7);
        } else {
          // Use dynamic pipeline orchestrator
          advancePipelineRef.current?.({ priorStage: 'generate', priorOutcome: { nodeCount: idea$.rawNodesRef.current.length, activePattern } });
        }
        triggerScoring(idea$.rawNodesRef.current, idea.trim());
      }
    }
  }, [idea, idea$, displayMode, saveVersionAndMemory, triggerScoring, yjs, checkUpgradable, emailContext, startLearnInChat, initialSession?.nextExperience]);

  // handleGenerateMulti (3-lens multi-agent generator) was removed —
  // superseded by the 6 heterogeneous DMAD reasoners that fire when
  // 🧬 Novelty is enabled on a regular generate-research run. See
  // features/ai-workflows/novelty-engine-phase-a.feature.

  const handleGenerateResearch = useCallback(async () => {
    if (!idea.trim() || idea$.isGenerating || idea$.isRegenerating) return;
    idea$.resetCanvas();
    idea$.setIsGenerating(true);
    const isLearnMode = displayMode === 'learn';
    if (!isLearnMode) setShowChat(true); // learn mode uses its own bottom-bar chat
    setMultiAgentProgress(isLearnMode ? 'Analyzing content...' : 'Planning research strategy...');
    setRedirectState('idle');

    // Initialize generation flow visualization — learn mode gets different labels
    if (isLearnMode) {
      setGenerationFlow([
        { id: 'research-plan', label: 'Analyzing Content', status: 'active', startedAt: new Date().toISOString() },
        { id: 'research-exec', label: 'Mapping Prerequisites', status: 'pending' },
        { id: 'tree-gen', label: 'Building Concept Tree', status: 'pending', nodeCount: 0 },
        { id: 'got', label: 'Generating Learning Path', status: 'pending' },
      ]);
    } else {
      setGenerationFlow([
        { id: 'research-plan', label: 'Research Planning', status: 'active', startedAt: new Date().toISOString() },
        { id: 'research-exec', label: 'Deep Research', status: 'pending' },
        { id: 'tree-gen', label: 'Generating Tree', status: 'pending', nodeCount: 0 },
        { id: 'got', label: 'Graph of Thoughts', status: 'pending' },
      ]);
    }
    // Initialize agent flow log with root node
    setAgentFlowLog([
      { _agentFlow: true, event: 'stage_start', id: 'root', label: isLearnMode ? 'Learn Engine' : 'Claude (Agent Core)', timestamp: Date.now() },
      { _agentFlow: true, event: 'stage_start', id: 'research-plan', label: isLearnMode ? 'Analyzing Content' : 'Research Planning', parentId: 'root', model: 'gemini:flash', timestamp: Date.now() },
    ]);
    // Phase A: reset novelty state for new generation
    setNoveltyScores({});
    setDmadResults([]);
    setDmadStatus({});
    setNoveltySummary(null);
    setResearchStatus(null);
    setCompetition({});
    setVerifications({});
    // Phase B: reset QD/recombination state
    setQdArchive(null);
    setRecombinations([]);
    setBlendScores({});
    setQdTags({});
    setEvolveGenerations({});
    setEvolveLineage([]);
    setEvolveActiveGen(null);
    setNoveltyFilters({ minNovelty: 0, cellMechanism: null, cellRisk: null, cellAudience: null, cellHorizon: null });
    setNoveltyProgress({ dmadDone: 0, dmadTotal: 6, scoreDone: 0, scoreTotal: 0, tagDone: 0, tagTotal: 0, blendDone: 0, blendTotal: 5, blendScoreDone: 0, blendScoreTotal: 0, activeStage: null });
    setDiscoveryDeckVisible(false);

    dynamicConfigRef.current = null;
    dynamicTypesRef.current = null;
    setDynamicDomain(null);
    setDynamicLegendTypes([]);

    // Initialise pipeline overlay when automation checkboxes are active
    if (autoRefineOnGen || autoPortfolioOnGen || autoPrototypeOnGen) {
      const stages = [
        { id: 'generate', label: 'Generate', status: 'active', detail: 'Research & multi-agent thinking...' },
        { id: 'debate', label: 'Debate', status: 'pending', detail: null },
        ...(autoRefineOnGen ? [{ id: 'refine', label: 'Refine', status: 'pending', detail: null }] : []),
        ...(autoPortfolioOnGen ? [{ id: 'portfolio', label: 'Portfolio', status: 'pending', detail: null }] : []),
        ...(autoPrototypeOnGen ? [{ id: 'prototype', label: 'Prototype', status: 'pending', detail: null }] : []),
      ];
      setPipelineStages(stages);
      setShowChat(true);  // open chat to show pipeline progress
    }

    if (idea$.abortRef.current) idea$.abortRef.current.abort();
    const controller = new AbortController();
    idea$.abortRef.current = controller;

    try {
      // URL detection (same as handleGenerate)
      let fetchedUrlContent = null;
      const urlRegex = /https?:\/\/[^\s"'<>]+/g;
      const urls = idea.trim().match(urlRegex);
      if (urls?.length) {
        setIsFetchingUrl(true);
        try {
          const fetches = urls.map(async (url) => {
            try {
              const parsed = new URL(url);
              const path = parsed.pathname.replace(/\/+$/, '');
              const isRootDomain = !path || path === '';
              if (isRootDomain) {
                const r = await authFetch(`${API_URL}/api/crawl-site`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }), signal: controller.signal });
                if (!r.ok) return null;
                const { pages } = await r.json();
                return (pages || []).filter(p => p.text).map(p => ({ url: p.url, text: p.text }));
              } else {
                const r = await authFetch(`${API_URL}/api/fetch-url`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }), signal: controller.signal });
                if (!r.ok) return null;
                const { text } = await r.json();
                return text ? [{ url, text }] : null;
              }
            } catch { return null; }
          });
          const results = await Promise.all(fetches);
          fetchedUrlContent = results.filter(Boolean).flat();
          if (!fetchedUrlContent.length) fetchedUrlContent = null;
        } finally {
          setIsFetchingUrl(false);
        }
      }

      const allTemplates = readTemplates();
      const templateGuidance = allTemplates.length > 0
        ? allTemplates.slice(0, 3).map(t => ({ domain: t.domain, idea_summary: t.idea_summary, structure: t.structure }))
        : undefined;

      const genParams = { idea: idea.trim(), mode: displayMode, fetchedUrlContent, templateGuidance, emailThread: emailContext?.formatted || null, claudeCodeContext: claudeCodeContext?.context || null, sessionFileContext: sessionFileContext || null, sessionId: gateway.sessionId || initialSession?.id || null, chaosLevel, constraints: constraints.length ? constraints : undefined, noveltyEngine, evolveEngine, ...(learnPdf && displayMode === 'learn' ? { learnPdf } : {}) };
      const seenTypes = [];
      let genNodeCount = 0;
      if (yjs) yjs.setLocalGenerating(true);
      const onNodeData = (nodeData) => {
        // Capture server-minted sessionId on fresh sessions (same rationale as
        // simple /api/generate path above).
        if (nodeData._done && nodeData.sessionId) {
          gateway.attachSession(nodeData.sessionId);
          return;
        }
        if (nodeData._agentFlow) {
          setAgentFlowLog(prev => [...prev, { ...nodeData, timestamp: Date.now() }]);
          // Phase B: side effects on specific stage_done events (do BEFORE returning)
          if (nodeData.id === 'novelty' && nodeData.event === 'stage_done') {
            setNoveltySummary({ avgNovelty: nodeData.avgNovelty });
          }
          if (nodeData.id === 'qd' && nodeData.event === 'stage_done') {
            setTimeout(() => setDiscoveryDeckVisible(true), 800);
            // Phase B redesign: re-synthesize the output doc with novelty engine data attached.
            // The first synthesis fired when generation finished but had no engine data yet.
            setTimeout(() => {
              if (idea$.rawNodesRef.current?.length > 0) {
                console.log('[novelty] re-synthesizing output doc with engine data');
                updateWorkflowOutput(idea$.rawNodesRef.current, ideaText);
              }
            }, 1200);
          }
          return;
        }
        if (nodeData._progress) {
          setMultiAgentProgress(nodeData.stage);
          // Also log to agent flow for the FLOW view
          setAgentFlowLog(prev => [...prev, { _agentFlow: true, event: 'progress', id: `prog_${Date.now()}`, label: nodeData.stage, timestamp: Date.now() }]);
          // Map progress to generation flow stages
          const stage = nodeData.stage || '';
          setGenerationFlow(prev => {
            if (!prev) return prev;
            const markDone = (id) => prev.map(s => s.id === id ? { ...s, status: 'done', completedAt: new Date().toISOString() } : s);
            const markActive = (id, detail) => prev.map(s => s.id === id ? { ...s, status: 'active', detail, startedAt: s.startedAt || new Date().toISOString() } : s);
            if (stage.includes('Researching market') || stage.includes('Research agent')) {
              setAgentFlowLog(p => [...p, { _agentFlow: true, event: 'stage_done', id: 'research-plan', timestamp: Date.now() }, { _agentFlow: true, event: 'stage_start', id: 'research-exec', label: 'Deep Research', parentId: 'root', model: 'gemini:flash', parallel: true, timestamp: Date.now() }]);
              return markDone('research-plan').map(s => s.id === 'research-exec' ? { ...s, status: 'active', detail: stage, startedAt: s.startedAt || new Date().toISOString() } : s);
            }
            if (stage.includes('Synthesizing') || stage.includes('thinking tree')) {
              setAgentFlowLog(p => [...p, { _agentFlow: true, event: 'stage_done', id: 'research-exec', timestamp: Date.now() }, { _agentFlow: true, event: 'stage_start', id: 'tree-gen', label: 'Tree Generation', parentId: 'root', model: 'claude:opus', timestamp: Date.now() }]);
              return markDone('research-exec').map(s => s.id === 'tree-gen' ? { ...s, status: 'active', startedAt: s.startedAt || new Date().toISOString() } : s);
            }
            if (stage.includes('Graph of Thoughts') || stage.includes('convergence')) {
              setAgentFlowLog(p => [...p, { _agentFlow: true, event: 'stage_done', id: 'tree-gen', nodeCount: genNodeCount, timestamp: Date.now() }, { _agentFlow: true, event: 'stage_start', id: 'got', label: 'Graph of Thoughts', parentId: 'root', model: 'claude:sonnet', timestamp: Date.now() }]);
              return markDone('tree-gen').map(s => s.id === 'got' ? { ...s, status: 'active', startedAt: s.startedAt || new Date().toISOString() } : s);
            }
            if (stage.includes('Refining synthesis'))
              return markActive('got', 'Refining synthesis...');
            return prev.map(s => s.status === 'active' ? { ...s, detail: stage } : s);
          });
          return;
        }
        if (nodeData._meta) {
          const config = buildDynamicConfig(nodeData.types || []);
          dynamicConfigRef.current = config;
          dynamicTypesRef.current = nodeData.types || [];
          idea$.dynamicTypesRef.current = nodeData.types || [];
          setDynamicDomain(nodeData.domain || 'Canvas');
          setMultiAgentProgress(null);
          if (yjs) yjs.writeMetaToYjs({ types: nodeData.types, domain: nodeData.domain, idea: idea.trim(), mode: displayMode });
          return;
        }
        // Research status (fires once after the 3 Gemini agents finish)
        if (nodeData._researchStatus) {
          setResearchStatus({
            succeededCount: nodeData.succeededCount,
            totalCount: nodeData.totalCount,
            briefBytes: nodeData.briefBytes,
            gemini: nodeData.gemini,
          });
          return;
        }
        // Competition scan (Tier 2) — fires per scanned candidate
        if (nodeData._competitionStatus) {
          const { candidateId, ...rest } = nodeData;
          setCompetition(prev => ({ ...prev, [candidateId]: rest }));
          return;
        }
        if (nodeData._competition_error) {
          console.warn('[competition]', nodeData.message);
          return;
        }
        // ── Phase A: Novelty engine SSE events ──────────────────
        if (nodeData._novelty) {
          const { nodeId, score, closestExisting, whyNovel } = nodeData;
          setNoveltyScores(prev => ({ ...prev, [nodeId]: { score, closestExisting, whyNovel } }));
          setNoveltyProgress(p => ({ ...p, scoreDone: p.scoreDone + 1, scoreTotal: Math.max(p.scoreTotal, p.scoreDone + 1), activeStage: 'score' }));
          return;
        }
        // Per-reasoner live status — fires when a reasoner enters its API
        // call, and again every 15s while it's still running. UI shows
        // "Means-Ends Analyst — 45s elapsed" instead of a silent spinner.
        if (nodeData._dmad_status) {
          setDmadStatus(prev => ({
            ...prev,
            [nodeData.id]: {
              label: nodeData.label,
              model: nodeData.model,
              status: nodeData.status,
              elapsedMs: nodeData.elapsedMs || 0,
              timeoutMs: nodeData.timeoutMs,
              startedAt: prev[nodeData.id]?.startedAt || Date.now(),
            },
          }));
          return;
        }
        if (nodeData._dmad) {
          setDmadResults(prev => [...prev, nodeData]);
          // Mark this reasoner as done in the live status map.
          setDmadStatus(prev => ({
            ...prev,
            [nodeData.id]: { ...(prev[nodeData.id] || {}), label: nodeData.label, model: nodeData.model, status: nodeData.error ? 'error' : 'done', elapsedMs: nodeData.ms, error: nodeData.error },
          }));
          setNoveltyProgress(p => ({ ...p, dmadDone: p.dmadDone + 1, activeStage: p.dmadDone + 1 < p.dmadTotal ? 'dmad' : p.activeStage }));
          // Auto-graft each reasoner's transformations onto the tree as
          // `experimental` nodes. Previously the LENSES tab required the
          // user to click "+ add to tree" per transformation; now they
          // land automatically with provenance "accepted from <lens>" so
          // the user sees the tree grow as reasoners report. Skip if
          // reasoner errored.
          if (!nodeData.error && Array.isArray(nodeData.output?.transformations)) {
            for (const t of nodeData.output.transformations) {
              if (t?.what) handleAcceptTransformation(t, nodeData.label);
            }
          }
          return;
        }
        if (nodeData._novelty_error) {
          console.warn('[novelty engine error]', nodeData.message);
          return;
        }
        // (stage_done side effects moved above to fire before _agentFlow's early return)
        // ── Phase B: QD archive + recombination events ───────
        if (nodeData._qdArchive) {
          setQdArchive({ stats: nodeData.stats, dimensions: nodeData.dimensions, cells: nodeData.cells });
          setNoveltyProgress(p => ({ ...p, activeStage: 'blend' }));
          return;
        }
        if (nodeData._recombination) {
          setRecombinations(prev => [...prev, nodeData]);
          setNoveltyProgress(p => ({ ...p, blendDone: p.blendDone + 1, activeStage: 'blend' }));
          return;
        }
        if (nodeData._blendScore) {
          setBlendScores(prev => ({ ...prev, [nodeData.nodeId]: nodeData }));
          setNoveltyProgress(p => ({ ...p, blendScoreDone: p.blendScoreDone + 1, blendScoreTotal: Math.max(p.blendScoreTotal, p.blendScoreDone + 1), activeStage: 'blend_score' }));
          return;
        }
        if (nodeData._qdTag) {
          const { nodeId, mechanism, risk, audience, horizon } = nodeData;
          setQdTags(prev => ({ ...prev, [nodeId]: { mechanism, risk, audience, horizon } }));
          setNoveltyProgress(p => ({ ...p, tagDone: p.tagDone + 1, tagTotal: Math.max(p.tagTotal, p.tagDone + 1), activeStage: 'tag' }));
          return;
        }
        if (nodeData._qd_error) {
          console.warn('[qd error]', nodeData.message);
          return;
        }
        // ── Phase C: Evolutionary loop SSE events ─────────
        if (nodeData._evolve_init) {
          setEvolveActiveGen(0);
          return;
        }
        if (nodeData._evolve_candidate) {
          const { genIdx, candidate } = nodeData;
          // Server payload omits genIdx inside `candidate`; bake it in so
          // the champion banner + detail panel can render the correct gen.
          const candWithGen = { ...candidate, genIdx };
          setEvolveGenerations(prev => {
            const existing = prev[genIdx] || [];
            // Replace if id already exists (re-eval), else append
            const filtered = existing.filter(c => c.id !== candWithGen.id);
            return { ...prev, [genIdx]: [...filtered, candWithGen] };
          });
          return;
        }
        if (nodeData._evolve_gen) {
          setEvolveActiveGen(nodeData.genIdx);
          return;
        }
        if (nodeData._evolve_lineage) {
          setEvolveLineage(nodeData.lineage || []);
          return;
        }
        if (nodeData._evolve_evaluator) {
          // Per-evaluator results streamed live; we already capture aggregated
          // scores via _evolve_candidate so no-op here unless we want a sub-progress bar.
          return;
        }
        if (nodeData._evolve_error) {
          console.warn('[evolve error]', nodeData.message);
          return;
        }
        // Regular node — update flow card count
        genNodeCount++;
        setGenerationFlow(prev => prev?.map(s => s.id === 'tree-gen' && s.status === 'active' ? { ...s, nodeCount: genNodeCount } : s));
        const flowNode = buildFlowNode(nodeData);
        if (dynamicConfigRef.current) flowNode.data.dynamicConfig = dynamicConfigRef.current;
        if (yjs) {
          yjs.addNodeToYjs(flowNode);
        } else {
          idea$.rawNodesRef.current = upsertNode(idea$.rawNodesRef.current, flowNode);
          idea$.applyLayout(idea$.rawNodesRef.current, []);
        }
        idea$.setNodeCount((idea$.rawNodesRef.current || []).length + 1);
        if (nodeData.type && !seenTypes.includes(nodeData.type)) {
          seenTypes.push(nodeData.type);
          setDynamicLegendTypes([...seenTypes]);
        }
      };

      let result;
      if (gatewayRef.current.connected) {
        result = await streamViaGateway(gatewayRef.current, 'generate-research', genParams, onNodeData);
      }
      if (!result) {
        const res = await authFetch(`${API_URL}/api/generate-research`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(genParams),
          signal: controller.signal,
        });
        await checkUpgradable(res);
        result = await readSSEStream(res, onNodeData);
      }
      if (result.error) idea$.setError(result.error);
      saveVersionAndMemory(idea, idea$.rawNodesRef.current);
    } catch (err) {
      if (err.name !== 'AbortError') idea$.setError(err.message);
    } finally {
      idea$.setIsGenerating(false);
      setMultiAgentProgress(null);
      setIsFetchingUrl(false);
      // Mark all generation flow stages as done
      setGenerationFlow(prev => prev?.map(s => ({ ...s, status: s.status === 'pending' ? 'pending' : 'done', completedAt: s.completedAt || new Date().toISOString() })));
      if (yjs) yjs.setLocalGenerating(false);
      if (!controller.signal.aborted && idea$.rawNodesRef.current.length > 0) {
        if (displayMode === 'learn') {
          startLearnInChat(7);
        } else {
          // Use dynamic pipeline orchestrator
          advancePipelineRef.current?.({ priorStage: 'generate', priorOutcome: { nodeCount: idea$.rawNodesRef.current.length } });
        }
        triggerScoring(idea$.rawNodesRef.current, idea.trim());
      }
    }
  }, [idea, idea$, displayMode, saveVersionAndMemory, triggerScoring, yjs, checkUpgradable, emailContext, startLearnInChat]);

  const handleStop = useCallback(() => {
    active.handleStop();
    // Also stop any in-flight WebSocket stream
    if (activeWsReqId && gatewayRef.current?.stop) {
      gatewayRef.current.stop(activeWsReqId);
      activeWsReqId = null;
    }
    // Stop all active streams
    if (refine$?.abortRef?.current) refine$.abortRef.current.abort();
    if (proto$?.handleStopBuild) proto$.handleStopBuild();
    setRedirectState('idle');
    setIsCritiquing(false);
    setMultiAgentProgress(null);
    setPipelineCheckpoint(null);
    setPipelineStages(null);
    setDebateStream(null);
    setRefineStream(null);
    setPortfolioStream(null);
    setPrototypeStream(null);
  }, [active, refine$, proto$]);

  // ── Pipeline Orchestrator: Dynamic Pattern Selection ─────
  const advancePipeline = useCallback(async ({ priorStage, priorOutcome }) => {
    // Determine which stages are still available based on pipeline order
    const stageOrder = ['generate', 'debate', 'refine', 'portfolio', 'prototype'];
    const completedStages = new Set();
    // Mark prior stages as completed
    const priorIdx = stageOrder.indexOf(priorStage);
    if (priorIdx >= 0) {
      for (let i = 0; i <= priorIdx; i++) completedStages.add(stageOrder[i]);
    }
    // If prior was a pattern, treat it as completing the debate slot
    if (priorStage === 'pattern') completedStages.add('generate').add('debate');

    // Only include stages the user actually selected (debate is always available).
    // refine + portfolio toggles removed — superseded by novelty engine.
    const enabledStages = new Set(['debate']);
    if (autoPrototypeOnGen) enabledStages.add('prototype');
    const availableStages = stageOrder.filter(s => !completedStages.has(s) && s !== 'generate' && enabledStages.has(s));

    // If no stages remain, pipeline is done
    if (availableStages.length === 0) {
      setPipelineStages(prev => prev?.map(s => ({ ...s, status: s.status === 'pending' ? 'done' : s.status })));
      return;
    }

    // Ask the server for optimal next stage
    try {
      const nodes = (active.rawNodesRef.current || []).map(n => ({
        id: n.id, type: n.data?.type, label: n.data?.label, reasoning: n.data?.reasoning?.slice(0, 100),
      }));
      const res = await authFetch(`${API_URL}/api/pattern/recommend-next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idea: ideaText, mode: displayMode, nodes,
          priorStage, priorOutcome,
          pipelinePosition: priorStage === 'generate' ? 0 : priorStage === 'debate' ? 1 : 2,
          availableStages,
        }),
      });
      const recommendation = await res.json();

      // Idea mode: critique should always run automatically post-generation.
      // The deep research brief (now including Reddit/HN community voice) is
      // the strongest signal we have; running critique on top of it without
      // user friction is the default behaviour the user expects.
      const ideaModeAutoCritique = displayMode === 'idea' && recommendation.stageType === 'debate';

      if (autonomousMode || ideaModeAutoCritique) {
        // Auto-dispatch without checkpoint
        dispatchPipelineStage(recommendation.stageType, recommendation.recommended?.id || recommendation.recommended, priorStage);
      } else {
        // Show checkpoint card for user decision (live marker handles rendering)
        setPipelineCheckpoint(recommendation);
        setShowChat(true);
      }
    } catch (err) {
      // Fallback: dispatch the first available stage
      const fallback = availableStages[0];
      const ideaModeAutoCritiqueFallback = displayMode === 'idea' && fallback === 'debate';
      if (autonomousMode || ideaModeAutoCritiqueFallback) {
        dispatchPipelineStage(fallback, null, priorStage);
      } else {
        setPipelineCheckpoint({
          recommended: fallback, stageType: fallback,
          alternatives: [], reasoning: 'Default sequence', shouldSkip: false,
        });
        setShowChat(true);
      }
    }
  }, [active, ideaText, displayMode, autoRefineOnGen, autoPortfolioOnGen, autoPrototypeOnGen, autonomousMode]);
  advancePipelineRef.current = advancePipeline;

  // Dispatch a pipeline stage (shared by advancePipeline and handlePipelineCheckpointDecision)
  const dispatchPipelineStage = useCallback((stageType, patternId, priorStage) => {
    setPipelineCheckpoint(null);

    // Update pipeline overlay
    setPipelineStages(prev => prev?.map(s =>
      s.id === priorStage ? { ...s, status: 'done' } :
      s.id === stageType || s.id === 'pattern' ? { ...s, status: 'active', detail: `Running ${stageType}...` } : s
    ));

    const pipelineCallback = (progress) => {
      if (progress.status === 'complete' || progress.done) {
        // Stage finished — advance to next
        advancePipeline({ priorStage: stageType, priorOutcome: progress });
      }
    };

    // Map pattern IDs that correspond to built-in rich UIs
    const PATTERN_TO_BUILTIN = {
      'adversarial': 'debate', 'adversarial-critique': 'debate', 'expert-committee': 'debate',
      'progressive-refine': 'refine', 'progressive-refinement': 'refine',
      'portfolio-explore': 'portfolio', 'portfolio-exploration': 'portfolio',
    };
    const builtins = ['debate', 'refine', 'portfolio', 'prototype'];
    const resolvedStage = PATTERN_TO_BUILTIN[patternId] || (builtins.includes(patternId) ? patternId : builtins.includes(stageType) ? stageType : null);

    if (resolvedStage === 'debate') {
      startDebateInChatRef.current?.(pipelineCallback);
    } else if (resolvedStage === 'refine') {
      startRefineInChatRef.current?.(3, pipelineCallback);
    } else if (resolvedStage === 'portfolio') {
      startPortfolioInChatRef.current?.(pipelineCallback);
    } else if (resolvedStage === 'prototype') {
      startPrototypeBuildRef.current?.();
    } else if (patternId) {
      // Execute a custom thinking pattern (no rich UI — generic executor)
      patternExec$.execute(patternId, ideaText, active.rawNodesRef.current, displayMode, { domain: dynamicDomain }, pipelineCallback);
      setShowChat(true);
    } else {
      // Fallback: start debate with rich UI
      startDebateInChatRef.current?.(pipelineCallback);
    }
  }, [patternExec$, ideaText, active, displayMode, dynamicDomain, advancePipeline]);

  const handlePipelineCheckpointDecision = useCallback((action, payload) => {
    setPipelineCheckpoint(null);
    if (action === 'skip') {
      // Skip this stage, advance to next
      const skippedStage = pipelineCheckpoint?.stageType || 'unknown';
      setPipelineStages(prev => prev?.map(s => s.id === skippedStage ? { ...s, status: 'done', detail: 'Skipped' } : s));
      advancePipeline({ priorStage: skippedStage, priorOutcome: { skipped: true } });
    } else if (action === 'run') {
      dispatchPipelineStage(payload?.stageType || pipelineCheckpoint?.stageType, payload?.patternId, pipelineCheckpoint?.stageType);
    }
  }, [pipelineCheckpoint, advancePipeline, dispatchPipelineStage]);

  // ── Autonomous Evolution ─────────────────────────────────
  const handleStartEvolve = useCallback(async () => {
    if (evolving || !gateway.sessionId) return;
    setEvolving(true);
    try {
      const res = await authFetch(`${API_URL}/api/evolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: gateway.sessionId,
          mode: displayMode || 'idea',
        }),
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const task = await res.json();
      setEvolutionTask(task);
      // Push evolution card to chat
      setPendingChatCards(prev => [...prev, {
        type: 'evolution',
        label: '⧬ Evolution Plan Started',
        detail: `5-step plan: refine → debate → experiment → refine → export. Runs daily at 9 AM.`,
        taskId: task.id,
        plan: task.config?.plan,
        evolutionHistory: task.config?.evolutionHistory || [],
      }]);
      setShowChat(true);
    } catch (err) {
      console.error('Evolve error:', err);
    } finally {
      setEvolving(false);
    }
  }, [evolving, gateway.sessionId, displayMode]);

  // ── Forest decomposition ────────────────────────────────
  const handleForestDecompose = useCallback(async () => {
    if (!idea.trim() || !onOpenForest || isDecomposing) return;
    setIsDecomposing(true);
    setShowChat(true);
    setPendingChatCards(prev => [...prev, {
      label: '◈ Decomposing into multi-canvas forest...',
      detail: `Analyzing "${idea.trim().slice(0, 50)}..." and planning interconnected canvases`,
      buttons: [],
    }]);
    try {
      const res = await authFetch(`${API_URL}/api/forest/decompose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idea: idea.trim(), mode: displayMode }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const payload = line.slice(6).trim();
            if (payload === '[DONE]') break;
            try {
              const event = JSON.parse(payload);
              if (event._forestProgress) {
                // Show canvas plan as it's discovered
                setPendingChatCards(prev => [...prev, {
                  label: `◈ ${event.stage || 'Planning canvases...'}`,
                  detail: event.canvasTitles ? `Canvases: ${event.canvasTitles.join(', ')}` : null,
                  buttons: [],
                }]);
              }
              if (event._forestResult) {
                const forestRes = await authFetch(`${API_URL}/api/forests/${event.forestId}`);
                if (forestRes.ok) {
                  const forest = await forestRes.json();
                  const canvasTitles = (forest.plan?.canvases || []).map(c => c.title).join(', ');
                  setPendingChatCards(prev => [...prev, {
                    label: `◈ Forest ready — ${forest.plan?.canvases?.length || 0} canvases`,
                    detail: canvasTitles,
                    buttons: [{ label: 'Open Forest', actionType: 'openForest', forest }],
                  }]);
                  onOpenForest(forest);
                }
              }
            } catch {}
          }
        }
      }
    } catch (err) {
      console.error('Forest decompose error:', err);
      setPendingChatCards(prev => [...prev, { label: '✗ Forest decomposition failed', detail: err.message, buttons: [] }]);
    } finally {
      setIsDecomposing(false);
    }
  }, [idea, displayMode, onOpenForest, isDecomposing]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (initialSession?.nextExperience) handleGenerate();
      else handleGenerateResearch();
    }
  }, [handleGenerateResearch, handleGenerate, initialSession?.nextExperience]);

  // ── Textarea auto-resize ────────────────────────────────
  const autoResize = useCallback(() => {
    const el = ideaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 150) + 'px';
  }, []);

  const handleIdeaChange = useCallback((e) => {
    const val = e.target.value;
    setIdea(val);
    setAttachedFile(null);              // clear file badge on manual edit
    setTimeout(autoResize, 0);
    // Auto-detect mode from input text (debounced 400 ms).
    // Detection always runs; manualMode takes precedence in displayMode expression.
    if (detectTimerRef.current) clearTimeout(detectTimerRef.current);
    detectTimerRef.current = setTimeout(() => {
      setDetectedMode(val.trim().length >= 8 ? detectMode(val) : null);
    }, 400);
  }, [autoResize]);

  // ── File upload ─────────────────────────────────────────
  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = file.name;
    const ext = name.split('.').pop().toLowerCase();

    // In learn mode, accept PDFs for paper-aware learning
    if (ext === 'pdf' && displayMode === 'learn') {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64 = ev.target.result?.split(',')[1] || '';
        setLearnPdf(base64);
        learn$.setPaperTitle(file.name.replace(/\.pdf$/i, '')); // mark as paper-based learning
        setIdea(file.name.replace(/\.pdf$/i, '')); // use filename as topic
        setAttachedFile({ name: file.name, size: file.size, isPdf: true });
        setTimeout(autoResize, 0);
      };
      reader.readAsDataURL(file);
      e.target.value = '';
      return;
    }

    if (!['txt', 'md', 'text', 'csv', 'json', 'html', 'rtf'].includes(ext)) {
      alert('Please upload a plain-text file (.txt, .md, .csv, .json). PDF and Word are not supported yet.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const content = ev.target.result || '';
      setIdea(content.slice(0, 50000));   // generous limit
      setAttachedFile({ name: file.name, size: file.size });
      setTimeout(autoResize, 0);
    };
    reader.readAsText(file);
    e.target.value = '';                  // allow re-uploading same file
  }, [autoResize, displayMode]);

  // ── Redirect / steering ───────────────────────────────────
  const handleRedirect = useCallback(() => {
    if (idea$.abortRef.current) idea$.abortRef.current.abort();
    idea$.setIsGenerating(false);
    setRedirectState('prompting');
    setSteeringText('');
  }, [idea$]);

  const handleCancelRedirect = useCallback(() => {
    setRedirectState('idle');
    setSteeringText('');
  }, []);

  // ── Mode selection ────────────────────────────────────────
  // Clicking the already-active manual mode releases it back to auto-detect
  const handleModeSelect = useCallback((modeId) => {
    setManualMode(prev => prev === modeId ? null : modeId);
  }, []);

  const handleNextModeSelect = useCallback((modeId, goal = '') => {
    if (modeId === displayMode && !goal) return;
    if (active.nodeCount > 0) {
      const shouldStart = window.confirm('Start a new mission in this mode? Your current mission will stay unchanged.');
      if (!shouldStart) return;
      window.dispatchEvent(new CustomEvent('thoughtclaw:start-mode', { detail: { mode: modeId, goal } }));
      return;
    }
    setManualMode(modeId);
    if (goal) setIdea(goal);
    setTimeout(() => ideaRef.current?.focus(), 0);
  }, [active.nodeCount, displayMode]);

  const handleSteeringSubmit = useCallback(async () => {
    if (!steeringText.trim()) return;
    setRedirectState('resuming');
    idea$.setIsGenerating(true);

    const controller = new AbortController();
    idea$.abortRef.current = controller;

    const existingNodes = idea$.rawNodesRef.current.map((n) => ({
      id: n.id, type: n.data.type, label: n.data.label,
      reasoning: n.data.reasoning, parentId: n.data.parentId,
    }));

    try {
      const res = await authFetch(`${API_URL}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idea: idea.trim(), steeringInstruction: steeringText.trim(), existingNodes, emailThread: emailContext?.formatted || null, sessionId: gateway.sessionId || initialSession?.id || null, chaosLevel }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const result = await readSSEStream(res, (nodeData) => {
        const flowNode = buildFlowNode(nodeData);
        idea$.rawNodesRef.current = upsertNode(idea$.rawNodesRef.current, flowNode);
        idea$.applyLayout(idea$.rawNodesRef.current, idea$.drillStackRef.current);
        idea$.setNodeCount(idea$.rawNodesRef.current.length);
      });
      if (result.error) idea$.setError(result.error);
      saveVersionAndMemory(idea, idea$.rawNodesRef.current);
    } catch (err) {
      if (err.name !== 'AbortError') idea$.setError(err.message);
    } finally {
      idea$.setIsGenerating(false);
      setRedirectState('idle');
    }
  }, [idea, steeringText, idea$, saveVersionAndMemory, emailContext]);

  const handleSteeringKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleSteeringSubmit();
    if (e.key === 'Escape') handleCancelRedirect();
  }, [handleSteeringSubmit, handleCancelRedirect]);

  // ── Codebase mode ─────────────────────────────────────────
  const handleAnalyzeCodebase = useCallback(async (payload) => {
    if (!payload || cb$.isGenerating) return;
    cb$.resetCanvas();
    cb$.setIsGenerating(true);
    setShowChat(true);
    setMultiAgentProgress('Analyzing codebase...');

    if (cb$.abortRef.current) cb$.abortRef.current.abort();
    const controller = new AbortController();
    cb$.abortRef.current = controller;

    // Route to GitHub endpoint if repoUrl is present, else local codebase
    const endpoint = payload.repoUrl ? '/api/analyze-github' : '/api/analyze-codebase';

    try {
      const res = await authFetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const result = await readSSEStream(res, (nodeData) => {
        if (nodeData._progress) {
          setMultiAgentProgress(nodeData.stage);
          return;
        }
        if (nodeData._meta) {
          const config = buildDynamicConfig(nodeData.types || []);
          cb$.dynamicTypesRef.current = nodeData.types || [];
          return;
        }
        const flowNode = buildFlowNode(nodeData);
        cb$.rawNodesRef.current = upsertNode(cb$.rawNodesRef.current, flowNode);
        cb$.applyLayout(cb$.rawNodesRef.current, cb$.drillStackRef.current);
        cb$.setNodeCount(cb$.rawNodesRef.current.length);
      });
      if (result.error) cb$.setError(result.error);
    } catch (err) {
      if (err.name !== 'AbortError') cb$.setError(err.message);
    } finally {
      cb$.setIsGenerating(false);
      setMultiAgentProgress(null);
    }
  }, [cb$]);

  const handleAnalysisReady = useCallback((payload) => {
    setCbFolderName(payload.folderName || 'project');
    handleAnalyzeCodebase(payload);
  }, [handleAnalyzeCodebase]);

  const handleNewCbAnalysis = useCallback(() => {
    cb$.resetCanvas();
    setCbFolderName('');
  }, [cb$]);

  // ── Resume mode: analyse JD + PDF ─────────────────────────
  const handleResumeAnalyze = useCallback(async ({ jdText, pdfBase64, jdUrl }) => {
    if (idea$.isGenerating) return;
    const label = jdUrl || (jdText ? jdText.slice(0, 60) + '…' : 'Resume Analysis');
    setResumeJobLabel(label);
    setResumePdf(pdfBase64 || null);  // lift PDF so changes modal can use it later
    setResumeChanges(null);           // clear any prior changes
    idea$.resetCanvas();
    idea$.setIsGenerating(true);


    if (idea$.abortRef.current) idea$.abortRef.current.abort();
    const controller = new AbortController();
    idea$.abortRef.current = controller;

    try {
      const res = await authFetch(`${API_URL}/api/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idea: jdText || jdUrl || '',
          mode: 'resume',
          jdText: jdText || '',
          resumePdf: pdfBase64 || null,
          sessionId: gateway.sessionId || initialSession?.id || null,
        }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const result = await readSSEStream(res, (nodeData) => {
        const flowNode = buildFlowNode(nodeData);
        idea$.rawNodesRef.current = upsertNode(idea$.rawNodesRef.current, flowNode);
        idea$.applyLayout(idea$.rawNodesRef.current, []);
        idea$.setNodeCount(idea$.rawNodesRef.current.length);
      });
      if (result.error) idea$.setError(result.error);
      saveVersionAndMemory(label, idea$.rawNodesRef.current);
    } catch (err) {
      if (err.name !== 'AbortError') idea$.setError(err.message);
    } finally {
      idea$.setIsGenerating(false);
      if (!controller.signal.aborted && idea$.rawNodesRef.current.length > 0) {
        if (initialSession?.nextExperience) {
          // Keep the redesigned experience to one explicit job. Career's
          // critique is available through "Review the strategy" instead of
          // silently mutating the first evidence-safe deliverable.
        } else if (displayMode === 'learn') {
          startLearnInChat(7);
        } else {
          startDebateInChatRef.current?.();
        }
      }
    }
  }, [idea$, displayMode, saveVersionAndMemory, startLearnInChat, initialSession?.nextExperience]);

  const handleNewResumeAnalysis = useCallback(() => {
    idea$.resetCanvas();
    setResumeJobLabel('');
    setResumePdf(null);
    setResumeChanges(null);
    setShowResumeChanges(false);
  }, [idea$]);

  // ── Resume: generate change manifest after debate ─────────
  const handleApplyToResume = useCallback(async () => {
    setResumeChanges(null);
    setResumeChangesError(null);
    setIsGeneratingChanges(true);
    setShowResumeChanges(true);

    try {
      const res = await authFetch(`${API_URL}/api/resume/changes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resumePdf: resumePdf || null,
          nodes: idea$.rawNodesRef.current.map(n => ({
            id: n.id,
            type: n.data?.type || n.type,
            label: n.data?.label || n.label,
            reasoning: n.data?.reasoning || n.reasoning,
          })),
          debateHistory: debateRoundsRef.current,
          idea: resumeJobLabel,
        }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      setResumeChanges(data);
    } catch (err) {
      console.error('Resume changes error:', err);
      setResumeChangesError(err.message);
    } finally {
      setIsGeneratingChanges(false);
    }
  }, [resumePdf, idea$, debateRoundsRef, resumeJobLabel]);

  // ── Auto-Fractal handler ────────────────────────────────
  const handleStartAutoFractal = useCallback(async () => {
    setAutoFractalRunning(true);
    setAutoFractalProgress(null);
    await active.handleAutoFractal(ideaText, autoFractalRounds, (progress) => {
      setAutoFractalProgress(progress);
      if (progress.status === 'done') {
        setAutoFractalRunning(false);
      }
    });
    setAutoFractalRunning(false);
  }, [active, autoFractalRounds, ideaText]);

  const handleStopAutoFractal = useCallback(() => {
    active.handleStopAutoFractal();
    setAutoFractalRunning(false);
  }, [active]);

  // ── Auto-Refine handlers ──────────────────────────────────
  const handleStartRefine = useCallback(async (rounds = 3, onProgress) => {
    await refine$.handleStartRefine(ideaText, displayMode, rounds, onProgress);
  }, [refine$, ideaText, displayMode]);

  const handleStopRefine = useCallback(() => {
    refine$.handleStopRefine();
    setRefineStream(null);
  }, [refine$]);

  const handleGoDeeper = useCallback(async (additionalRounds = 2, onProgress) => {
    await refine$.handleGoDeeper(ideaText, displayMode, additionalRounds, onProgress);
  }, [refine$, ideaText, displayMode]);

  // ── Shared: run refine inline in chat ────────────────────
  const startRefineInChat = useCallback((rounds = 3, pipelineCallback) => {
    setRefineStream({ status: 'critiquing', round: 1, maxRounds: rounds });
    setShowChat(true);
    const refineHistory = [];
    handleStartRefine(rounds, (progress) => {
      // Forward to pipeline overlay if provided
      pipelineCallback?.(progress);
      if (progress.status === 'round_complete') {
        refineHistory.push({
          round: progress.round, oldScore: progress.oldScore, newScore: progress.newScore,
          newNodeCount: progress.newNodeCount, summary: progress.summary,
        });
      }
      if (progress.status === 'done' || progress.status === 'complete') {
        setRefineStream(null);
        setPendingChatCards(prev => [...prev, { type: 'refine_card', state: { status: 'done', history: refineHistory } }]);
      } else {
        setRefineStream({ ...progress, history: refineHistory });
      }
    });
  }, [handleStartRefine]);
  startRefineInChatRef.current = startRefineInChat;

  // ── Shared: run prototype build inline in chat ────────────
  const startPrototypeBuild = useCallback(() => {
    setPrototypeStream({ status: 'planning', stage: 'Analyzing tree...', screensTotal: 0, screensComplete: 0, screenNames: [] });
    setShowChat(true);

    proto$.handleBuildPrototype(active.rawNodesRef.current, ideaText, displayMode, (event) => {
      if (event._progress) {
        let status = 'planning';
        if (event.plan) status = 'generating';
        if (event.stage?.includes('Wiring')) status = 'wiring';
        if (event.stage?.includes('Polish')) status = 'polishing';
        setPrototypeStream(prev => ({
          ...prev,
          status,
          stage: event.stage,
          plan: event.plan || prev?.plan,
          screensTotal: event.plan?.screens?.length || prev?.screensTotal,
        }));
        // Update pipeline stages if running in pipeline
        setPipelineStages(prev => prev?.map(s =>
          s.id === 'prototype' ? { ...s, status: 'active', detail: event.stage } : s
        ));
      }
      if (event._screen) {
        setPrototypeStream(prev => ({
          ...prev,
          screensComplete: (prev?.screensComplete || 0) + 1,
          screenNames: [...(prev?.screenNames || []), event.name],
        }));
      }
      if (event._result) {
        setPrototypeStream(prev => ({ ...prev, status: 'done' }));
        setPipelineStages(prev => prev?.map(s =>
          s.id === 'prototype' ? { ...s, status: 'done', detail: 'Prototype built' } : s
        ));
      }
      if (event.error) {
        setPrototypeStream(prev => ({ ...prev, status: 'error', error: event.error }));
        setPipelineStages(prev => prev?.map(s =>
          s.id === 'prototype' ? { ...s, status: 'error', detail: event.error } : s
        ));
      }
    }, gateway.sessionId || initialSession?.id);
  }, [proto$, active.rawNodesRef, ideaText, displayMode, gateway.sessionId, initialSession?.id]);
  startPrototypeBuildRef.current = startPrototypeBuild;

  // ── Debate mode config for inline card ────────────────
  const DEBATE_MODE_CONFIG = useMemo(() => ({
    idea:     { panelIcon: '⚔', panelTitle: 'AUTO-CRITIQUE', statusCritiquing: 'Critic researching and analyzing...', statusRebutting: 'Architect researching and responding...', consensusDesc: (r, fc) => `After ${r} round${r!==1?'s':''}, the critic is satisfied.${fc>0?` ${fc} nodes updated.`:''}` },
    resume:   { panelIcon: '◎', panelTitle: 'HIRING REVIEW', statusCritiquing: 'Hiring manager reviewing strategy...', statusRebutting: 'Career coach building responses...', consensusDesc: (r, fc) => `After ${r} round${r!==1?'s':''}, the hiring manager would advance this candidate.${fc>0?` ${fc} nodes updated.`:''}` },
    codebase: { panelIcon: '⟨/⟩', panelTitle: 'CODE AUDIT', statusCritiquing: 'Security auditor reviewing code...', statusRebutting: 'Tech lead proposing solutions...', consensusDesc: (r, fc) => `After ${r} round${r!==1?'s':''}, the auditor is satisfied.${fc>0?` ${fc} nodes updated.`:''}` },
    decision: { panelIcon: '⚖', panelTitle: "DEVIL'S ADVOCATE", statusCritiquing: "Devil's advocate analyzing...", statusRebutting: 'Strategic advisor responding...', consensusDesc: (r, fc) => `After ${r} round${r!==1?'s':''}, the decision is validated.${fc>0?` ${fc} nodes updated.`:''}` },
    writing:  { panelIcon: '✦', panelTitle: 'EDITORIAL REVIEW', statusCritiquing: 'Senior editor reviewing...', statusRebutting: 'Writer addressing critiques...', consensusDesc: (r, fc) => `After ${r} round${r!==1?'s':''}, the editor approves.${fc>0?` ${fc} nodes updated.`:''}` },
    plan:     { panelIcon: '◉', panelTitle: 'RISK ANALYSIS', statusCritiquing: 'Risk analyst reviewing...', statusRebutting: 'Project manager mitigating...', consensusDesc: (r, fc) => `After ${r} round${r!==1?'s':''}, the plan is approved.${fc>0?` ${fc} nodes updated.`:''}` },
  }), []);

  // ── Debate: add nodes to canvas from debate loop ─────────
  const handleDebateNodesAdded = useCallback((newNodes) => {
    const existingIds = new Set(active.rawNodesRef.current.map(n => n.id));
    newNodes.forEach((flowNode) => {
      // Prevent duplicate IDs from debate rounds
      if (existingIds.has(flowNode.id)) {
        flowNode = { ...flowNode, id: flowNode.id + '_' + Date.now().toString(36).slice(-4) };
      }
      existingIds.add(flowNode.id);
      active.rawNodesRef.current = upsertNode(active.rawNodesRef.current, flowNode);
    });
    active.applyLayout(active.rawNodesRef.current, active.drillStackRef.current);
    active.setNodeCount(active.rawNodesRef.current.length);
  }, [active]);

  // ── Debate: update existing nodes in-place after consensus ─
  const handleDebateNodeUpdate = useCallback((updatedNode) => {
    active.rawNodesRef.current = active.rawNodesRef.current.map((n) =>
      n.id === updatedNode.id
        ? { ...n, data: { ...n.data, label: updatedNode.label, reasoning: updatedNode.reasoning } }
        : n
    );
    active.applyLayout(active.rawNodesRef.current, active.drillStackRef.current);
  }, [active]);

  // ── Shared: run debate inline in chat ────────────────
  const startDebateInChat = useCallback(async (pipelineCallback) => {
    const maxRounds = 5;
    const modeConfig = DEBATE_MODE_CONFIG[displayMode] || DEBATE_MODE_CONFIG.idea;
    const debateRounds = [];
    const allRoundsHistory = [];
    let currentNodes = [...active.rawNodesRef.current];

    setDebateStream({ status: 'critiquing', round: 1, maxRounds, modeConfig, rounds: [], finalizeCount: 0 });
    setShowChat(true);
    debateLoopRef.current = true;

    const updateStream = (patch) => {
      setDebateStream(prev => prev ? { ...prev, ...patch } : null);
    };

    const serializeNodes = (nodes) => nodes.map(n => ({
      id: n.id, type: n.data?.type || n.type, label: n.data?.label || n.label,
      reasoning: n.data?.reasoning || n.reasoning, parentId: n.data?.parentId || n.parentId,
    }));

    try {
      for (let round = 1; round <= maxRounds && debateLoopRef.current; round++) {
        // ── Step 1: Critique ──
        updateStream({ status: 'critiquing', round });
        pipelineCallback?.({ status: 'critiquing', round });

        const controller = new AbortController();
        debateAbortRef.current = controller;

        const critiqueRes = await authFetch(`${API_URL}/api/debate/critique`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nodes: serializeNodes(currentNodes), idea: ideaText, round,
            priorCritiques: allRoundsHistory.flatMap(r => r.blockers || []), mode: displayMode,
            sessionId: gateway.sessionId || initialSession?.id || null, chaosLevel,
          }),
          signal: controller.signal,
        });

        if (!critiqueRes.ok) throw new Error(`Server error: ${critiqueRes.status}`);
        const critiqueResult = await critiqueRes.json();

        if (!debateLoopRef.current) break;

        const { verdict, round_summary, critiques, consensus_blockers, suggestions } = critiqueResult;
        const blockers = suggestions || consensus_blockers || [];

        // Add critique nodes to canvas
        const critiqueFlowNodes = (critiques || []).map(c => buildFlowNode({
          id: `crit_r${round}_${c.id}`, parentId: c.targetNodeId,
          type: 'critique', label: c.challenge, reasoning: c.reasoning,
        }));
        if (critiqueFlowNodes.length) {
          currentNodes = [...currentNodes, ...critiqueFlowNodes];
          handleDebateNodesAdded(critiqueFlowNodes);
        }

        const roundEntry = {
          round, verdict, summary: round_summary,
          critiques: critiques || [], suggestions: blockers,
          rebutCount: 0,
        };
        debateRounds.push(roundEntry);
        updateStream({ rounds: [...debateRounds] });

        // Consensus → finalize
        if (verdict === 'YES') {
          allRoundsHistory.push({ ...roundEntry, blockers });
          updateStream({ status: 'finalizing' });
          pipelineCallback?.({ status: 'finalizing' });

          let fc = 0;
          const finController = new AbortController();
          debateAbortRef.current = finController;

          const finRes = await authFetch(`${API_URL}/api/debate/finalize`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              nodes: serializeNodes(currentNodes), idea: ideaText,
              debateHistory: allRoundsHistory, mode: displayMode,
              sessionId: gateway.sessionId || initialSession?.id || null, chaosLevel,
            }),
            signal: finController.signal,
          });
          if (finRes.ok) {
            await readSSEStream(finRes, (nodeData) => {
              if (nodeData._update) {
                handleDebateNodeUpdate(nodeData);
              } else {
                const flowNode = buildFlowNode(nodeData);
                handleDebateNodesAdded([flowNode]);
                currentNodes = [...currentNodes, flowNode];
              }
              fc++;
              updateStream({ finalizeCount: fc });
            });
          }
          handleConsensusReachedRef.current?.();
          debateRoundsRef.current = debateRounds;

          setDebateStream(null);
          setSessionDebates(prev => [...prev, ...debateRounds]);
          setPendingChatCards(prev => [...prev, {
            type: 'debate_card',
            state: { status: 'done', rounds: debateRounds, maxRounds, modeConfig, finalizeCount: fc },
          }]);
          pipelineCallback?.({ status: 'done' });
          debateLoopRef.current = false;
          return;
        }

        if (round >= maxRounds) {
          allRoundsHistory.push({ ...roundEntry, blockers });
          // Max rounds — finalize anyway
          updateStream({ status: 'finalizing' });
          let fc = 0;
          const finController = new AbortController();
          debateAbortRef.current = finController;
          const finRes = await authFetch(`${API_URL}/api/debate/finalize`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              nodes: serializeNodes(currentNodes), idea: ideaText,
              debateHistory: allRoundsHistory, mode: displayMode,
              sessionId: gateway.sessionId || initialSession?.id || null, chaosLevel,
            }),
            signal: finController.signal,
          });
          if (finRes.ok) {
            await readSSEStream(finRes, (nodeData) => {
              if (nodeData._update) {
                handleDebateNodeUpdate(nodeData);
              } else {
                const flowNode = buildFlowNode(nodeData);
                handleDebateNodesAdded([flowNode]);
                currentNodes = [...currentNodes, flowNode];
              }
              fc++;
              updateStream({ finalizeCount: fc });
            });
          }
          debateRoundsRef.current = debateRounds;
          setDebateStream(null);
          setSessionDebates(prev => [...prev, ...debateRounds]);
          setPendingChatCards(prev => [...prev, {
            type: 'debate_card',
            state: { status: 'done', rounds: debateRounds, maxRounds, modeConfig, finalizeCount: fc },
          }]);
          pipelineCallback?.({ status: 'done' });
          debateLoopRef.current = false;
          return;
        }

        // ── Step 2: Rebut ──
        if (!debateLoopRef.current) break;
        updateStream({ status: 'rebutting', round });
        pipelineCallback?.({ status: 'rebutting', round });

        const rebutController = new AbortController();
        debateAbortRef.current = rebutController;

        const rebutRes = await authFetch(`${API_URL}/api/debate/rebut`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nodes: serializeNodes(currentNodes), idea: ideaText,
            round, critiques: critiques || [], mode: displayMode,
            sessionId: gateway.sessionId || initialSession?.id || null, chaosLevel,
          }),
          signal: rebutController.signal,
        });

        if (!rebutRes.ok) throw new Error(`Server error: ${rebutRes.status}`);

        let rebutCount = 0;
        await readSSEStream(rebutRes, (nodeData) => {
          const flowNode = buildFlowNode(nodeData);
          currentNodes = [...currentNodes, flowNode];
          handleDebateNodesAdded([flowNode]);
          rebutCount++;
        });

        roundEntry.rebutCount = rebutCount;
        debateRounds[debateRounds.length - 1] = roundEntry;
        allRoundsHistory.push({ ...roundEntry, blockers });
        updateStream({ rounds: [...debateRounds] });
      }

      // Loop ended normally
      debateLoopRef.current = false;
      debateRoundsRef.current = debateRounds;
      setDebateStream(null);
      setPendingChatCards(prev => [...prev, {
        type: 'debate_card',
        state: { status: 'done', rounds: debateRounds, maxRounds, modeConfig, finalizeCount: 0 },
      }]);
    } catch (err) {
      if (err.name === 'AbortError') {
        // User stopped
        debateRoundsRef.current = debateRounds;
        setDebateStream(null);
        setPendingChatCards(prev => [...prev, {
          type: 'debate_card',
          state: { status: 'stopped', rounds: debateRounds, maxRounds, modeConfig, finalizeCount: 0 },
        }]);
      } else {
        console.error('Debate error:', err);
        updateStream({ status: 'error', error: err.message });
      }
      debateLoopRef.current = false;
    }
  }, [active, ideaText, displayMode, handleDebateNodesAdded, handleDebateNodeUpdate, DEBATE_MODE_CONFIG]);
  startDebateInChatRef.current = startDebateInChat;

  const handleStopDebateInChat = useCallback(() => {
    debateLoopRef.current = false;
    if (debateAbortRef.current) debateAbortRef.current.abort();
  }, []);

  // ── Shared: run portfolio inline in chat ────────────────
  const startPortfolioInChat = useCallback((pipelineCallback) => {
    setPortfolioStream({ status: 'generating', stageDetail: 'Starting...', alternatives: [], scores: [] });
    setShowChat(true);
    portfolio$.generate({
      idea: ideaText,
      mode: displayMode,
      focus: portfolioFocus,
      count: 3,
      existingTitles: portfolio$.alternatives.map(a => a.title),
      onProgress: (progress) => {
        // Forward to pipeline overlay if provided
        pipelineCallback?.(progress);
        if (progress.status === 'done') {
          setPortfolioStream(null);
          setPendingChatCards(prev => [...prev, {
            type: 'portfolio_card',
            state: {
              status: 'done',
              alternatives: progress.alternatives || [],
              scores: progress.scores || [],
              recommendation: progress.recommendation || '',
            },
          }]);
        } else {
          setPortfolioStream(prev => ({
            ...(prev || {}),
            status: 'generating',
            stageDetail: progress.stageDetail || '',
            alternatives: progress.alternatives || prev?.alternatives || [],
            scores: progress.scores || prev?.scores || [],
          }));
        }
      },
    });
  }, [portfolio$, ideaText, displayMode, portfolioFocus]);
  startPortfolioInChatRef.current = startPortfolioInChat;

  const startExperimentInChat = useCallback((iterations = 5) => {
    setExperimentStream({ status: 'scoring_baseline', iteration: 0, maxIterations: iterations, detail: 'Starting experiment loop...' });
    setShowChat(true);
    experiment$.handleStartExperiment(ideaText, displayMode, iterations, (progress) => {
      if (progress.status === 'done') {
        setExperimentStream(null);
        setPendingChatCards(prev => [...prev, {
          type: 'experiment_card',
          state: { status: 'done', history: progress.history || [], bestTree: progress.bestTree },
        }]);
      } else {
        setExperimentStream(progress);
      }
    });
  }, [experiment$, ideaText, displayMode]);

  // ── Load session handlers ─────────────────────────────────
  const handleLoadIdeaSession = useCallback((session) => {
    idea$.handleLoadSession(session, setIdea);
  }, [idea$]);

  const handleLoadCbSession = useCallback((session) => {
    cb$.handleLoadSession(session, setCbFolderName);
  }, [cb$]);

  // ── Version History ───────────────────────────────────────
  const handleShowHistory = useCallback(() => {
    setIdeaVersions(readVersions(idea.trim()));
    setShowHistory(true);
  }, [idea]);

  const handleLoadVersion = useCallback((version) => {
    idea$.rawNodesRef.current = version.rawNodes;
    idea$.drillStackRef.current = [];
    idea$.setDrillStack([]);
    idea$.setNodeCount(version.rawNodes.length);
    idea$.setSelectedNode(null);
    idea$.applyLayout(version.rawNodes, []);
    setIdea(version.label);
    setShowHistory(false);
  }, [idea$]);

  // ── Template extraction: after debate consensus ──────────
  const handleConsensusReached = useCallback(async () => {
    try {
      const rawNodes = active.rawNodesRef.current;
      if (!rawNodes?.length) return;
      const res = await authFetch(`${API_URL}/api/extract-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idea: ideaText.trim(),
          nodes: rawNodes.map(n => ({
            id: n.id, type: n.data?.type, label: n.data?.label,
            reasoning: n.data?.reasoning, parentId: n.data?.parentId,
          })),
        }),
      });
      if (!res.ok) return;
      const template = await res.json();
      if (template.structure?.length) saveTemplate(template);
    } catch (err) {
      console.error('Template extraction failed:', err);
    }

    // Pipeline: debate done — use dynamic pipeline orchestrator for next stage
    setPipelineStages(prev => prev?.map(s =>
      s.id === 'debate' ? { ...s, status: 'done', detail: 'Consensus reached' } : s
    ));

    // Advance pipeline to next stage (dynamic pattern selection)
    advancePipelineRef.current?.({ priorStage: 'debate', priorOutcome: { consensus: true, nodeCount: active.rawNodesRef.current?.length } });
  }, [idea, idea$, displayMode, resumeJobLabel, active]);
  handleConsensusReachedRef.current = handleConsensusReached;

  // ── Suggestion expand: add suggestion node + children to tree ─
  const handleSuggestionExpand = useCallback(async (suggestionText) => {
    const rawNodes = active.rawNodesRef.current;
    if (!rawNodes?.length) return;

    const res = await authFetch(`${API_URL}/api/expand-suggestion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        suggestion: suggestionText,
        idea: ideaText,
        nodes: rawNodes.map(n => ({
          id: n.id, type: n.data?.type, label: n.data?.label,
          reasoning: n.data?.reasoning, parentId: n.data?.parentId,
        })),
        mode: displayMode,
        dynamicTypes: active.dynamicTypesRef?.current || undefined,
        chaosLevel,
      }),
    });
    if (!res.ok) throw new Error(`Server error: ${res.status}`);

    const existingDynConfig = rawNodes[0]?.data?.dynamicConfig || null;
    await readSSEStream(res, (nodeData) => {
      const flowNode = buildFlowNode(nodeData);
      if (existingDynConfig) flowNode.data.dynamicConfig = existingDynConfig;
      active.rawNodesRef.current = upsertNode(active.rawNodesRef.current, flowNode);
      active.applyLayout(active.rawNodesRef.current, active.drillStackRef.current);
      active.setNodeCount(active.rawNodesRef.current.length);
    });
  }, [ideaText, active, displayMode]);

  // ── Chat → Graph actions (dispatched via action registry) ───
  const handleChatAction = useCallback((actions) => {
    if (!actions) return;

    // Build context object with all dependencies action handlers need
    const ctx = {
      active, setChatFilter, buildFlowNode, upsertNode,
      startDebateInChat, startRefineInChat, startPortfolioInChat,
      setRefineStream, setShowChat, handleGoDeeper, setPendingChatCards,
      setPortfolioFocus, setAutoFractalRounds, setShowAutoFractal,
      handleStartAutoFractal, ideaText, triggerScoring,
      setManualMode, setIdea, patternExec$, displayMode, dynamicDomain,
      startPrototypeBuild, nodeTools, authFetch, API_URL,
      emailContext, gmail, gateway, initialSession,
      setGoogleArtifacts, setViewLevel,
      setShowClaudeMemoryImport,
      handleNextModeSelect,
    };

    const cards = executeActions(actions, ctx);
    if (cards.length > 0) {
      setPendingChatCards(prev => [...prev, ...cards]);
    }
  }, [active, startRefineInChat, startPortfolioInChat, handleGoDeeper, handleStartAutoFractal, ideaText, triggerScoring, setManualMode, setIdea, patternExec$, displayMode, dynamicDomain, startPrototypeBuild, nodeTools, gateway.sessionId, initialSession?.id, handleNextModeSelect]);

  const handleClearChatFilter = useCallback(() => setChatFilter(null), []);

  // ── Execute action on a node (e.g., "Fix this" via Claude Code) ──
  const handleExecuteAction = useCallback(async (nodeId) => {
    const targetNode = active.rawNodesRef.current.find(n => n.id === nodeId);
    if (!targetNode) return;
    if (!cbProjectPath) {
      setPendingChatCards(prev => [...prev, {
        label: '⚠ No project path set',
        detail: 'Enter your local project path in the CODE mode header to enable "Fix this".',
        buttons: [],
      }]);
      setShowChat(true);
      return;
    }

    // Set node to in_progress
    active.updateNodeStatus(nodeId, 'in_progress', null);

    // Initialize live execution stream in chat
    const nodeLabel = targetNode.data.label;
    setExecutionStream({ nodeLabel, text: '', done: false, error: null, nodeId });
    setShowChat(true);

    // Create abort controller
    const abortController = new AbortController();
    executionAbortRef.current = abortController;

    let streamAccum = '';

    try {
      const res = await authFetch(`${API_URL}/api/execute-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nodeId,
          nodeData: targetNode.data,
          mode: 'codebase',
          projectPath: cbProjectPath,
        }),
        signal: abortController.signal,
      });

      // Read SSE stream
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        sseBuffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') continue;

          try {
            const evt = JSON.parse(payload);
            if (evt._text) {
              streamAccum += evt.text;
              setExecutionStream(prev => prev ? { ...prev, text: streamAccum } : prev);
            } else if (evt._progress) {
              streamAccum += `\n── ${evt.stage} ──\n`;
              setExecutionStream(prev => prev ? { ...prev, text: streamAccum } : prev);
            } else if (evt._result) {
              active.updateNodeStatus(nodeId, 'completed', evt);
              streamAccum += `\n✓ ${evt.summary || 'Fix completed successfully.'}`;
              setExecutionStream(prev => prev ? { ...prev, text: streamAccum, done: true } : prev);
            } else if (evt._error) {
              active.updateNodeStatus(nodeId, 'failed', { error: evt.error });
              streamAccum += `\n✗ Error: ${evt.error || 'An error occurred.'}`;
              setExecutionStream(prev => prev ? { ...prev, text: streamAccum, done: true, error: evt.error } : prev);
            }
          } catch {
            // skip unparseable lines
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        active.updateNodeStatus(nodeId, 'failed', { error: 'Aborted by user' });
        setExecutionStream(prev => prev ? { ...prev, text: streamAccum + '\n⏹ Execution stopped by user.', done: true, error: 'Aborted' } : prev);
      } else {
        active.updateNodeStatus(nodeId, 'failed', { error: err.message });
        setExecutionStream(prev => prev ? { ...prev, text: streamAccum + `\n✗ ${err.message || 'Network error.'}`, done: true, error: err.message } : prev);
      }
    } finally {
      executionAbortRef.current = null;
    }
  }, [active, cbProjectPath]);

  const handleCardButtonClick = useCallback((btn) => {
    if (btn.actionType === 'openPanel') {
      if (btn.panel === 'debate') startDebateInChat();
      else if (btn.panel === 'refine') startRefineInChat(3);
      else if (btn.panel === 'portfolio') startPortfolioInChat();
      else if (btn.panel === 'fractalExpand') setShowAutoFractal(true);
      else if (btn.panel === 'learn') startLearnInChat(7);
    } else if (btn.actionType === 'stopExecution') {
      if (executionAbortRef.current) {
        executionAbortRef.current.abort();
        executionAbortRef.current = null;
      }
      authFetch(`${API_URL}/api/stop-execution`, { method: 'POST' }).catch(() => {});
    } else if (btn.actionType === 'stopDebate') {
      handleStopDebateInChat();
    } else if (btn.actionType === 'resumeDebate') {
      startDebateInChat();
    } else if (btn.actionType === 'stopRefine') {
      handleStopRefine();
    } else if (btn.actionType === 'goDeeper') {
      // Trigger another refine round inline
      setRefineStream({ status: 'critiquing', round: 1, maxRounds: 2 });
      const refineHistory = [];
      handleGoDeeper(2, (progress) => {
        if (progress.status === 'round_complete') {
          refineHistory.push({
            round: progress.round, oldScore: progress.oldScore, newScore: progress.newScore,
            newNodeCount: progress.newNodeCount, summary: progress.summary,
          });
        }
        if (progress.status === 'done') {
          setRefineStream(null);
          setPendingChatCards(prev => [...prev, { type: 'refine_card', state: { status: 'done', history: refineHistory } }]);
        } else {
          setRefineStream({ ...progress, history: refineHistory });
        }
      });
    } else if (btn.actionType === 'exploreAlternative') {
      // Explore a portfolio alternative on the canvas
      portfolio$.handleExplore(btn.altIndex);
    } else if (btn.actionType === 'exploreAndRefine') {
      // Explore then start refine
      portfolio$.handleExplore(btn.altIndex);
      startRefineInChat(3);
    } else if (btn.actionType === 'generateMore') {
      startPortfolioInChat();
    }
    // ── Node Focus Card actions ──────────────────────────────
    else if (btn.actionType === 'drillDown') {
      const node = active.rawNodesRef.current.find(n => n.id === btn.nodeId);
      active.handleDrill(btn.nodeId);
      setFocusedNode(null);
      setPendingChatCards(prev => [...prev, { label: `Drilled into "${node?.data?.label || btn.nodeId}"`, detail: null, buttons: [] }]);
    } else if (btn.actionType === 'toggleStar') {
      active.handleToggleStar(btn.nodeId);
      const node = active.rawNodesRef.current.find(n => n.id === btn.nodeId);
      const nowStarred = node?.data?.starred;
      // Refresh focusedNode to show updated star state
      if (node) setFocusedNode(prev => prev ? { ...prev, node: { ...node } } : null);
    } else if (btn.actionType === 'expandNode') {
      active.handleFractalExpand(btn.nodeId);
      setPendingChatCards(prev => [...prev, { label: `Expanding "${active.rawNodesRef.current.find(n => n.id === btn.nodeId)?.data?.label || ''}"`, detail: 'Generating child nodes...', buttons: [] }]);
    } else if (btn.actionType === 'regenerateNode') {
      active.handleRegenerate(btn.nodeId);
      setPendingChatCards(prev => [...prev, { label: 'Regenerating subtree', detail: 'AI is regenerating this branch...', buttons: [] }]);
    } else if (btn.actionType === 'editNodeSave') {
      active.handleSaveNodeEdit(btn.nodeId, btn.updates);
      const updated = active.rawNodesRef.current.find(n => n.id === btn.nodeId);
      if (updated) setFocusedNode(prev => prev ? { ...prev, node: { ...updated } } : null);
    } else if (btn.actionType === 'splitNode') {
      const node = active.rawNodesRef.current.find(n => n.id === btn.nodeId);
      if (node) nodeTools.handleRazor(node);
    } else if (btn.actionType === 'mergeNode') {
      const node = active.rawNodesRef.current.find(n => n.id === btn.nodeId);
      if (node) nodeTools.handleStartMerge(node);
    } else if (btn.actionType === 'cancelMerge') {
      nodeTools.cancelMerge();
    } else if (btn.actionType === 'rippleDelete') {
      const node = active.rawNodesRef.current.find(n => n.id === btn.nodeId);
      if (node) {
        nodeTools.handleRippleDelete(node);
        setFocusedNode(null);
        setPendingChatCards(prev => [...prev, { label: `Deleted "${node.data?.label || ''}"`, detail: 'Children re-parented', buttons: [] }]);
      }
    } else if (btn.actionType === 'deleteBranch') {
      const node = active.rawNodesRef.current.find(n => n.id === btn.nodeId);
      if (node) {
        nodeTools.handleDeleteBranch(node);
        setFocusedNode(null);
        setPendingChatCards(prev => [...prev, { label: `Deleted branch "${node.data?.label || ''}"`, detail: 'Node and all descendants removed', buttons: [] }]);
      }
    } else if (btn.actionType === 'assignPattern') {
      const nodes = active.rawNodesRef.current;
      const idx = nodes.findIndex(n => n.id === btn.nodeId);
      if (idx >= 0) {
        const updated = { ...nodes[idx], data: { ...nodes[idx].data, pattern: btn.patternId } };
        const newNodes = [...nodes];
        newNodes[idx] = updated;
        active.rawNodesRef.current = newNodes;
        active.applyLayout(newNodes, active.drillStackRef.current);
        setFocusedNode(prev => prev ? { ...prev, node: updated } : null);
      }
    } else if (btn.actionType === 'runPatternOnSubtree') {
      // Direct "Run on subtree" from NodeFocusCard
      const allNodes = active.rawNodesRef.current;
      const subtreeIds = getSubtreeNodeIds(btn.nodeId, allNodes);
      const scopedNodes = allNodes.filter(n => subtreeIds.has(n.id));
      patternExec$.execute(btn.patternId, ideaText, scopedNodes, displayMode, { domain: dynamicDomain });
      setShowChat(true);
    } else if (btn.actionType === 'fixThis') {
      handleExecuteAction(btn.nodeId);
    } else if (btn.actionType === 'dismissNodeFocus') {
      setFocusedNode(null);
      active.setSelectedNode(null);
    }
    // ── Learn mode actions ──────────────────────────────────
    else if (btn.actionType === 'submitLearnAnswer') {
      learn$.submitAnswer(btn.answer);
    } else if (btn.actionType === 'learnContinue') {
      learn$.continueLoop();
    } else if (btn.actionType === 'learnExplainDifferently') {
      learn$.requestExplainDifferently();
    } else if (btn.actionType === 'learnSkip') {
      learn$.skipConcept(btn.conceptId);
    } else if (btn.actionType === 'learnHint') {
      learn$.requestHint();
    } else if (btn.actionType === 'learnReviewLesson') {
      learn$.reviewLesson(btn.conceptId);
    } else if (btn.actionType === 'stopLearn') {
      learn$.handleStopLearn();
      setLearnStream(null);
    } else if (btn.actionType === 'stopExperiment') {
      experiment$.handleStopExperiment();
      setExperimentStream(null);
    } else if (btn.actionType === 'experimentMore') {
      startExperimentInChat(3);
    } else if (btn.panel === 'experiment') {
      startExperimentInChat(5);
    } else if (btn.actionType === 'expandSuggestion') {
      handleSuggestionExpand(btn.suggestion).catch(err => console.error('Suggestion expand error:', err));
    } else if (btn.actionType === 'viewPrototype') {
      setShowPrototypeViewer(true);
    } else if (btn.actionType === 'stopPrototype') {
      proto$.handleStopBuild();
      setPrototypeStream(null);
    } else if (typeof btn.onClick === 'function') {
      // Generic fallback for buttons with direct onClick handlers (e.g. Open Doc)
      btn.onClick();
    }
  }, [handleStopRefine, handleGoDeeper, handleStartRefine, active, portfolio$, startRefineInChat, startPortfolioInChat, nodeTools, handleExecuteAction, learn$, experiment$, startExperimentInChat, proto$, handleSuggestionExpand]);

  // ── Toolbar scroll arrows ────────────────────────────────
  const updateToolbarScroll = useCallback(() => {
    const el = toolbarScrollRef.current;
    if (!el) return;
    setToolbarCanScrollLeft(el.scrollLeft > 2);
    setToolbarCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);
  useEffect(() => {
    const el = toolbarScrollRef.current;
    if (!el) return;
    updateToolbarScroll();
    el.addEventListener('scroll', updateToolbarScroll, { passive: true });
    const ro = new ResizeObserver(updateToolbarScroll);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateToolbarScroll); ro.disconnect(); };
  }, [updateToolbarScroll]);
  const scrollToolbar = useCallback((dir) => {
    const el = toolbarScrollRef.current;
    if (el) el.scrollBy({ left: dir * 150, behavior: 'smooth' });
  }, []);

  // ── 2D Temporal: maxRound ─────────────────────────────────
  const maxRound = useMemo(() => {
    if (!active.nodes.length) return 0;
    return Math.max(0, ...active.nodes.map(n => getRoundIndex(n)));
  }, [active.nodes]);

  // Sync roundRange when maxRound changes
  useEffect(() => {
    setRoundRange(prev => [prev[0], Math.max(prev[1], maxRound) > maxRound ? maxRound : Math.max(prev[1], maxRound)]);
  }, [maxRound]);

  // Reset temporal state on mode switch
  useEffect(() => {
    setRoundRange([0, 12]);
    setIsolatedRound(null);
    setIsPlayingRounds(false);
    setChatFilter(null);
  }, [activeMode]);

  // ── 2D Playback engine ──────────────────────────────────
  useEffect(() => {
    if (playbackTimerRef.current) clearInterval(playbackTimerRef.current);
    if (!isPlayingRounds || is3D) return;
    const intervalMs = 1200 / playbackSpeed;
    playbackTimerRef.current = setInterval(() => {
      setRoundRange(prev => {
        const next = Math.min(prev[1] + 1, maxRound);
        if (next >= maxRound) setIsPlayingRounds(false);
        return [0, next];
      });
    }, intervalMs);
    return () => { if (playbackTimerRef.current) clearInterval(playbackTimerRef.current); };
  }, [isPlayingRounds, playbackSpeed, maxRound, is3D]);

  // ── Child count map (for badge on parent nodes) ──
  // Use raw nodes (not filtered/visible) so collapsed parents still show correct counts
  const childCountMap = useMemo(() => {
    const map = {};
    const raw = active.rawNodesRef.current;
    (raw || []).forEach((n) => {
      const pid = n.data?.parentId;
      if (pid) map[pid] = (map[pid] || 0) + 1;
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.nodes, activeMode]);

  // ── Tree search: match label or reasoning (case-insensitive) ──
  const treeSearchTrim = (treeSearchQuery || '').trim();
  const chatFilterActive = chatFilter !== null;
  const selectedNodeId = active.selectedNode?.id;
  const displayNodes = useMemo(() => active.nodes.map((n) => {
    const roundIdx = getRoundIndex(n);
    const inRange = is3D ? true : (isolatedRound !== null
      ? roundIdx === isolatedRound
      : roundIdx >= roundRange[0] && roundIdx <= roundRange[1]);
    const searchActive = treeSearchTrim.length > 0;
    const searchMatch = !searchActive || (() => {
      const label = (n.data?.label || n.label || '').toLowerCase();
      const reasoning = (n.data?.reasoning || n.reasoning || '').toLowerCase();
      return label.includes(treeSearchTrim.toLowerCase()) || reasoning.includes(treeSearchTrim.toLowerCase());
    })();
    // Chat filter: match by type and/or specific node IDs
    let chatFilterMatch = true;
    if (chatFilterActive) {
      const matchesType = chatFilter.types?.length ? chatFilter.types.includes(n.data?.type) : true;
      const matchesId = chatFilter.nodeIds?.length ? chatFilter.nodeIds.includes(n.id) : true;
      // If both type and ID filters are set, match either
      chatFilterMatch = chatFilter.types?.length && chatFilter.nodeIds?.length
        ? matchesType || matchesId
        : matchesType && matchesId;
    }
    return {
      ...n,
      data: {
        ...n.data,
        isSelected: n.id === selectedNodeId,
        roundIndex: roundIdx,
        isInRange: inRange,
        searchActive,
        searchMatch,
        chatFilterActive,
        chatFilterMatch,
        childCount: childCountMap[n.id] || 0,
        // Pattern metadata for badge rendering
        patternMeta: n.data?.pattern ? availablePatterns.find(p => p.id === n.data.pattern) || null : null,
        // Fractal callbacks
        onFractalExpand: active.handleFractalExpand,
        onToggleCollapse: active.handleToggleCollapse,
        // Learn mode mastery
        ...(displayMode === 'learn' && learn$.masteryMap[n.id] ? { mastery: learn$.masteryMap[n.id].score } : {}),
        // Mnemonic video disabled

      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [active.nodes, selectedNodeId, is3D, isolatedRound, roundRange, treeSearchTrim, chatFilterActive, chatFilter, childCountMap, displayMode, learn$.masteryMap, mnemonic$.mnemonicJobs]);

  const activeEdges = active.edges;
  const displayEdges = useMemo(() => is3D ? activeEdges : activeEdges.map(e => {
    const srcNode = displayNodes.find(n => n.id === e.source);
    const tgtNode = displayNodes.find(n => n.id === e.target);
    const srcIn = srcNode?.data.isInRange !== false;
    const tgtIn = tgtNode?.data.isInRange !== false;
    if (srcIn && tgtIn) return e;
    const bothOut = !srcIn && !tgtIn;
    return {
      ...e,
      style: {
        ...e.style,
        stroke: bothOut ? '#111118' : '#1a1a2a',
        strokeWidth: bothOut ? 0.5 : 1,
        opacity: bothOut ? 0.15 : 0.35,
      },
      animated: false,
    };
  }), [activeEdges, displayNodes, is3D]);

  const isBusy = active.isGenerating || active.isRegenerating || isCritiquing;
  const panelOpen = !!active.selectedNode;
  const cbShowUpload = activeMode === 'codebase' && cb$.nodes.length === 0 && !cb$.isGenerating;
  const hasVersions = activeMode === 'idea' && idea.trim() && readVersions(idea.trim()).length > 1;

  const handlePlayToggle = useCallback(() => {
    if (isolatedRound !== null) setIsolatedRound(null);
    if (!isPlayingRounds && roundRange[1] >= maxRound) {
      setRoundRange([0, 0]);
    }
    setIsPlayingRounds(p => !p);
  }, [isolatedRound, isPlayingRounds, roundRange, maxRound]);

  // ── Pipeline stage status ──────────────────────────────────
  const pipelineStageStatus = useMemo(() => computeStageStatus(displayMode, {
    nodeCount: active.nodeCount,
    debateCount: sessionDebates?.length || 0,
    refineCount: refine$.refineHistory?.length || 0,
    evolveStarted: evolving || false,
    exported: false,
    portfolioCount: portfolio$.comparisons?.length || 0,
    isDebating: !!debateStream || patternExec$.isExecuting,
    isRefining: refine$.isRefining,
    isEvolving: evolving,
  }), [displayMode, active.nodeCount, sessionDebates?.length, refine$.refineHistory?.length, evolving, portfolio$.comparisons?.length, debateStream, patternExec$.isExecuting, refine$.isRefining]);

  // ── Contextual toolbar buttons (top 3 based on pipeline state) ──
  const contextualActions = useMemo(() => {
    const stages = getPipelineStages(displayMode);
    const actions = [];
    if (active.nodeCount === 0) return actions;

    // Find the current pipeline position
    let currentIdx = 0;
    for (let i = 0; i < stages.length; i++) {
      if (pipelineStageStatus[stages[i]] === 'done') currentIdx = i + 1;
    }

    // Map stage names to toolbar button configs
    const stageToAction = {
      'Critique':  { id: 'critique',  icon: (DEBATE_LABELS[displayMode] || DEBATE_LABELS.idea).icon, label: (DEBATE_LABELS[displayMode] || DEBATE_LABELS.idea).label },
      'Audit':     { id: 'critique',  icon: '⟨/⟩', label: 'AUDIT' },
      'Debate':    { id: 'critique',  icon: '⚖', label: 'ADVOCATE' },
      'Editorial': { id: 'critique',  icon: '✦', label: 'EDITORIAL' },
      'Risk':      { id: 'critique',  icon: '◉', label: 'RISK' },
      'Review':    { id: 'critique',  icon: '◎', label: 'REVIEW' },
      'Quiz':      { id: 'critique',  icon: '⧫', label: 'CHALLENGE' },
      'Refine':    { id: 'refine',    icon: '⟲', label: 'REFINE' },
      'Compare':   { id: 'portfolio', icon: '◈', label: 'COMPARE' },
      'Gaps':      { id: 'refine',    icon: '⟲', label: 'REFINE' },
      'Evolve':    { id: 'evolve',    icon: '⧬', label: 'EVOLVE' },
      'Deepen':    { id: 'evolve',    icon: '⧬', label: 'EVOLVE' },
      'Prototype': { id: 'prototype', icon: '◰', label: 'PROTOTYPE' },
      'Export':    { id: 'export',    icon: '↓', label: 'EXPORT' },
      'Master':    { id: 'export',    icon: '↓', label: 'EXPORT' },
    };

    // Show 2-3 actions starting from current position
    for (let i = currentIdx; i < stages.length && actions.length < 3; i++) {
      const mapping = stageToAction[stages[i]];
      if (mapping) actions.push(mapping);
    }

    // If we have room, add EXPLORE as a persistent suggestion
    if (actions.length < 3 && !actions.find(a => a.id === 'explore')) {
      actions.push({ id: 'explore', icon: '∞', label: 'EXPLORE' });
    }

    return actions;
  }, [displayMode, active.nodeCount, pipelineStageStatus]);

  // ── Command palette actions ────────────────────────────────
  const cmdActions = useMemo(() => {
    const hasNodes = active.nodeCount > 0;
    const debateLabel = DEBATE_LABELS[displayMode] || DEBATE_LABELS.idea;
    const chatLabel = CHAT_LABELS[displayMode] || CHAT_LABELS.idea;
    return [
      // Think
      { id: 'critique', icon: debateLabel.icon, label: debateLabel.label, description: debateLabel.tooltip, category: 'Think', disabled: !hasNodes, active: !!debateStream, onAction: () => { if (debateStream || patternExec$.isExecuting) { setShowChat(true); } else { setPipelineStages(null); setPipelineCheckpoint(null); const allNodes = active.rawNodesRef.current; const focusNode = focusedNode?.node; let patternId = null; let scopedNodes = allNodes; if (focusNode) { patternId = resolveNodePattern(focusNode.id, allNodes) || activePattern; const subtreeIds = getSubtreeNodeIds(focusNode.id, allNodes); scopedNodes = allNodes.filter(n => subtreeIds.has(n.id)); } else { patternId = activePattern; } if (patternId) { patternExec$.execute(patternId, ideaText, scopedNodes, displayMode, { domain: dynamicDomain }); setShowChat(true); } else { startDebateInChatRef.current?.(); } } }, keywords: ['debate', 'critique', 'audit', 'review'] },
      { id: 'explore', icon: '∞', label: 'EXPLORE', description: 'Autonomous fractal exploration', category: 'Think', disabled: !hasNodes, active: showAutoFractal, onAction: () => setShowAutoFractal(v => !v), keywords: ['fractal', 'expand', 'auto'] },
      { id: 'refine', icon: '⟲', label: 'REFINE', description: '5-pass deep analysis and strengthening', category: 'Think', disabled: !hasNodes || refine$.isRefining, active: refine$.isRefining, onAction: () => { setPipelineStages(null); setPipelineCheckpoint(null); startRefineInChatRef.current?.(3); }, keywords: ['improve', 'strengthen'] },
      { id: 'portfolio', icon: '◈', label: 'PORTFOLIO', description: 'Generate and compare alternatives', category: 'Think', disabled: !hasNodes || portfolio$.isGenerating, active: portfolio$.isGenerating, onAction: () => { setPipelineStages(null); setPipelineCheckpoint(null); startPortfolioInChatRef.current?.(); }, keywords: ['compare', 'alternatives'] },
      { id: 'experiment', icon: '⟳', label: 'EXPERIMENT', description: 'Autonomous idea experimentation loop', category: 'Think', disabled: !hasNodes || experiment$.isExperimenting, active: experiment$.isExperimenting, onAction: () => startExperimentInChat(5), keywords: ['autoidea', 'loop'] },
      { id: 'evolve', icon: '⧬', label: 'EVOLVE', description: 'Autonomous 5-step evolution plan', category: 'Think', disabled: !hasNodes || evolving || !gateway.sessionId, active: evolving, onAction: handleStartEvolve, keywords: ['evolution', 'daily'] },
      { id: 'next', icon: '◇', label: 'NEXT', description: 'AI picks the next best stage', category: 'Think', disabled: !hasNodes || !!pipelineCheckpoint, onAction: () => advancePipeline({ priorStage: 'generate', priorOutcome: { nodeCount: active.nodes.length } }), keywords: ['suggest', 'auto', 'pipeline'] },
      // Panels
      { id: 'chat', icon: '✦', label: chatLabel.title, description: chatLabel.tooltip, category: 'Panels', disabled: !hasNodes, active: showChat, onAction: () => setShowChat(v => !v), keywords: ['strategist', 'chat', 'advisor'] },
      { id: 'context', icon: '◧', label: 'CONTEXT', description: 'Session artifacts, files, activity', category: 'Panels', disabled: !hasNodes, active: rightPanel === 'context', onAction: () => setRightPanel(prev => prev === 'context' ? null : 'context'), keywords: ['files', 'artifacts'] },
      { id: 'memory', icon: '◈', label: 'MEMORY', description: 'Your thinking patterns', category: 'Panels', disabled: !(memorySessionCount >= 2 && activeMode === 'idea'), onAction: () => setShowMemory(v => !v), active: showMemory, keywords: ['patterns', 'history'] },
      ...(hasVersions ? [{ id: 'history', icon: '⎇', label: 'HISTORY', description: 'Version history', category: 'Panels', onAction: handleShowHistory, keywords: ['versions'] }] : []),
      ...(displayMode === 'learn' ? [{ id: 'learn', icon: '⧫', label: 'LEARN', description: 'Comprehension quiz loop', category: 'Think', disabled: learn$.isLearning, active: learn$.isLearning, onAction: () => startLearnInChat(7), keywords: ['quiz', 'study'] }] : []),
      // Output
      { id: 'prototype', icon: '◰', label: proto$.prototype ? 'VIEW PROTOTYPE' : 'PROTOTYPE', description: 'Build interactive prototype from tree', category: 'Output', disabled: !hasNodes || proto$.isBuilding || (active.rawNodesRef.current?.length || 0) < 5, active: proto$.isBuilding, onAction: () => { if (proto$.prototype && !proto$.isBuilding) { setShowPrototypeViewer(true); } else if (!proto$.isBuilding) { startPrototypeBuildRef.current?.(); } }, keywords: ['build', 'preview'] },
      { id: 'share', icon: '⊞', label: 'SHARE', description: 'Share or collaborate', category: 'Output', disabled: !hasNodes, onAction: () => setShowShareModal(true), keywords: ['collaborate', 'link'] },
      // View
      ...(agentFlowLog.length > 0 ? [{ id: 'flow', icon: '◉', label: 'FLOW', description: 'Agent execution flow view', category: 'View', active: viewMode === 'agent-flow', onAction: () => setViewMode(viewMode === 'agent-flow' ? 'flowchart' : 'agent-flow'), keywords: ['agent'] }] : []),
      ...(hasNodes ? [
        { id: 'slides', icon: '▤', label: viewMode === 'slides' ? 'GRAPH' : 'SLIDES', description: 'Slide deck walkthrough', category: 'View', active: viewMode === 'slides', onAction: () => setViewMode(viewMode === 'slides' ? 'flowchart' : 'slides'), keywords: ['deck', 'walkthrough', 'presentation'] },
        ...(idea$.nodes.length > 0 ? [{ id: 'journey', icon: '◈', label: viewMode === 'workflow-journey' ? 'GRAPH' : 'JOURNEY', description: 'Workflow journey document view', category: 'View', active: viewMode === 'workflow-journey', onAction: () => setViewMode(viewMode === 'workflow-journey' ? 'flowchart' : 'workflow-journey'), keywords: ['journey', 'document', 'workflow', 'output'] }] : []),
        { id: 'links', icon: '⇌', label: 'LINKS', description: 'Toggle cross-links', category: 'View', active: showCrossLinks, onAction: () => setShowCrossLinks(v => !v), keywords: ['cross-link', 'connections'] },
      ] : []),
    ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.nodeCount, displayMode, debateStream, patternExec$.isExecuting, showAutoFractal, refine$.isRefining, portfolio$.isGenerating, experiment$.isExperimenting, evolving, showChat, rightPanel, showMemory, memorySessionCount, activeMode, hasVersions, learn$.isLearning, proto$.prototype, proto$.isBuilding, showPrototypeViewer, agentFlowLog.length, viewMode, showCrossLinks, pipelineCheckpoint, gateway.sessionId, activePattern, dynamicDomain, focusedNode]);

  // Handler for pipeline stage clicks
  const handlePipelineStageClick = useCallback((stageName) => {
    const s = stageName.toLowerCase();
    const action = cmdActions.find(a => {
      if (s === 'critique' || s === 'audit' || s === 'debate' || s === 'editorial' || s === 'risk' || s === 'review' || s === 'quiz') return a.id === 'critique';
      if (s === 'refine' || s === 'compare' || s === 'gaps') return a.id === 'refine';
      if (s === 'evolve' || s === 'deepen') return a.id === 'evolve';
      if (s === 'prototype') return a.id === 'prototype';
      if (s === 'export' || s === 'master') return a.id === 'export';
      return false;
    });
    if (action && !action.disabled) action.onAction();
  }, [cmdActions]);

  // Phase B: accept a reasoner's transformation as a new node in the tree.
  // Adds it as a child of the seed node (or root) with provenance pointing
  // back to the lens that produced it. Lets the user act on critiques
  // without leaving the canvas.
  const handleAcceptTransformation = useCallback((transformation, lensLabel) => {
    if (!transformation?.what) return;
    const rootNode = idea$.rawNodesRef.current?.[0];
    const parentId = rootNode?.id;
    const newId = `via-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,7)}`;
    const reasoningParts = [];
    if (transformation.why) reasoningParts.push(`Why: ${transformation.why}`);
    if (transformation.expectedShift) reasoningParts.push(`Expected shift: ${transformation.expectedShift}`);
    reasoningParts.push(`\n_Provenance: accepted from ${lensLabel}_`);
    const newFlowNode = buildFlowNode({
      id: newId,
      type: 'experimental',
      label: transformation.what.slice(0, 200),
      reasoning: reasoningParts.join('\n'),
      parentId,
      provenance: { source: 'lens', lens: lensLabel },
    });
    if (yjsSyncRef.current) {
      yjsSyncRef.current.addNodeToYjs(newFlowNode);
    } else {
      idea$.rawNodesRef.current = [...(idea$.rawNodesRef.current || []), newFlowNode];
      idea$.applyLayout(idea$.rawNodesRef.current, []);
    }
    // Brief feedback could go here (toast); for v1, the new node appearing in the tree IS the feedback.
  }, [idea$]);

  // Phase B: drill into a sub-idea by seeding a fresh generate run.
  // Sets the idea text + enables novelty, then programmatically clicks GENERATE.
  const handleDrillNovelty = useCallback((seedLabel, seedReasoning, source = 'blend') => {
    const seed = `Drill into this idea further:\n\n${seedLabel}\n\n${seedReasoning || ''}\n\n(Drilled from ${source} of: "${ideaText.slice(0, 80)}...")`;
    setIdea(seed);
    setNoveltyEngine(true);
    // Programmatic click on GENERATE after state settles
    setTimeout(() => {
      const btn = Array.from(document.querySelectorAll('button')).find(b => /GENERATE/.test(b.textContent) && !/AGAIN/.test(b.textContent));
      btn?.click();
    }, 200);
  }, [ideaText]);

  // Tier 3: on-demand deep competitive brief. POST /api/verify-novelty,
  // cache the result on `verifications[candidateId]`, persist on the
  // session record server-side. Returns the brief so the caller can
  // render progress/result inline.
  const handleVerifyNovelty = useCallback(async (candidate, candidateId) => {
    if (!candidate?.label || !candidateId) return null;
    // Mark as in-flight so the UI can show a spinner
    setVerifications(prev => ({ ...prev, [candidateId]: { ...(prev[candidateId] || {}), _loading: true } }));
    try {
      const sid = gateway.sessionId || initialSession?.id || null;
      const res = await authFetch(`${API_URL}/api/verify-novelty`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate, candidateId, sessionId: sid }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const brief = await res.json();
      setVerifications(prev => ({ ...prev, [candidateId]: { ...brief, _loading: false } }));
      return brief;
    } catch (err) {
      setVerifications(prev => ({ ...prev, [candidateId]: { _loading: false, _error: err.message } }));
      return null;
    }
  }, [gateway.sessionId, initialSession?.id]);

  // Mirror state into refs so memoized callbacks see fresh values
  useEffect(() => { noveltyScoresRef.current = noveltyScores; }, [noveltyScores]);
  useEffect(() => { dmadResultsRef.current = dmadResults; }, [dmadResults]);
  useEffect(() => { recombinationsRef.current = recombinations; }, [recombinations]);
  useEffect(() => { blendScoresRef.current = blendScores; }, [blendScores]);
  useEffect(() => { qdArchiveRef.current = qdArchive; }, [qdArchive]);
  useEffect(() => { noveltySummaryRef.current = noveltySummary; }, [noveltySummary]);

  // ── Phase B: Novelty context value (memoized) ────────────
  const noveltyCtxValue = useMemo(() => ({
    scores: noveltyScores,
    cells: qdTags,
    filters: noveltyFilters,
    setFilter: (key, value) => setNoveltyFilters(prev => ({ ...prev, [key]: value })),
    clearFilters: () => setNoveltyFilters({ minNovelty: 0, cellMechanism: null, cellRisk: null, cellAudience: null, cellHorizon: null }),
    archive: qdArchive,
    recombinations,
    blendScores,
    dmadResults,
    dmadStatus,
    progress: noveltyProgress,
    onDrillNovelty: handleDrillNovelty,
    onAcceptTransformation: handleAcceptTransformation,
    // Phase C
    evolveGenerations,
    evolveLineage,
    evolveActiveGen,
    // Research grounding
    researchStatus,
    // Competition grounding
    competition,
    verifications,
    onVerifyNovelty: handleVerifyNovelty,
  }), [noveltyScores, qdTags, noveltyFilters, qdArchive, recombinations, blendScores, dmadResults, dmadStatus, noveltyProgress, handleDrillNovelty, handleAcceptTransformation, evolveGenerations, evolveLineage, evolveActiveGen, researchStatus, competition, verifications, handleVerifyNovelty]);

  return (
    <NoveltyContext.Provider value={noveltyCtxValue}>
    <div className={`app-shell${initialSession?.nextExperience ? ' app-shell--next' : ''}`}>
      {/* ── Global activity status line ── */}
      <StatusLine
        isGenerating={active.isGenerating}
        isRegenerating={active.isRegenerating}
        isDebating={!!debateStream}
        isRefining={refine$.isRefining}
        isPortfolio={portfolio$.isGenerating}
        isDrilling={active.isDrilling}
        isChatting={false /* managed internally by AgentChatPanel */}
        isLearning={learn$.isLearning}
        isPrototype={proto$.isBuilding}
        isPattern={patternExec$.isExecuting}
        isScoring={false}
        isExporting={false}
      />
      {/* ── Top bar ── */}
      <header className="top-bar">
        <div className="top-bar-left">
          {onBackToDashboard ? (
            <button className="btn btn-icon btn-home" onClick={onBackToDashboard} title="Home">
              <span className="logo-mark">◈</span>
              <span className="app-title">THOUGHTCLAW</span>
            </button>
          ) : (
            <>
              <span className="logo-mark">◈</span>
              <span className="app-title">THOUGHTCLAW</span>
            </>
          )}
        </div>

        {/* Right section — new contextual toolbar */}
        <div className="top-bar-right-wrapper">
        <div className="top-bar-right" ref={toolbarScrollRef}>
          {/* Info cluster */}
          {active.nodeCount > 0 && <span className="node-counter">{active.nodeCount} nodes</span>}
          <VelocityMeter
            nodes={active.nodes || []}
            debates={sessionDebates}
            artifacts={sessionArtifacts}
            chatMessages={savedChatMessages}
            refineRounds={refine$.refineHistory?.length || 0}
          />

          {/* Creativity controls */}
          <div className="toolbar-sep" />
          <div className={`creativity-controls${isBusy && (chaosLevel !== 50 || constraints.length > 0) ? ' creativity-controls--active' : ''}`}>
            <label className="creativity-label" title={`Creativity: ${chaosLevel}%`}>
              <span className="creativity-icon">{chaosLevel <= 30 ? '◎' : chaosLevel <= 70 ? '◈' : '✦'}</span>
              <input
                type="range"
                min="0"
                max="100"
                value={chaosLevel}
                onChange={(e) => setChaosLevel(parseInt(e.target.value, 10))}
                className="creativity-slider"
                title={`Creativity: ${chaosLevel}%`}
              />
              <span className="creativity-value">{chaosLevel}</span>
            </label>
            <button
              className={`btn btn-icon btn-constraints${constraints.length ? ' active-icon' : ''}`}
              onClick={() => setShowConstraints(v => !v)}
              title={constraints.length ? `${constraints.length} constraint${constraints.length > 1 ? 's' : ''} active` : 'Add forcing constraints'}
            >
              ⊘ {constraints.length || ''}
            </button>
          </div>
          {showConstraints && (
            <div className="constraints-dropdown">
              <div className="constraints-presets">
                {['No SaaS model', 'No cloud dependency', 'Zero marketing budget', 'Must work offline', 'No enterprise pricing', 'Open source only'].map(preset => (
                  <button
                    key={preset}
                    className={`constraint-chip${constraints.includes(preset) ? ' active' : ''}`}
                    onClick={() => setConstraints(prev =>
                      prev.includes(preset) ? prev.filter(c => c !== preset) : [...prev, preset]
                    )}
                  >
                    {preset}
                  </button>
                ))}
              </div>
              <div className="constraints-custom">
                <input
                  type="text"
                  className="constraint-input"
                  placeholder="Add custom constraint..."
                  value={constraintInput}
                  onChange={(e) => setConstraintInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && constraintInput.trim()) {
                      setConstraints(prev => [...prev, constraintInput.trim()]);
                      setConstraintInput('');
                    }
                  }}
                />
              </div>
              {constraints.length > 0 && (
                <div className="constraints-active">
                  {constraints.map((c, i) => (
                    <span key={i} className="constraint-chip active">
                      {c} <button className="constraint-remove" onClick={() => setConstraints(prev => prev.filter((_, j) => j !== i))}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Pipeline progress */}
          {active.nodeCount > 0 && (
            <>
              <div className="toolbar-sep" />
              <PipelineBar
                mode={displayMode}
                stageStatus={pipelineStageStatus}
                onStageClick={handlePipelineStageClick}
              />
            </>
          )}

          {/* Contextual action buttons (top 3 based on pipeline state) */}
          {active.nodeCount > 0 && contextualActions.length > 0 && (
            <>
              <div className="toolbar-sep" />
              {contextualActions.map(action => {
                const full = cmdActions.find(a => a.id === action.id);
                return (
                  <button
                    key={action.id}
                    className={`btn btn-icon${full?.active ? ' active-icon' : ''}`}
                    onClick={full?.onAction}
                    disabled={full?.disabled}
                    title={full?.description}
                  >
                    {action.icon} {action.label}
                  </button>
                );
              })}
            </>
          )}

          {/* Undo / Redo */}
          {active.rawNodesRef.current.length > 0 && (
            <div className="undo-redo-bar">
              <button className="undo-btn" onClick={undoStack.undo} disabled={!undoStack.canUndo} title="Undo (Ctrl+Z)">↩</button>
              <button className="redo-btn" onClick={undoStack.redo} disabled={!undoStack.canRedo} title="Redo (Ctrl+Shift+Z)">↪</button>
            </div>
          )}

          {/* Export dropdown */}
          {active.nodeCount > 0 && (
            <ExportDropdown
              nodes={active.nodes}
              idea={ideaText}
              disabled={isBusy || active.nodes.length === 0}
              sessionId={gateway.sessionId || initialSession?.id || null}
            />
          )}

          {/* Command palette trigger */}
          <div className="toolbar-sep" />
          <button
            className="btn btn-icon btn-cmd-palette"
            onClick={() => setCmdPaletteOpen(true)}
            title="Command palette (⌘K)"
          >
            ⌘K
          </button>

          {/* Yjs collaboration status bar */}
          {yjs && (
            <SyncStatusBar
              syncStatus={yjs.syncStatus}
              collaborators={yjs.collaborators}
              roomId={initialSession?.roomId}
            />
          )}

          {/* Usage indicator + User profile */}
          {usageData && (
            <div className={`usage-indicator${usageData.remaining <= 3 ? (usageData.remaining === 0 ? ' exhausted' : ' warning') : ''}`}>
              {usageData.remaining === 0 ? '⊘' : '◈'} {usageData.generationsToday}/{usageData.limit} today
            </div>
          )}
          {authUser && (
            <div className="user-profile" onClick={() => setShowUserMenu(v => !v)}>
              {authUser.photoURL
                ? <img src={authUser.photoURL} alt="" className="user-avatar" referrerPolicy="no-referrer" />
                : <div className="user-avatar-fallback">{(authUser.displayName || authUser.email || '?')[0].toUpperCase()}</div>
              }
              {showUserMenu && (
                <div className="user-dropdown">
                  <div className="user-dropdown-email">{authUser.email}</div>
                  <button className="user-dropdown-item" onClick={(e) => { e.stopPropagation(); authLogout(); }}>Sign out</button>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </header>

      {/* Command Palette */}
      <CommandPalette
        actions={cmdActions}
        open={cmdPaletteOpen}
        onClose={() => setCmdPaletteOpen(false)}
      />

      {/* ── View-level toggle (Session / Cowork) ── */}
      <div className="view-level-bar">
        <div className="view-level-toggle">
          <button
            className={`vl-btn${viewLevel === 'session' ? ' active' : ''}`}
            onClick={() => setViewLevel('session')}
          >Session</button>
          <button
            className={`vl-btn${viewLevel === 'cowork' ? ' active' : ''}`}
            onClick={() => setViewLevel('cowork')}
          >Cowork</button>
        </div>
      </div>

      {/* ── Mode bar ── */}
      <nav className="mode-bar">
        {MODES.filter(m => !m.hidden).map(mode => {
          const isActive   = displayMode === mode.id;
          const isDetected = detectedMode === mode.id && !manualMode && !isActive;
          const isManual   = manualMode === mode.id;
          const hasNodes   = idea$.nodes.length > 0 || cb$.nodes.length > 0;
          const isLocked   = hasNodes && !isActive;
          return (
            <button
              key={mode.id}
              className={`mode-tab${isActive ? ' active' : ''}${isDetected ? ' detected' : ''}${isLocked ? ' disabled' : ''}`}
              style={isActive ? { color: mode.color } : isLocked ? { opacity: 0.35, cursor: 'not-allowed' } : {}}
              onClick={() => !isLocked && handleModeSelect(mode.id)}
              title={
                isLocked   ? `${mode.label} — start a new session to switch modes` :
                isManual   ? `${mode.label} — locked · click to release` :
                isDetected ? `${mode.label} — auto-detected` :
                mode.label
              }
            >
              <span className="mode-tab-icon">{mode.icon}</span>
              <span>{mode.label}</span>
              {isDetected && <span className="mode-detect-dot" style={{ background: mode.color }} />}
              {isManual   && <span className="mode-locked-dot">⊕</span>}
            </button>
          );
        })}
      </nav>

      {initialSession?.nextExperience && (
        <NextModeHeader
          mode={displayMode}
          nodeCount={active.nodeCount}
          isGenerating={isBusy || patternExec$.isExecuting}
          hasMemory={!!claudeCodeContext}
          onSelectMode={handleNextModeSelect}
          onPrimaryAction={(actionId) => cmdActions.find(action => action.id === actionId)?.onAction?.()}
          actionDisabled={isBusy}
        />
      )}

      {upgradePrompt && (
        <div className="upgrade-banner">
          <span>You've used all {upgradePrompt.limit} generations today. Upgrade for 150/day.</span>
          <button className="upgrade-banner-btn" onClick={() => { setUpgradePrompt(null); onOpenSettings?.('billing'); }}>
            Upgrade to Pro — $20/mo
          </button>
          <button className="upgrade-banner-close" onClick={() => setUpgradePrompt(null)}>✕</button>
        </div>
      )}

      {active.error && (
        <div className="error-banner">
          <span>⚠ {active.error}</span>
          <button onClick={() => active.setError(null)}>✕</button>
        </div>
      )}

      {/* ── Content row: canvas + chat drawer ── */}
      <div className={`app-content-row${viewLevel === 'cowork' ? ' app-content-row--cowork' : ''}`}>
      <div className="canvas-column">
      {viewLevel === 'cowork' && (
        <CoworkPanel
          nodes={active.rawNodesRef.current}
          canvasArtifacts={sessionArtifacts}
          googleArtifacts={googleArtifacts}
          idea={ideaText}
          mode={displayMode}
          isGenerating={active.isGenerating}
          onNodeUpdate={(nodeId, updates) => active.updateNodeData(nodeId, updates)}
          onSelectArtifact={(selectFn) => { coworkSelectRef.current = selectFn; }}
        />
      )}
      {viewLevel !== 'cowork' && (
      /* ── Canvas area ── */
      <main className="canvas-area" style={{}}>

        {/* Onboarding tooltips overlay */}
        {onboardingTooltipStep >= 0 && (
          <OnboardingTooltips step={onboardingTooltipStep} onDismiss={handleDismissTooltip} />
        )}
        {showDemoPrompt && (
          <div className="onboarding-demo-prompt">Now try your own idea →</div>
        )}
        {progressiveNudge && (
          <ProgressiveNudge text={progressiveNudge.text} onDismiss={() => setProgressiveNudge(null)} />
        )}

        {/* Portfolio navigation breadcrumb */}
        {portfolio$.navStack.length > 0 && (
          <div className="portfolio-breadcrumb-bar">
            <button className="portfolio-crumb portfolio-crumb--root" onClick={() => portfolio$.handleNavigateBack(0)}>
              ⬆ Original Tree
            </button>
            {portfolio$.navStack.map((entry, i) => (
              <span key={i} className="portfolio-crumb-segment">
                <span className="portfolio-crumb-sep">›</span>
                <button
                  className={`portfolio-crumb ${i === portfolio$.navStack.length - 1 ? 'portfolio-crumb--active' : ''}`}
                  onClick={() => portfolio$.handleNavigateBack(i + 1)}
                >
                  {entry.title}
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Floating Journey/Graph toggle — hidden in learn mode (LEARN has its own nav) */}
        {activeMode === 'idea' && displayMode !== 'learn' && idea$.nodes.length > 0 && viewMode !== 'slides' && viewMode !== 'agent-flow' && (
          <button
            className={`journey-toggle-fab ${viewMode === 'workflow-journey' ? 'active' : ''}`}
            onClick={() => setViewMode(viewMode === 'workflow-journey' ? 'flowchart' : 'workflow-journey')}
            title={viewMode === 'workflow-journey' ? 'Back to Graph' : 'Journey View'}
          >
            <span className="journey-toggle-icon">{viewMode === 'workflow-journey' ? '⬡' : '◈'}</span>
            <span className="journey-toggle-label">{viewMode === 'workflow-journey' ? 'Graph' : 'Journey'}</span>
          </button>
        )}

        {activeMode === 'idea' && (
          /* Resume mode with empty canvas — show the resume input panel */
          displayMode === 'resume' && idea$.nodes.length === 0 && !idea$.isGenerating ? (
            <ResumeInput onAnalyzeReady={handleResumeAnalyze} />
          ) : idea$.nodes.length === 0 && !idea$.isGenerating && !patternExec$.isExecuting ? (
            <>
              {showMemory && (
                <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 25 }}>
                  <MemoryInsights onDismiss={() => setShowMemory(false)} />
                </div>
              )}
              {initialSession?.nextExperience
                ? <NextModeEmptyState mode={displayMode} hasMemory={!!claudeCodeContext} onExampleClick={(text) => { setIdea(text); setTimeout(() => ideaRef.current?.focus(), 0); }} />
                : <IdeaEmptyState mode={displayMode} onExampleClick={(text) => { setIdea(text); }} />}
            </>
          ) : sessionLoading ? (
            <div className="generating-indicator">
              <div className="generating-spinner" />
              <div className="generating-text">Loading session...</div>
            </div>
          ) : idea$.nodes.length === 0 && (idea$.isGenerating || patternExec$.isExecuting) ? (
            <div className="generating-indicator">
              <div className="generating-spinner" />
              <div className="generating-text">
                {patternExec$.isExecuting
                  ? `Running workflow: ${patternExec$.patternName || 'Executing'}...`
                  : initialSession?.nextExperience
                    ? `${getNextMode(displayMode).stages[1]} — building your ${getNextMode(displayMode).deliverable.toLowerCase()}...`
                    : 'Research & multi-agent thinking...'}
              </div>
              <div className="generating-sub">
                {patternExec$.isExecuting && patternExec$.currentStage
                  ? `Stage: ${patternExec$.currentStage}`
                  : initialSession?.nextExperience
                    ? getNextMode(displayMode).description
                    : 'Building your thinking tree from multiple perspectives'}
              </div>
            </div>
          ) : displayMode === 'learn' && idea$.nodes.length > 0 ? (
            <LearnJourneyView
              displayNodes={displayNodes}
              learnStream={learnStream}
              masteryMap={learn$.masteryMap}
              isLearning={learn$.isLearning}
              onConceptClick={(conceptId) => {
                const node = idea$.rawNodesRef.current.find(n => n.id === conceptId);
                if (node) {
                  idea$.handleNodeClick(node);
                  // Sync chat panel's focused-node context so questions about
                  // the currently-selected concept don't answer for the prior one.
                  setFocusedNode({ node, surgicalExpanded: false });
                }
              }}
              onStartLearn={(conceptId, opts) => {
                if (learn$.isLearning) learn$.handleStopLearn();
                // probeMode: 'mcq' from Quiz Me button → MCQ flow; default 'text'.
                learn$.setProbeMode(opts?.probeMode === 'mcq' ? 'mcq' : 'text');
                startLearnInChat(7, { startConceptId: conceptId });
              }}
              onAction={handleCardButtonClick}
              mnemonicJobs={{}}
              lessonCache={learn$.lessonCache}
              pushDrillRef={learnDrillPushRef}
              paperTitle={learn$.paperTitle}
              topic={idea}
              sessionId={gateway.sessionId || initialSession?.id}
              onExploreDeeper={(conceptId, conceptLabel, opts) => {
                if (conceptId === '__root__') {
                  // Jump back to root — exit drill and show full tree
                  idea$.handleExitDrill();
                  return;
                }
                if (opts?.jumpBack) {
                  // Breadcrumb jump — drill to that concept
                  const node = idea$.rawNodesRef.current.find(n => n.id === conceptId);
                  if (node) {
                    idea$.handleExitDrill();
                    setTimeout(() => idea$.handleDrill(conceptId), 50);
                  }
                  return;
                }
                // Stop current learn loop, drill into concept, then restart learning in sub-tree
                if (learn$.isLearning) learn$.handleStopLearn();
                idea$.handleDrill(conceptId).then(() => {
                  // After drill generates sub-nodes, start learning in the sub-tree
                  setTimeout(() => startLearnInChat(7), 300);
                }).catch(() => {});
              }}
            />
          ) : viewMode === 'workflow-journey' ? (
            <WorkflowJourneyView
              patternName={patternExec$.patternName || (debateStream ? 'Adversarial Critique' : null)}
              stageHistory={patternExec$.stageHistory?.length ? patternExec$.stageHistory : (pipelineStages || []).map(s => ({
                id: s.id, label: s.label || s.id, status: s.status === 'done' ? 'done' : s.status === 'active' ? 'active' : 'pending',
                round: 0, detail: s.detail || null, startedAt: null, completedAt: s.status === 'done' ? new Date().toISOString() : null,
              }))}
              isExecuting={patternExec$.isExecuting || !!debateStream || refine$.isRefining || portfolio$.isGenerating || !!pipelineStages?.some(s => s.status === 'active' || s.status === 'pending')}
              isSynthesizing={isSynthesizing}
              currentStage={patternExec$.currentStage || (debateStream ? 'debate' : null)}
              startedAt={patternExec$.startedAt}
              displayNodes={displayNodes}
              synthesizedOutput={workflowOutput}
              contractVerification={contractVerification}
              onToggleGraphView={() => setViewMode('flowchart')}
              onDrillNovelty={handleDrillNovelty}
            />
          ) : viewMode === 'slides' ? (
            <IdeaWalkthroughView
              displayNodes={displayNodes}
              dynamicDomain={dynamicDomain}
              dynamicLegendTypes={dynamicLegendTypes}
              onNodeClick={(node) => idea$.handleNodeClick(node)}
              onNodeFocus={(node) => {
                setFocusedNode({ node, surgicalExpanded: false });
                setShowChat(true);
              }}
              availablePatterns={availablePatterns}
            />
          ) : viewMode === 'agent-flow' ? (
            <ReactFlowProvider>
              <AgentFlowView
                agentFlowLog={agentFlowLog}
                onNodeClick={(node) => {
                  setShowChat(true);
                  setPendingChatCards(prev => [...prev, {
                    label: `Execution: ${node.data?.label || node.id}`,
                    detail: node.data?.model ? `Model: ${node.data.model}` : null,
                    buttons: [],
                  }]);
                }}
              />
            </ReactFlowProvider>
          ) : viewMode === '3d' ? (
            <Graph3D
              nodes={idea$.rawNodesRef.current}
              onNodeClick={(node3d) => {
                const raw = idea$.rawNodesRef.current.find(n => n.id === node3d.id);
                if (raw) {
                  idea$.handleNodeClick({ id: raw.id, data: raw.data || raw });
                  if (displayMode === 'learn' && raw.data?.type && ['concept', 'prerequisite'].includes(raw.data.type)) {
                    if (learn$.isLearning) learn$.handleStopLearn();
                    startLearnInChat(7, { startConceptId: raw.id });
                  }
                }
              }}
            />
          ) : viewMode === 'flowchart' ? (
            <ReactFlowProvider>
              {showMemory && (
                <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 25 }}>
                  <MemoryInsights onDismiss={() => setShowMemory(false)} />
                </div>
              )}
              <FlowchartView
                displayNodes={displayNodes}
                isGenerating={idea$.isGenerating}
                onNodeClick={(node) => {
                  idea$.handleNodeClick(node);
                  handleTooltipNodeInteraction();
                  if (displayMode === 'learn' && node?.data?.type && ['concept', 'prerequisite'].includes(node.data.type)) {
                    if (learn$.isLearning) learn$.handleStopLearn();
                    startLearnInChat(7, { startConceptId: node.id });
                  }
                }}
                onNodeDoubleClick={(nodeId) => { idea$.handleDrill(nodeId); handleTooltipNodeInteraction(); }}
                onNodeContextMenu={(nodeId, x, y) => {
                  idea$.handleNodeContextMenu(nodeId, x, y);
                  const node = idea$.rawNodesRef.current.find(n => n.id === nodeId);
                  if (node) setInspectorNode(node);
                }}
                onCloseContextMenu={() => { idea$.handleCloseContextMenu(); setInspectorNode(null); }}
                drillStack={idea$.drillStack}
                onExitDrill={idea$.handleExitDrill}
                onJumpToBreadcrumb={idea$.handleJumpToBreadcrumb}
                searchQuery={treeSearchQuery}
                onSearchChange={setTreeSearchQuery}
                onCollapseAll={idea$.handleCollapseAll}
                onExpandAll={idea$.handleExpandAll}
                hasCollapsed={displayNodes.some(n => n.data?.isCollapsed)}
                onReactFlowReady={(instance) => { reactFlowRef.current = instance; }}
                selectedNode={idea$.selectedNode}
              />
            </ReactFlowProvider>
          ) : (
            <ReactFlowProvider>
              {showMemory && (
                <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 25 }}>
                  <MemoryInsights onDismiss={() => setShowMemory(false)} />
                </div>
              )}
              <IdeaCanvas
                nodes={displayNodes}
                edges={displayEdges}
                isGenerating={isBusy}
                isScoring={isScoring}
                progressText={multiAgentProgress}
                onNodeClick={(node) => {
                  idea$.handleNodeClick(node);
                  handleTooltipNodeInteraction();
                  if (displayMode === 'learn' && node?.data?.type && ['concept', 'prerequisite'].includes(node.data.type)) {
                    if (learn$.isLearning) learn$.handleStopLearn();
                    startLearnInChat(7, { startConceptId: node.id });
                  }
                }}
                onNodeDoubleClick={(nodeId) => { idea$.handleDrill(nodeId); handleTooltipNodeInteraction(); }}
                onNodeContextMenu={(nodeId, x, y) => {
                  idea$.handleNodeContextMenu(nodeId, x, y);
                  const node = idea$.rawNodesRef.current.find(n => n.id === nodeId);
                  if (node) setInspectorNode(node);
                }}
                onCloseContextMenu={() => { idea$.handleCloseContextMenu(); setInspectorNode(null); }}
                drillStack={idea$.drillStack}
                onExitDrill={idea$.handleExitDrill}
                onJumpToBreadcrumb={idea$.handleJumpToBreadcrumb}
                searchQuery={treeSearchQuery}
                onSearchChange={setTreeSearchQuery}
                onReactFlowReady={(instance) => { reactFlowRef.current = instance; }}
                onCollapseAll={idea$.handleCollapseAll}
                onExpandAll={idea$.handleExpandAll}
                hasCollapsed={displayNodes.some(n => n.data?.isCollapsed)}
                selectedNode={idea$.selectedNode}
                allNodes={idea$.rawNodesRef.current}
              />
            </ReactFlowProvider>
          )
        )}

        {activeMode === 'codebase' && (
          cbShowUpload ? (
            <div className="codebase-upload-wrapper">
              <CodebaseUpload
                onAnalysisReady={handleAnalysisReady}
                isAnalyzing={cb$.isGenerating}
                github={{
                  configured: githubStatus.configured,
                  connected: githubStatus.connected,
                  account: githubStatus.account,
                  connect: githubConnect,
                  disconnect: githubDisconnect,
                }}
              />
            </div>
          ) : cb$.nodes.length === 0 && cb$.isGenerating ? (
            <div className="generating-indicator">
              <div className="generating-spinner" />
              <div className="generating-text">Analyzing codebase...</div>
              <div className="generating-sub">Fetching files, scoring relevance, and mapping architecture</div>
            </div>
          ) : viewMode === '3d' ? (
            <Graph3D
              nodes={cb$.rawNodesRef.current}
              onNodeClick={(node3d) => {
                const raw = cb$.rawNodesRef.current.find(n => n.id === node3d.id);
                if (raw) cb$.handleNodeClick({ id: raw.id, data: raw.data || raw });
              }}
            />
          ) : viewMode === 'flowchart' ? (
            <ReactFlowProvider>
              <FlowchartView
                displayNodes={displayNodes}
                isGenerating={cb$.isGenerating}
                onNodeClick={cb$.handleNodeClick}
                onNodeDoubleClick={cb$.handleDrill}
                onNodeContextMenu={cb$.handleNodeContextMenu}
                onCloseContextMenu={cb$.handleCloseContextMenu}
                drillStack={cb$.drillStack}
                onExitDrill={cb$.handleExitDrill}
                onJumpToBreadcrumb={cb$.handleJumpToBreadcrumb}
                searchQuery={treeSearchQuery}
                onSearchChange={setTreeSearchQuery}
                onCollapseAll={cb$.handleCollapseAll}
                onExpandAll={cb$.handleExpandAll}
                hasCollapsed={displayNodes.some(n => n.data?.isCollapsed)}
                onReactFlowReady={(instance) => { reactFlowRef.current = instance; }}
                selectedNode={cb$.selectedNode}
              />
            </ReactFlowProvider>
          ) : (
            <ReactFlowProvider>
              <IdeaCanvas
                nodes={displayNodes}
                edges={displayEdges}
                isGenerating={cb$.isGenerating || cb$.isRegenerating}
                onNodeClick={cb$.handleNodeClick}
                onNodeContextMenu={cb$.handleNodeContextMenu}
                onCloseContextMenu={cb$.handleCloseContextMenu}
                drillStack={cb$.drillStack}
                onExitDrill={cb$.handleExitDrill}
                onJumpToBreadcrumb={cb$.handleJumpToBreadcrumb}
                searchQuery={treeSearchQuery}
                onSearchChange={setTreeSearchQuery}
                onCollapseAll={cb$.handleCollapseAll}
                onExpandAll={cb$.handleExpandAll}
                hasCollapsed={displayNodes.some(n => n.data?.isCollapsed)}
                selectedNode={cb$.selectedNode}
                allNodes={cb$.rawNodesRef.current}
              />
            </ReactFlowProvider>
          )
        )}

        {/* TimelineBar2D removed — not useful for users */}

        {/* ── Tree Swap Banner (experiment loop) ── */}
        {treeSwapBanner && (
          <div className="tree-swap-banner">
            <span className="tree-swap-icon">⟳</span>
            <span className="tree-swap-text">
              Tree evolved! <strong>{treeSwapBanner.title}</strong> — score {treeSwapBanner.oldScore?.toFixed(1)} → {treeSwapBanner.newScore?.toFixed(1)} ({treeSwapBanner.nodeCount} nodes)
            </span>
            <span className="tree-swap-strategy">{(treeSwapBanner.strategy || '').replace(/_/g, ' ')}</span>
          </div>
        )}
      </main>
      )}

      {/* Timeline filmstrip removed — replaced by MiniMap in FlowchartView */}

      {/* ── Bottom input bar (hidden in cowork mode — chat is the primary input) ── */}
      <footer className="bottom-bar" style={viewLevel === 'cowork' ? { display: 'none' } : {}}>
        {/* Idea mode input row */}
        {activeMode === 'idea' && !(displayMode === 'resume' && idea$.nodes.length === 0 && !idea$.isGenerating) && (
          <div className="input-row">
            {displayMode === 'resume' ? (
              <>
                <div className="input-wrapper" style={{ opacity: 0.65, pointerEvents: 'none', flex: 1 }}>
                  <span className="input-prefix" style={{ color: '#74c0fc' }}>◎</span>
                  <span className="idea-input" style={{ padding: '0 0 0 4px', display: 'flex', alignItems: 'center' }}>
                    {resumeJobLabel || 'Resume Analysis'}
                  </span>
                </div>
                {idea$.isGenerating || isCritiquing ? (
                  <button className="btn btn-stop" onClick={handleStop}>■ STOP</button>
                ) : (
                  <button className="btn btn-generate" onClick={handleNewResumeAnalysis}>↺ NEW ANALYSIS</button>
                )}
              </>
            ) : redirectState === 'prompting' ? (
              <>
                <div className="input-wrapper steering-active">
                  <span className="input-prefix" style={{ color: '#ffa94d' }}>⟳</span>
                  <input
                    className="idea-input" type="text"
                    placeholder="steer the agent: what should it focus on next?"
                    value={steeringText}
                    onChange={(e) => setSteeringText(e.target.value)}
                    onKeyDown={handleSteeringKeyDown}
                    autoFocus
                  />
                </div>
                <button className="btn btn-redirect-submit" onClick={handleSteeringSubmit} disabled={!steeringText.trim()}>↵ RESUME</button>
                <button className="btn btn-stop" onClick={handleCancelRedirect}>✕ CANCEL</button>
              </>
            ) : (
              <>
                <div className="input-wrapper input-wrapper--multi">
                  <span className="input-prefix" style={{ color: MODES.find(m => m.id === displayMode)?.color ?? '#6c63ff' }}>›</span>
                  <textarea
                    ref={ideaRef}
                    className="idea-input"
                    rows={1}
                    placeholder={MODES.find(m => m.id === displayMode)?.placeholder ?? 'describe your idea, paste a doc, or upload a file...'}
                    value={idea}
                    onChange={handleIdeaChange}
                    onKeyDown={handleKeyDown}
                    disabled={isBusy}
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={displayMode === 'learn' ? '.pdf,.txt,.md,.text,.csv,.json,.html,.rtf' : '.txt,.md,.text,.csv,.json,.html,.rtf'}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                  />
                  {/* + Attachments menu */}
                  <div className="plus-menu-anchor">
                    <button
                      ref={plusMenuBtnRef}
                      className="plus-menu-btn"
                      title="Attach file or connect integrations"
                      onClick={() => setShowPlusMenu(v => !v)}
                      disabled={isBusy}
                    >
                      +
                    </button>
                    {showPlusMenu && (
                      <>
                        <div className="plus-menu-backdrop" onClick={() => setShowPlusMenu(false)} />
                        <div className="plus-menu-popover" style={(() => {
                          const r = plusMenuBtnRef.current?.getBoundingClientRect();
                          return r ? { bottom: window.innerHeight - r.top + 8, left: r.left } : {};
                        })()}>
                          <button className="plus-menu-item" onClick={() => { setShowPlusMenu(false); fileInputRef.current?.click(); }}>
                            <span className="plus-menu-item-icon">📎</span> Upload File
                          </button>
                          <button className="plus-menu-item" onClick={() => { setShowPlusMenu(false); sessionFileInputRef.current?.click(); }}>
                            <span className="plus-menu-item-icon">📁</span> Attach Context Files
                          </button>
                          <button className="plus-menu-item" onClick={() => { setShowPlusMenu(false); setShowClaudeCodePicker(true); }}>
                            <span className="plus-menu-item-icon">⬡</span> Import Claude Context
                          </button>
                          {gmail.configured && (
                            gmail.connected ? (
                              <>
                                <button className="plus-menu-item" onClick={() => { setShowPlusMenu(false); gmail.openPicker(); }}>
                                  <span className="plus-menu-item-icon">✉</span> Import Email
                                </button>
                                <button className="plus-menu-item plus-menu-item--danger" onClick={() => { setShowPlusMenu(false); gmail.disconnect(); }}>
                                  <span className="plus-menu-item-icon">✕</span> Disconnect Google
                                </button>
                              </>
                            ) : (
                              <button className="plus-menu-item" onClick={() => { setShowPlusMenu(false); gmail.connect(); }}>
                                <span className="plus-menu-item-icon">✉</span> Connect Google
                              </button>
                            )
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {attachedFile && (
                    <span className="file-badge" title={attachedFile.name}>
                      {attachedFile.name.length > 18 ? attachedFile.name.slice(0, 15) + '...' : attachedFile.name}
                      <button className="file-badge-x" onClick={() => { setAttachedFile(null); setLearnPdf(null); learn$.setPaperTitle(null); setIdea(''); setTimeout(autoResize, 0); }}>×</button>
                    </span>
                  )}
                  {emailContext && (
                    <span className="file-badge email-badge" title={emailContext.subject}>
                      ✉ {emailContext.subject?.length > 20 ? emailContext.subject.slice(0, 18) + '...' : emailContext.subject} ({emailContext.messageCount})
                      <button className="file-badge-x" onClick={() => setEmailContext(null)}>×</button>
                    </span>
                  )}
                  {claudeCodeContext && (
                    <span className="file-badge cc-badge" title="Claude Code context loaded">
                      ⬡ Claude Code ({claudeCodeContext.sessionCount} sessions)
                      <button className="file-badge-x" onClick={() => setClaudeCodeContext(null)}>×</button>
                    </span>
                  )}
                </div>
                {(idea$.isGenerating || isCritiquing) ? (
                  <>
                    {(chaosLevel !== 50 || constraints.length > 0) && (
                      <span className="gen-creativity-badge">
                        <span className="gen-creativity-tier">{chaosLevel <= 30 ? '◎' : chaosLevel <= 70 ? '◈' : '✦'}</span>
                        {chaosLevel}%
                        {constraints.length > 0 && <span className="gen-creativity-constraints">⊘{constraints.length}</span>}
                      </span>
                    )}
                    <button className="btn btn-redirect" onClick={handleRedirect} disabled={isCritiquing}>⟳ REDIRECT</button>
                    <button className="btn btn-stop" onClick={handleStop}>■ STOP</button>
                  </>
                ) : (
                  <>
                    <button className="btn btn-generate" onClick={initialSession?.nextExperience ? handleGenerate : handleGenerateResearch} disabled={!idea.trim() || idea$.isRegenerating || isFetchingUrl}>
                      {isFetchingUrl ? '◌ FETCHING URL...' : '▶ GENERATE'}
                    </button>
                    {displayMode === 'idea' && onOpenForest && (
                      <button className="btn btn-forest" onClick={handleForestDecompose} disabled={!idea.trim() || isDecomposing} title="Decompose into multi-canvas forest">
                        {isDecomposing ? '◌ DECOMPOSING...' : '◈ FOREST'}
                      </button>
                    )}
                    <div className="gen-auto-options">
                      {/* ⟲ Refine + ◈ Portfolio toggles removed — superseded by the
                          novelty engine (Phase A counterfactual + Phase C
                          market-sim cover Refine; Phase B QD archive replaces
                          Portfolio). */}
                      <label className="gen-auto-check" title="Auto-build interactive prototype after generation">
                        <input type="checkbox" checked={autoPrototypeOnGen} onChange={e => setAutoPrototypeOnGen(e.target.checked)} />
                        <span>◰ Prototype</span>
                      </label>
                      <label className="gen-auto-check" title="Experimental: heterogeneous reasoning agents + novelty scoring (Phase A of NOVELTY_ENGINE_PLAN.md)">
                        <input type="checkbox" checked={noveltyEngine} onChange={e => setNoveltyEngine(e.target.checked)} />
                        <span>🧬 Novelty</span>
                      </label>
                      <label className="gen-auto-check" title="Phase C: evolutionary loop with external evaluators (falsifiability + anti-pattern + market-sim). Requires Novelty engine.">
                        <input
                          type="checkbox"
                          checked={evolveEngine}
                          disabled={!noveltyEngine}
                          onChange={e => setEvolveEngine(e.target.checked)}
                        />
                        <span>🌱 Evolve</span>
                      </label>
                      {autoPrototypeOnGen && (
                        <label className="gen-auto-check" title="Auto-pilot: skip checkpoints and let AI choose patterns automatically">
                          <input type="checkbox" checked={autonomousMode} onChange={e => setAutonomousMode(e.target.checked)} />
                          <span>⚡ Auto-pilot</span>
                        </label>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
            {/* Hidden file input for session context files */}
            <input
              ref={sessionFileInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.pptx,.txt,.md,.json,.yaml,.yml,.js,.ts,.jsx,.tsx,.py"
              style={{ display: 'none' }}
              onChange={async (e) => {
                if (!e.target.files?.length || !gateway.sessionId) return;
                const formData = new FormData();
                for (const file of e.target.files) formData.append('files', file);
                try {
                  const res = await authFetch(`${API_URL}/api/sessions/${gateway.sessionId}/files`, { method: 'POST', body: formData });
                  const data = await res.json();
                  if (data.files) setSessionFiles(prev => [...prev, ...data.files]);
                } catch (err) { console.error('Upload failed:', err); }
                e.target.value = '';
              }}
            />
            <SessionFilesBar
              sessionId={gateway.sessionId}
              files={sessionFiles}
              setFiles={setSessionFiles}
              onContextUpdate={setSessionFileContext}
            />
          </div>
        )}

        {/* Codebase mode input row */}
        {activeMode === 'codebase' && !cbShowUpload && (
          <div className="input-row">
            <div className="input-wrapper" style={{ opacity: 0.65, pointerEvents: 'none', flex: 1 }}>
              <span className="input-prefix">⟨/⟩</span>
              <span className="idea-input" style={{ padding: '0 0 0 4px', display: 'flex', alignItems: 'center' }}>{cbFolderName}</span>
            </div>
            <div className="project-path-wrapper">
              <input
                type="text"
                className="project-path-input"
                value={cbProjectPath}
                onChange={(e) => setCbProjectPath(e.target.value)}
                placeholder={`local path, e.g. /Users/you/${cbFolderName || 'project'}`}
                title="Local filesystem path for Claude Code to fix issues"
              />
            </div>
            {cb$.isGenerating ? (
              <button className="btn btn-stop" onClick={handleStop}>■ STOP</button>
            ) : (
              <button className="btn btn-generate" onClick={handleNewCbAnalysis}>↺ NEW ANALYSIS</button>
            )}
          </div>
        )}
      </footer>

      {/* NodeEditPanel removed — replaced by NodeFocusCard in ChatPanel */}

      {/* NodeContextMenu removed — replaced by NodeFocusCard in ChatPanel */}

      {/* ── F4: Preview Overlay ── */}
      {previewState && (
        <PreviewOverlay
          previewNodes={previewState.nodes}
          removedNodeIds={previewState.removedIds}
          actionLabel={previewState.label}
          onAccept={() => {
            previewState.onAccept?.();
            setPreviewState(null);
          }}
          onReject={() => {
            previewState.onReject?.();
            setPreviewState(null);
          }}
        />
      )}

      {/* ── Full Prototype Viewer ── */}
      {showPrototypeViewer && proto$.prototype && (
        <FullPrototypePlayer
          prototype={proto$.prototype}
          onClose={() => setShowPrototypeViewer(false)}
          onRegenScreen={(idx, instruction) => proto$.handleRegenScreen(idx, instruction, gateway.sessionId || initialSession?.id)}
        />
      )}

      {/* ── Claude Code Picker ── */}
      {showClaudeCodePicker && (
        <ClaudeCodePicker
          onLoadContext={(context, sessionCount) => setClaudeCodeContext({ context, sessionCount })}
          onClose={() => setShowClaudeCodePicker(false)}
        />
      )}

      {showClaudeMemoryImport && (
        <div className="cm-overlay" role="dialog" aria-modal="true" aria-label="Import Claude memory">
          <ClaudeMemoryImport onClose={() => setShowClaudeMemoryImport(false)} onStartMission={() => setShowClaudeMemoryImport(false)} />
        </div>
      )}

      {/* ── F5: Inspector Panel ── */}
      {inspectorNode && (
        <InspectorPanel
          node={inspectorNode}
          rawNodesRef={active.rawNodesRef}
          onSave={(nodeId, updates) => {
            active.handleSaveNodeEdit(nodeId, updates);
            // Refresh inspector with updated node
            const updated = active.rawNodesRef.current.find(n => n.id === nodeId);
            if (updated) setInspectorNode(updated);
          }}
          onClose={() => setInspectorNode(null)}
          onNodeClick={(n) => {
            active.handleNodeClick(n);
            setInspectorNode(n);
          }}
          streamActions={streams$}
          synapseActions={synapses$}
          activationActions={activation$}
        />
      )}

      {/* ── F5: Hover Preview Tooltip ── */}
      {hoverPreview.hoverPreview && (
        <div
          className="hover-preview-card"
          style={{
            left: Math.min(hoverPreview.hoverPreview.x + 20, window.innerWidth - 320),
            top: Math.min(hoverPreview.hoverPreview.y - 10, window.innerHeight - 200),
          }}
        >
          <div className="hover-preview-header">
            <span className="hover-preview-type" style={{
              color: hoverPreview.hoverPreview.nodeData?.type === 'thesis' ? '#a78bfa' :
                     hoverPreview.hoverPreview.nodeData?.type === 'antithesis' ? '#f472b6' :
                     hoverPreview.hoverPreview.nodeData?.type === 'synthesis' ? '#34d399' : '#888'
            }}>
              {(hoverPreview.hoverPreview.nodeData?.type || 'node').toUpperCase()}
            </span>
            {hoverPreview.hoverPreview.nodeData?.score != null && (
              <span className="hover-preview-score">
                {hoverPreview.hoverPreview.nodeData.score}/10
              </span>
            )}
          </div>
          <div className="hover-preview-label">
            {hoverPreview.hoverPreview.nodeData?.label}
          </div>
          {hoverPreview.hoverPreview.nodeData?.reasoning && (
            <div className="hover-preview-reasoning">
              {hoverPreview.hoverPreview.nodeData.reasoning.slice(0, 120)}
              {hoverPreview.hoverPreview.nodeData.reasoning.length > 120 ? '…' : ''}
            </div>
          )}
          <div className="hover-preview-meta">
            {hoverPreview.hoverPreview.parents.length > 0 && (
              <span>↑ {hoverPreview.hoverPreview.parents.length} parent{hoverPreview.hoverPreview.parents.length > 1 ? 's' : ''}</span>
            )}
            {hoverPreview.hoverPreview.childCount > 0 && (
              <span>↓ {hoverPreview.hoverPreview.childCount} children</span>
            )}
          </div>
        </div>
      )}

      {showHistory && (
        <HistoryModal
          versions={ideaVersions}
          currentNodes={idea$.rawNodesRef.current}
          onLoad={handleLoadVersion}
          onClose={() => setShowHistory(false)}
        />
      )}

      {/* Drawer toggle — re-opens chat panel without losing position. Visible
          in all modes so chat is one click away (was previously hidden in learn). */}
      {viewLevel !== 'cowork' && !rightPanel && active.rawNodesRef.current.length > 0 && (
        <button
          className={`chat-drawer-toggle${displayMode === 'learn' ? ' chat-drawer-toggle-learn' : ''}`}
          onClick={() => setShowChat(true)}
          title="Open chat panel"
          aria-label="Open chat panel"
        >
          <span className="chat-drawer-toggle-icon" aria-hidden="true">⊞</span>
        </button>
      )}
      </div>{/* end canvas-column */}
      {/* ── Agentic Chat Panel ── */}
      <AgentChatPanel
        isOpen={showChat || viewLevel === 'cowork'}
        onClose={() => { if (viewLevel !== 'cowork') { setShowChat(false); setChatFilter(null); } }}
        nodes={active.rawNodesRef.current}
        idea={ideaText}
        chaosLevel={chaosLevel}
        mode={displayMode}
        onChatAction={handleChatAction}
        chatFilterActive={chatFilterActive}
        onClearFilter={handleClearChatFilter}
        pendingChatCards={pendingChatCards}
        onClearPendingCards={() => setPendingChatCards([])}
        onCardButtonClick={handleCardButtonClick}
        executionStream={executionStream}
        onStopExecution={() => {
          if (executionAbortRef.current) {
            executionAbortRef.current.abort();
            authFetch(`${API_URL}/api/stop-execution`, { method: 'POST' }).catch(() => {});
          }
        }}
        onDismissStream={() => setExecutionStream(null)}
        refineStream={refineStream}
        portfolioStream={portfolioStream}
        learnStream={learnStream}
        experimentStream={experimentStream}
        debateStream={debateStream}
        prototypeStream={prototypeStream}
        focusedNode={focusedNode ? {
          ...focusedNode,
          node: active.rawNodesRef.current.find(n => n.id === focusedNode.node?.id) || focusedNode.node,
          isSplitting: nodeTools.isSplitting,
          isMerging: nodeTools.isMerging,
          mergeTarget: nodeTools.mergeTarget,
        } : null}
        onDismissFocus={() => { setFocusedNode(null); active.setSelectedNode(null); }}
        emailContext={emailContext}
        pipelineStages={pipelineStages}
        onClosePipeline={() => setPipelineStages(null)}
        patternFramework={patternFramework}
        patternExecState={patternExec$.isExecuting || patternExec$.stageHistory?.length ? {
          stage: patternExec$.currentStage,
          round: patternExec$.currentRound,
          checkpoint: patternExec$.checkpoint,
          stages: patternExec$.stageHistory,
          patternName: patternExec$.patternName,
          startedAt: patternExec$.startedAt,
          complete: !patternExec$.isExecuting && patternExec$.stageHistory?.length > 0,
          error: !!patternExec$.error,
        } : null}
        onStopPattern={() => patternExec$.stop()}
        availablePatterns={availablePatterns}
        sessionFileContext={[sessionFileContext, claudeCodeContext?.context].filter(Boolean).join('\n\n') || null}
        sessionId={gateway.sessionId || initialSession?.id || null}
        pipelineCheckpoint={pipelineCheckpoint}
        onPipelineCheckpointAction={handlePipelineCheckpointDecision}
        mnemonicJobs={{}}
        paperTitle={learn$.paperTitle}
        generationFlow={generationFlow}
        noveltyScores={noveltyScores}
        dmadResults={dmadResults}
        noveltySummary={noveltySummary}
        qdArchive={qdArchive}
        recombinations={recombinations}
        blendScores={blendScores}
        workflowOutput={workflowOutput}
        googleArtifacts={googleArtifacts}
        onClickArtifact={(artifactId) => {
          setViewLevel('cowork');
          if (coworkSelectRef.current) coworkSelectRef.current(artifactId);
        }}
        initialChatMessages={savedChatMessages}
        onSaveChatMessages={(msgs) => {
          setSavedChatMessages(msgs);
          // Save to cloud session if available
          const sid = gateway.sessionId || initialSession?.id;
          if (sid) {
            authFetch(`${API_URL}/api/sessions/${sid}/chat`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ chatMessages: msgs.slice(-30) }), // keep last 30
            }).catch(() => {});
          }
        }}
        onChatExploreDeeper={(topic, aiResponse) => {
          // Create a synthetic concept node from the chat topic, add to tree, drill into it
          const syntheticId = `chat_drill_${Date.now().toString(36)}`;
          const syntheticNode = buildFlowNode({
            id: syntheticId,
            type: 'concept',
            label: topic,
            reasoning: aiResponse?.slice(0, 300) || '',
            parentIds: learnStream?.conceptId ? [learnStream.conceptId] : [],
          });
          // Add synthetic node to the tree
          idea$.rawNodesRef.current = [...idea$.rawNodesRef.current, syntheticNode];
          idea$.applyLayout(idea$.rawNodesRef.current, idea$.drillStackRef?.current || []);
          // Push to LearnJourneyView breadcrumb
          learnDrillPushRef.current?.(syntheticId, topic);
          // Stop current learn loop, drill into the synthetic node, restart learning
          if (learn$.isLearning) learn$.handleStopLearn();
          idea$.handleDrill(syntheticId).then(() => {
            setTimeout(() => startLearnInChat(7), 300);
          }).catch(() => {});
        }}
      />
      {/* ── Session Context Panel ── */}
      <SessionContextPanel
        isOpen={rightPanel === 'context'}
        onClose={() => setRightPanel(null)}
        artifacts={sessionArtifacts}
        canvasArtifacts={[]}
        sessionFiles={sessionFiles}
        debates={sessionDebates}
        chatMessages={savedChatMessages}
        generationFlow={generationFlow}
        noveltyScores={noveltyScores}
        dmadResults={dmadResults}
        noveltySummary={noveltySummary}
        qdArchive={qdArchive}
        recombinations={recombinations}
        blendScores={blendScores}
        pipelineStages={pipelineStages}
        patternExecState={patternExec$.isExecuting || patternExec$.stageHistory?.length ? {
          stage: patternExec$.currentStage,
          round: patternExec$.currentRound,
          stages: patternExec$.stageHistory,
          patternName: patternExec$.patternName,
          complete: !patternExec$.isExecuting && patternExec$.stageHistory?.length > 0,
        } : null}
        debateStream={debateStream}
        refineStream={refineStream}
        portfolioStream={portfolioStream}
        experimentStream={experimentStream}
        prototypeStream={prototypeStream}
        mode={displayMode}
        nodeCount={active.rawNodesRef?.current?.length || 0}
        refineRounds={refine$.refineHistory?.length || 0}
      />
      </div>{/* end app-content-row */}

      {/* ── Gmail Thread Picker Modal ── */}
      <GmailPicker {...gmail} />

      {/* ── Auto-Fractal Panel ── */}
      {showAutoFractal && (
        <div className="auto-fractal-panel">
          <div className="auto-fractal-header">
            <span>∞ FRACTAL EXPLORE</span>
            <button className="panel-close-btn" onClick={() => setShowAutoFractal(false)}>✕</button>
          </div>
          {!autoFractalRunning ? (
            <div className="auto-fractal-config">
              <label className="auto-fractal-label">
                Rounds: <strong>{autoFractalRounds}</strong>
              </label>
              <input
                type="range"
                min={1}
                max={10}
                value={autoFractalRounds}
                onChange={(e) => setAutoFractalRounds(Number(e.target.value))}
                className="auto-fractal-slider"
              />
              <div className="auto-fractal-desc">
                AI will select the most promising leaf node and expand it, repeating for {autoFractalRounds} rounds.
              </div>
              <button
                className="btn btn-generate"
                onClick={handleStartAutoFractal}
                disabled={active.rawNodesRef.current.length === 0}
                style={{ width: '100%', marginTop: 8 }}
              >
                ▶ START EXPLORATION
              </button>
            </div>
          ) : (
            <div className="auto-fractal-progress">
              {autoFractalProgress && autoFractalProgress.status !== 'done' && (
                <>
                  <div className="auto-fractal-round">
                    Round {autoFractalProgress.round}/{autoFractalProgress.maxRounds}
                  </div>
                  <div className="auto-fractal-status">
                    {autoFractalProgress.status === 'selecting'
                      ? '🔍 Selecting most promising node...'
                      : `⊕ Expanding: "${active.rawNodesRef.current.find(n => n.id === autoFractalProgress.selectedNodeId)?.data?.label || autoFractalProgress.selectedNodeId}"`
                    }
                  </div>
                  {autoFractalProgress.reasoning && (
                    <div className="auto-fractal-reasoning">
                      "{autoFractalProgress.reasoning}"
                    </div>
                  )}
                  {autoFractalProgress.newNodeCount > 0 && (
                    <div className="auto-fractal-count">
                      +{autoFractalProgress.newNodeCount} nodes added
                    </div>
                  )}
                </>
              )}
              <button
                className="btn btn-stop"
                onClick={handleStopAutoFractal}
                style={{ width: '100%', marginTop: 8 }}
              >
                ◼ STOP
              </button>
            </div>
          )}
        </div>
      )}

      {/* Pipeline now renders inside ChatPanel */}

      {/* ── Resume Changes Modal ── */}
      <ResumeChangesModal
        isOpen={showResumeChanges}
        onClose={() => setShowResumeChanges(false)}
        changes={resumeChanges?.changes}
        summary={resumeChanges?.summary}
        isLoading={isGeneratingChanges}
        error={resumeChangesError}
      />


      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        nodes={active.rawNodesRef.current}
        idea={ideaText}
      />

      <VideoModal
        isOpen={!!videoModalNodeId}
        onClose={() => setVideoModalNodeId(null)}
        job={videoModalNodeId ? mnemonic$.mnemonicJobs[videoModalNodeId] : null}
        nodeLabel={videoModalNodeId ? active.nodes.find(n => n.id === videoModalNodeId)?.data?.label : ''}
        onRetry={videoModalNodeId ? () => {
          const serialized = active.nodes.map(nd => nd.data);
          mnemonic$.generateMnemonic(videoModalNodeId, ideaText, serialized);
        } : null}
      />

      {/* Phase B redesign: post-completion discovery overlay */}
      <DiscoveryDeck
        visible={discoveryDeckVisible}
        onDismiss={() => setDiscoveryDeckVisible(false)}
        onShowInTree={() => setDiscoveryDeckVisible(false)}
      />
    </div>
    </NoveltyContext.Provider>
  );
}

const EMPTY_STATE_CONFIG = {
  idea: {
    icon: '◈',
    title: 'THOUGHTCLAW',
    desc: (<>Type an idea above and hit <kbd>Enter</kbd> or <kbd>GENERATE</kbd> to let the agent build your product thinking tree.</>),
    examples: ['"AI code review tool"', '"grocery delivery for seniors"', '"multiplayer design app"'],
  },
  decision: {
    icon: '⚖',
    title: 'DECISION CANVAS',
    desc: (<>Describe the decision you're weighing above and hit <kbd>Enter</kbd> or <kbd>GENERATE</kbd> to map out trade-offs, risks, and alternatives.</>),
    examples: ['"Leave my job to start a company"', '"React vs Vue for our frontend"', '"Raise a Series A or stay bootstrapped"'],
  },
  writing: {
    icon: '✦',
    title: 'WRITING CANVAS',
    desc: (<>Describe what you want to write above and hit <kbd>Enter</kbd> or <kbd>GENERATE</kbd> to build a structured argument tree.</>),
    examples: ['"Essay on remote work productivity"', '"Technical blog post on AI agents"', '"Pitch deck for Series A raise"'],
  },
  plan: {
    icon: '◉',
    title: 'PLAN CANVAS',
    desc: (<>Describe your project or goal above and hit <kbd>Enter</kbd> or <kbd>GENERATE</kbd> to build a milestone and dependency tree.</>),
    examples: ['"Launch a mobile app in 3 months"', '"Migrate monolith to microservices"', '"Q1 go-to-market roadmap"'],
  },
  learn: {
    icon: '⧫',
    title: 'LEARN CANVAS',
    desc: (<>Type a topic above and hit <kbd>Enter</kbd> or <kbd>GENERATE</kbd> to build a learning tree with concepts and prerequisites.</>),
    examples: ['"How neural networks work"', '"Rust ownership and borrowing"', '"Distributed systems fundamentals"'],
  },
  resume: {
    icon: '◎',
    title: 'RESUME CANVAS',
    desc: (<>Paste a job description above and hit <kbd>Enter</kbd> or <kbd>GENERATE</kbd> to build a tailored resume analysis.</>),
    examples: ['"Senior frontend engineer at Stripe"', '"Product manager role at a Series B startup"', '"ML engineer job description"'],
  },
};

function IdeaEmptyState({ mode, onExampleClick }) {
  const cfg = EMPTY_STATE_CONFIG[mode] || EMPTY_STATE_CONFIG.idea;
  return (
    <div className="empty-state">
      <div className="empty-icon">{cfg.icon}</div>
      <div className="empty-title">{cfg.title}</div>
      <div className="empty-desc">{cfg.desc}</div>
      <div className="empty-examples">
        <span className="examples-label">try:</span>
        {cfg.examples.map((ex) => (
          <span key={ex} className="example-chip" onClick={() => onExampleClick && onExampleClick(ex.replace(/^"|"$/g, ''))}>{ex}</span>
        ))}
      </div>
    </div>
  );
}
